import { PersonChip } from "@/components/PersonChip";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { clientsLookup, brandsLookup, projectsLookup } from "@/lib/lookupCache";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

import PageHeader from "@/components/PageHeader";
import AdvancedTable from "@/components/table/AdvancedTable";
import { TaskSkeletonLoader } from "@/components/project/ProjectSubTabs";
import { buildTaskColumns } from "@/components/tasks/buildTaskColumns";
import PhasePicker from "@/components/tasks/PhasePicker";
import TaskViewSwitcher from "@/components/tasks/TaskViewSwitcher";
import TaskBillingView from "@/components/tasks/TaskBillingView";
import { BulkActionBar, AiNarrateButton, RiskBadge } from "@/components/tasks/TaskRiskAndBulk";
import TeamRollupWidget from "@/components/tasks/TeamRollupWidget";

// Iter 236g · Compact "how old is this task?" badge. Only renders once a task
// is >= 3 days old so we don't spam every new row. Colour ramps: neutral
// (<7d), amber (7-30d), rose (30d+). Suppressed on done/cancelled tasks —
// knowing an archived task took 4 months is noise.
// R68 · Inline-rename cell: double-click title (or press F2) to edit;
// Enter saves, Esc cancels. Falls back to the plain title otherwise so
// the row's normal click-to-open behaviour is preserved.
// Feb 2026 R100 · Exported so the Projects › Tasks sub-tab shares the
// same title cell, inline date editor, overdue check, and column config
// (see components/tasks/buildTaskColumns.jsx).
export function TaskTitleCell({ task, onRename, memberName, onOpenSubtask }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title || "");

  useEffect(() => { if (!editing) setDraft(task.title || ""); }, [task.title, editing]);

  const commit = async () => {
    const v = draft.trim();
    setEditing(false);
    if (!v || v === task.title) { setDraft(task.title || ""); return; }
    try { await onRename?.(v); } catch { setDraft(task.title || ""); }
  };

  if (editing) {
    return (
      <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setDraft(task.title || ""); setEditing(false); }
          }}
          onBlur={commit}
          className="h-7 text-sm rounded-none"
          data-testid={`task-rename-input-${task.id}`}
        />
      </div>
    );
  }
  return (
    <div className="min-w-0"
         onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
         title="Double-click to rename"
         data-testid={`task-title-cell-${task.id}`}>
      <div className="font-medium flex items-start gap-1.5 flex-wrap">
        <span className="min-w-0 break-words whitespace-normal leading-snug">{task.title}</span>
        <RiskBadge risk={task.risk} size="xs" showLabel={false} />
        <TaskAgeBadge createdAt={task.created_at} status={task.status} />
        {task.subtask_count > 0 && (
          <>
            <SubtaskPreviewButton task={task} memberName={memberName} onOpenSubtask={onOpenSubtask} />
            {/* R148e · Feb 2026 — Subtask progress chip. Colours shift
                from muted → amber → emerald as the parent gets closer to
                complete so managers can eyeball completion at a glance. */}
            {(() => {
              const done = task.subtask_done_count || 0;
              const total = task.subtask_count;
              const pct = total ? (done / total) : 0;
              const cls = pct >= 1
                ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                : pct >= 0.5
                  ? "border-amber-500 text-amber-700 bg-amber-50"
                  : "border-border text-muted-foreground bg-muted/40";
              return (
                <span
                  className={`inline-block border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}
                  title={`${done} of ${total} subtasks done`}
                  data-testid={`subtask-progress-chip-${task.id}`}
                >
                  {done}/{total} done
                </span>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}


function TaskAgeBadge({ createdAt, status }) {
  if (!createdAt || status === "done" || status === "cancelled") return null;
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);
  if (days < 3) return null;
  const cls = days >= 30 ? "bg-rose-500/10 border-rose-500/40 text-rose-700"
            : days >= 7  ? "bg-amber-500/10 border-amber-500/40 text-amber-700"
            : "bg-muted border-border text-muted-foreground";
  const label = days >= 30 ? `${Math.floor(days / 30)}mo`
              : days >= 14 ? `${Math.floor(days / 7)}w`
              : `${days}d`;
  return (
    <span className={`inline-flex items-center px-1 py-0 text-[9px] font-mono border ${cls}`}
           title={`Created ${days} days ago`}
           data-testid="task-age-badge">
      {label}
    </span>
  );
}
import { QuickAddTaskPopover, QuickAddTaskInline } from "@/components/tasks/QuickAddTask";
import BulkTaskCreator from "@/components/tasks/BulkTaskCreator";
import CloseTaskWithTimeDialog from "@/components/tasks/CloseTaskWithTimeDialog";
import RiskDigestButton from "@/components/tasks/RiskDigestButton";
import RunSOPButton from "@/components/sops/RunSOPButton";
import ArchiveToggle from "@/components/ArchiveToggle";
import { toggleWatch, refreshWatchList } from "@/components/WatchListHeaderButton";
import PriorityIcon from "@/components/tasks/PriorityIcon";
import TaskDateField from "@/components/tasks/TaskDateField";
import PlanVsActualChip from "@/components/tasks/PlanVsActualChip";
import SubtaskPreviewButton from "@/components/tasks/SubtaskPreviewButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiSelect from "@/components/ui/multi-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import SearchableSelect from "@/components/ui/SearchableSelect";
import SmartRichText from "@/components/SmartRichText";
import RecurrenceEditor from "@/components/tasks/RecurrenceEditor";
import { ListChecks, Filter, MessageSquare, Calendar, CalendarDays, Tag as TagIcon, User, X, Send, LayoutGrid, Copy, Edit3, Pencil, Plus, Trash2, MoreHorizontal, TrendingUp, FolderKanban, Users, Clock, FileText, Link2, Eye, EyeOff, Repeat, ChevronDown, ChevronUp, CheckCircle2, Download, RefreshCw } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { fmtRelative, fmtAbsRelative } from "@/lib/format";
import { formatSystemActivity } from "@/lib/formatSystemActivity";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import askDateChangeReason from "@/components/tasks/DateChangeReasonDialog";

export const STATUSES = ["pending_approval", "open", "in_progress", "review", "blocked", "done"];

/** Strip HTML tags for use in truncated list previews. Keeps entities decoded. */
function stripHtml(html) {
  if (!html) return "";
  if (typeof document === "undefined") return String(html).replace(/<[^>]*>/g, "");
  const el = document.createElement("div");
  el.innerHTML = String(html);
  return (el.textContent || el.innerText || "").trim();
}
export const PRIORITIES = ["low", "medium", "high", "urgent"];

/**
 * SearchWithApply — Feb 2026 UX audit
 *
 * Replaces the on-every-keystroke search input. Keystrokes update local
 * state only; the parent's `onApply(value)` fires on Enter, Apply-click,
 * or blur. Prevents filter thrash on 500+-task boards (was the #1 cause
 * of filter latency reported by Design 11).
 */
function SearchWithApply({ value, onApply }) {
  const [draft, setDraft] = React.useState(value || "");
  // Sync when parent clears (e.g. "Clear filters" button).
  React.useEffect(() => { setDraft(value || ""); }, [value]);
  const dirty = (draft || "") !== (value || "");
  const apply = () => { if (dirty) onApply(draft); };
  return (
    <div className="flex items-center gap-1">
      <Input
        placeholder="Search title or description…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
        onBlur={apply}
        className="h-8 w-56 text-xs rounded-none"
        data-testid="tasks-filter-query"
      />
      <Button type="button" size="sm" variant={dirty ? "default" : "outline"}
              className="h-8 rounded-none text-xs"
              onClick={apply}
              disabled={!dirty}
              data-testid="tasks-filter-query-apply"
              title="Press Enter or click Apply to filter">
        Apply
      </Button>
      {value && (
        <button type="button"
                onClick={() => { setDraft(""); onApply(""); }}
                className="h-8 px-1 text-[10px] text-muted-foreground hover:text-foreground"
                data-testid="tasks-filter-query-clear"
                title="Clear search">
          ✕
        </button>
      )}
    </div>
  );
}



export default function Tasks({ initialAssigneeIds = null, initialProjectIds = null, initialTaskIds = null, embedded = false } = {}) {
  const { activeWorkspaceId, user } = useAuth();
  const { hasPerm } = useWorkspacePerms();
  // Iter Feb 2026 R33 · Granular per-button + per-view gates. Fallbacks
  // to admin/manage so existing owners see every button. Missing
  // perms simply hide the corresponding action/view.
  const _admin = hasPerm("workspace.admin") || hasPerm("workspace.manage");
  const canDigest = hasPerm("task.digest.use") || _admin;
  const canAiRisk = hasPerm("task.ai_risk.use") || _admin;
  const canBulkCreate = hasPerm("task.bulk_create") || _admin;
  const canFromTemplate = hasPerm("task.from_template") || _admin;
  const canQuickAdd = hasPerm("task.quick_add") || _admin || hasPerm("task.create");
  const myUserId = user?.id;
  // Feb 2026 R97 · Inline task-list editing gate. We accept the classic
  // `task.update` perm plus per-user assignee/creator fallbacks so that
  // owning a task always lets the user drag its dates on the list without
  // opening the modal.
  const canManageTask = (t) => (
    _admin
    || hasPerm("task.update")
    || hasPerm("task.manage")
    || (t && t.assignee_id && t.assignee_id === myUserId)
    || (t && t.created_by && t.created_by === myUserId)
  );
  const canTaskView = {
    list:      true,  // list always accessible if user can view any tasks
    cards:     true,  // R138h · Feb 2026 — Card view for mobile / at-a-glance
    kanban:    hasPerm("task.view.kanban")    || _admin,
    calendar:  hasPerm("task.view.calendar")  || _admin,
    feed:      hasPerm("task.view.feed")      || _admin,
    dashboard: hasPerm("task.view.dashboard") || _admin,
    gantt:     hasPerm("task.view.gantt")     || _admin,
    timeline:  hasPerm("task.view.timeline")  || _admin,
    recurring: hasPerm("task.view.recurring") || _admin,
    billing:   hasPerm("task.view.billing") || hasPerm("finance.view") || hasPerm("finance.manage") || _admin,  // R69 + R148g (task.view.billing = standalone)
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const openNew = searchParams.get("new") === "1";
  const [showBulk, setShowBulk] = useState(searchParams.get("bulk") === "1");
  const [tasks, setTasks] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false); // R148 · admin-only toggle
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(true);
  // Feb 2026 · First-paint flag (vs. subsequent refetches) — used to
  // show the shared skeleton loader on the very first load and avoid
  // flashing it on routine refetches (filter tweak, debounced search).
  const [firstLoad, setFirstLoad] = useState(true);

  // R52.1 · Default filter is "mine + not done" per user request.
  // Users can change filters afterwards to see other tasks they have
  // access to (backend task-view scope always applies).
  // `showMine=true` calls /me/tasks which already returns
  // `assignee_id = current user` + `status != done`, so we don't need
  // a separate fStatus filter for the default view.
  // Feb 2026 R97 · Filter state is now arrays so users can multi-select
  // status / client / project / assignee / tag / risk. Backend params still
  // accept a single value; when 2+ are picked we send NO server-side filter
  // for that key and let the client-side filter block below prune the rows.
  // Iter · Feb 2026 — When no saved view auto-applies, default the
  // Status filter to every status EXCEPT `done` so first-time visitors
  // land on their incomplete-only worklist instead of everything.
  // Saved views still override this via setFStatus() when they load.
  // When embedded (initialProjectIds/initialAssigneeIds/initialTaskIds)
  // we keep the widest default so drilldowns still surface completed
  // items for the requested scope.
  const [fStatus, setFStatus] = useState(() => {
    const embedded = (initialAssigneeIds && initialAssigneeIds.length) ||
      (initialProjectIds && initialProjectIds.length) ||
      (initialTaskIds && initialTaskIds.length);
    return embedded ? [] : STATUSES.filter((s) => s !== "done");
  });
  const [fClient, setFClient] = useState([]);
  const [fProject, setFProject] = useState(() => initialProjectIds || []);
  // R138p-p7 · When embedded from Task Intelligence, seed the assignee
  // filter to the target person and disable the "mine only" default so
  // the drilldown surfaces THAT person's tasks, not the viewer's own.
  const [fAssignee, setFAssignee] = useState(() => initialAssigneeIds || []);
  const [fTag, setFTag] = useState([]);
  // Feb 2026 R292w · Task Category filter — multi-select across admin-
  // managed categories. Applied client-side after the fetch (same as
  // tag + unassigned filters) because the server-side filter space is
  // already covering status/project/assignee for data minimisation.
  const [fCategory, setFCategory] = useState([]);
  const [fQuery, setFQuery] = useState("");
  const [showMine, setShowMine] = useState(    !((initialAssigneeIds && initialAssigneeIds.length) ||
      (initialProjectIds && initialProjectIds.length) ||
      (initialTaskIds && initialTaskIds.length))
  );
  const [onlyRecurring, setOnlyRecurring] = useState(false);
  // R138p-p9 · Optional hard task-id whitelist — used by Task Intelligence
  // "Replans" drill to scope to specific tasks. Applied client-side after
  // fetch since Tasks page keeps its whole ws roll-up in memory.
  const initialTaskIdSet = useMemo(
    () => (initialTaskIds && initialTaskIds.length ? new Set(initialTaskIds) : null),
    [initialTaskIds]
  );

  // Feb 2026 · "See as manager" scope from the Team Roll-up widget.
  // When populated, the tasks list additionally restricts to the
  // impersonated manager's reports subtree. The widget owns the setter.
  const [viewAsScope, setViewAsScope] = useState({ is_impersonating: false, subtree_user_ids: [], manager_id: null });
  const [detailTask, setDetailTask] = useState(null);
  const [fRisk, setFRisk] = useState([]);
  // R268llll · Feb 2026 — Department filter (multi-select). Task
  // doesn't carry department_id natively; we derive it from the task's
  // project via `projectDeptMap` (built once per project list load).
  const [fDepartment, setFDepartment] = useState([]);
  const [departments, setDepartments] = useState([]);
  // R268llll · Feb 2026 — Quick-scope selector. One-of value persisted
  // per user-per-workspace so refresh lands them where they left off.
  //   "all"           — everyone's tasks (default)
  //   "assigned-me"   — task.assignee_id === myUserId
  //   "created-by-me" — task.created_by === myUserId
  //   "watching"      — myUserId in task.watcher_user_ids
  //   "mentioning-me" — myUserId in task.mention_user_ids
  const [scope, setScope] = useState(() => {
    if (typeof window === "undefined") return "involved";
    try {
      const saved = window.localStorage.getItem(`tasks:${activeWorkspaceId || "_"}:scope`);
      // Legacy upgrade — users whose saved default was "assigned-me"
      // get silently migrated to "involved" so Ramesh's complaint
      // (couldn't see tasks he created) resolves on next load without
      // him switching tabs. Users who explicitly picked another scope
      // keep their choice.
      if (saved === "assigned-me-legacy-migrated") return "involved";
      if (saved === "assigned-me" && !window.localStorage.getItem(`tasks:${activeWorkspaceId || "_"}:scopeOptIn`)) {
        return "involved";
      }
      return saved || "involved";
    } catch { return "involved"; }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(`tasks:${activeWorkspaceId || "_"}:scope`, scope || "involved");
      // Flag that the user has (either passively accepted or actively
      // changed) the scope so we don't keep migrating them.
      window.localStorage.setItem(`tasks:${activeWorkspaceId || "_"}:scopeOptIn`, "1");
    } catch { /* ignore */ }
  }, [scope, activeWorkspaceId]);
  // R148l · Feb 2026 — Billable / billing-lifecycle filter.
  //   "" | "billable" | "not_billable" | "billed" | "unbilled" | "paid" | "unpaid"
  const [fBillable, setFBillable] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  // R138h · Feb 2026 — Quick reports on top of the tasks list.
  //   overdue      — due_date < today, not done
  //   due_today    — due_date == today, not done
  //   upcoming     — 1..7 days out, not done
  //   effort_over  — timesheet_hours > estimated_hours × 1.2
  const [quickReport, setQuickReport] = useState("");
  // R138k · Filter bar collapsible.
  // R138l · Feb 2026 — Persist per-user preferences in localStorage:
  //   filtersOpen (bool) — whether the filter row is expanded
  //   compactMode (bool) — hides header description + quick reports for
  //     max screen real-estate on 13" laptops
  const _lsKey = (k) => `tasks:${activeWorkspaceId || "_"}:${k}`;
  const [filtersOpen, setFiltersOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem(`tasks:${activeWorkspaceId || "_"}:filtersOpen`);
    if (saved === "1") return true;
    if (saved === "0") return false;
    return window.innerWidth >= 640;
  });
  // Iter · Feb 2026 — Tasks defaults to COMPACT (dense rows, no
  // description strip) because users open this module to scan a lot
  // of items quickly. Toggle unpacks it into a "comfortable" layout
  // with more whitespace + the intro description.
  const [compactMode, setCompactMode] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem(`tasks:${activeWorkspaceId || "_"}:compact`);
    if (saved === "0") return false;
    return true; // new default: compact
  });
  useEffect(() => {
    try { window.localStorage.setItem(_lsKey("filtersOpen"), filtersOpen ? "1" : "0"); }
    catch { /* localStorage quota / private mode — ignore */ }
  }, [filtersOpen, activeWorkspaceId]); // eslint-disable-line
  useEffect(() => {
    try { window.localStorage.setItem(_lsKey("compact"), compactMode ? "1" : "0"); }
    catch { /* ignore */ }
  }, [compactMode, activeWorkspaceId]); // eslint-disable-line

  // R137 · Feb 2026 — Task list uses lean projections for speed; hydrate the
  // full task (description, attachments, subtasks, etc.) when a row is opened.
  const openTaskDetail = async (t) => {
    if (!t?.id) return;
    setDetailTask(t);
    try {
      const { data } = await api.get(`/tasks/${t.id}`);
      if (data && data.id === t.id) {
        setDetailTask((cur) => (cur && cur.id === t.id ? { ...cur, ...data } : cur));
      }
      // R148o · Feb 2026 — Also fetch pending reschedule so the Plan
      // block renders the "🕐 Pending approval" chip on open.
      try {
        const { data: pend } = await api.get(`/tasks/${t.id}/reschedule-pending`);
        setDetailTask((cur) => (cur && cur.id === t.id ? { ...cur, reschedule_pending: pend || null } : cur));
      } catch { /* non-critical */ }
    } catch { /* row-open fallback: keep the lean row */ }
  };

  // R138v · Feb 2026 — When embedded from Executive Portfolio with a
  // single-task whitelist, auto-open the full TaskDetail popup so the
  // director gets the identical editing experience as the standalone
  // Tasks module (description, subtasks, checklist, attachments,
  // comments, time entries) without an extra click.
  useEffect(() => {
    if (!embedded) return;
    if (!initialTaskIds || initialTaskIds.length !== 1) return;
    if (detailTask?.id === initialTaskIds[0]) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/tasks/${initialTaskIds[0]}`);
        if (alive && data && data.id) setDetailTask(data);
      } catch { /* task deleted or no access — ignore */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, initialTaskIds]);

  const loadRefData = async () => {
    if (!activeWorkspaceId) return;
    // R69c · Use Promise.allSettled + safe defaults so a restricted role
    // (e.g. hr-manager without client.view) still gets a functional Tasks
    // page instead of an uncaught 403 dev-overlay.
    // Nov 2026 · Switched clients + projects to the cached lightweight
    // /lookup and /picker endpoints — id+name only. Dropdown render used
    // to block on hydrating project effort, review-item counts and
    // per-client role lookups which the Tasks list never shows.
    const [cs, ps, ms, ds] = await Promise.allSettled([
      clientsLookup(activeWorkspaceId),
      projectsLookup(activeWorkspaceId),
      api.get(`/workspaces/${activeWorkspaceId}/members`),
      api.get(`/workspaces/${activeWorkspaceId}/departments`),
    ]);
    setClients(cs.status === "fulfilled" ? (cs.value || []) : []);
    setProjects(ps.status === "fulfilled" ? (ps.value || []) : []);
    setMembers(ms.status === "fulfilled" ? (ms.value?.data || []) : []);
    setDepartments(ds.status === "fulfilled" ? (ds.value?.data || []) : []);
  };

  // R268llll · Feb 2026 — Map project_id → { department_id, department_name }
  // so tasks (which carry only project_id) can be filtered/grouped by
  // department client-side without a per-row lookup.
  const projectDeptMap = useMemo(() => {
    const m = new Map();
    for (const p of (projects || [])) {
      if (p?.id) m.set(p.id, { department_id: p.department_id || null, department_name: p.department_name || p.department?.name || null });
    }
    return m;
  }, [projects]);

  // Oct 2026 · Mobile crash guard — Portalwiz support user with
  // workspace-wide visibility had 2000+ tasks returned from the
  // backend, which blew the mobile JS heap when combined with the
  // search-haystack build and the department-enrichment map pass.
  // On narrow viewports we start with a smaller server-side limit
  // and show a banner so the user can tap "Load all" if they really
  // need to scroll past 500 rows.
  const _initialMobileCap = (typeof window !== "undefined"
                               && window.innerWidth < 640);
  const [mobileCapActive, setMobileCapActive] = useState(_initialMobileCap);
  const [mobileCapTotal, setMobileCapTotal] = useState(null);

  const loadTasks = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      // Iter 164: use tasks-with-risk so every row carries computed risk.
      // R97 · When a filter has multiple picks we skip the server param and
      // rely on the client-side filter block below. Single-pick continues
      // to hit the backend for efficiency.
      const one = (arr) => (arr && arr.length === 1 ? arr[0] : null);
      // Feb 2026 R292s · Data-minimisation — when the user picks
      // multiple status / project / assignee chips, send them as
      // comma-separated server-side filters so the API returns ONLY
      // the slice they're looking at. Previously we'd fall back to
      // "no filter" + client-side trimming, which leaked every row
      // in the user's scope into the browser's dev console.
      const multi = (arr) => (Array.isArray(arr) && arr.length > 0
                              ? arr.filter(Boolean).join(",") : "");
      const oneStatus = one(fStatus);
      const oneProject = one(fProject);
      const oneAssignee = one(fAssignee);
      const oneRisk = one(fRisk);
      const multiStatus = multi(fStatus);
      const multiProject = multi(fProject);
      const multiAssignee = multi(fAssignee.filter((a) => a !== "_unassigned"));
      const params = new URLSearchParams();
      params.set("lean", "1");
      if (includeArchived) params.set("include_archived", "true");
      if (multiStatus) params.set("status", multiStatus);
      if (multiProject) params.set("project_id", multiProject);
      if (multiAssignee) params.set("assignee_id", multiAssignee);
      if (oneRisk) params.set("risk_level", oneRisk);
      // Oct 2026 · Mobile cap. 500 keeps the heap under ~1.5 MB of
      // row objects — well below Safari-iOS's wedge-crash threshold.
      if (mobileCapActive) params.set("limit", "500");
      let rows;
      // Feb 2026 · When "View as" is impersonating another manager /
      // report, the Me-tasks endpoint (which is hardcoded to the
      // viewer) returns the WRONG universe — admin's own tasks
      // instead of the impersonated person's. The client-side filter
      // further down only narrows this bad universe, so the dropdown
      // badges (sourced from a workspace-wide managers-tree endpoint)
      // and the list rows disagree. When impersonating, always route
      // through `/tasks-with-risk` so the request can be scoped by
      // assignee at the server + fall back to subtree filtering
      // client-side. For a single-user subtree we also push the
      // `assignee_id` param so the backend prunes early.
      const impersonating = viewAsScope.is_impersonating && (
        viewAsScope.subtree_user_ids?.length || viewAsScope.manager_id
      );
      const useMeEndpoint = showMine && !impersonating;
      // When impersonating a single person, prune on the server.
      if (impersonating) {
        const allowed = [...(viewAsScope.subtree_user_ids || []), viewAsScope.manager_id]
          .filter(Boolean);
        if (allowed.length === 1) params.set("assignee_id", allowed[0]);
      }
      // Feb 2026 — Collaborator-only users have no task.view perm, so
      // `tasks-with-risk` and `tasks-search` both 403. When that happens
      // we silently fall back to `/me/tasks` (which returns tasks where
      // the user is assignee OR collaborator) instead of blasting a red
      // "No task-view permission" toast.
      const _meFallback = async () => {
        const _meUrl = `/me/tasks?workspace_id=${encodeURIComponent(activeWorkspaceId)}`
                       + (mobileCapActive ? "&limit=500" : "");
        const { data } = await api.get(_meUrl);
        return data || [];
      };
      if (useMeEndpoint) {
        // Bug 30-Sep: `/me/tasks` without ws param returns tasks across
        // every workspace the user belongs to — a Portalwiz admin was
        // seeing Dev-workspace tasks under Portalwiz. Always pin to
        // the currently active workspace.
        rows = await _meFallback();
      } else {
        try {
          const { data } = await api.get(
            `/workspaces/${activeWorkspaceId}/tasks-with-risk?${params.toString()}`);
          rows = data || [];
        } catch (e1) {
          // 403 → no task-view perm → go straight to /me/tasks.
          if (e1?.response?.status === 403) {
            rows = await _meFallback();
          } else {
            try {
              const p2 = new URLSearchParams();
              const oneClient = one(fClient);
              const oneTag = one(fTag);
              const multiClient = multi(fClient);
              const multiTag = multi(fTag);
              if (multiStatus) p2.set("status", multiStatus);
              if (multiClient) p2.set("client_id", multiClient);
              if (multiProject) p2.set("project_id", multiProject);
              if (multiAssignee) p2.set("assignee_id", multiAssignee);
              if (impersonating) {
                const allowed = [...(viewAsScope.subtree_user_ids || []), viewAsScope.manager_id]
                  .filter(Boolean);
                if (allowed.length === 1) p2.set("assignee_id", allowed[0]);
              }
              if (oneTag) p2.set("tag", oneTag);
              if (multiTag && !oneTag) p2.set("tag", multiTag);
              if (fQuery) p2.set("q", fQuery);
              if (mobileCapActive) p2.set("limit", "500");
              const { data } = await api.get(`/workspaces/${activeWorkspaceId}/tasks-search?${p2.toString()}`);
              rows = data || [];
            } catch (e2) {
              if (e2?.response?.status === 403) {
                rows = await _meFallback();
              } else {
                throw e2;
              }
            }
          }
        }
      }
      // Oct 2026 · Mobile cap probe — if the server returned exactly
      // the capped number, there's likely more. Remember the fact so
      // the banner can offer "Load all" without an extra round-trip.
      if (mobileCapActive && Array.isArray(rows) && rows.length >= 500) {
        setMobileCapTotal(rows.length);
      } else if (mobileCapActive) {
        setMobileCapTotal(rows?.length || 0);
      }
      // Client-side multi-value filters.
      if (fStatus.length > 1) rows = rows.filter((t) => fStatus.includes(t.status));
      if (fProject.length > 1) rows = rows.filter((t) => fProject.includes(t.project_id));
      if (fClient.length > 0) rows = rows.filter((t) => fClient.includes(t.client_id));
      if (fTag.length > 0) rows = rows.filter((t) => (t.tags || []).some((x) => fTag.includes(x)));
      // Feb 2026 R292w · Task Category multi-filter.
      if (fCategory.length > 0) {
        rows = rows.filter((t) => fCategory.includes(t.task_category_id || ""));
      }
      if (fRisk.length > 1) rows = rows.filter((t) => fRisk.includes(t.risk_level));
      // R148l · Feb 2026 — Billable / billed / paid filter.
      if (fBillable) {
        rows = rows.filter((t) => {
          switch (fBillable) {
            case "billable":     return !!t.billable;
            case "not_billable": return !t.billable;
            case "billed":       return !!t.billed;
            case "unbilled":     return !!t.billable && !t.billed;
            case "paid":         return !!t.paid;
            case "unpaid":       return !!t.billed && !t.paid;
            default: return true;
          }
        });
      }
      if (fAssignee.length > 0) {
        rows = rows.filter((t) => {
          if (fAssignee.includes("_unassigned") && !t.assignee_id) return true;
          return t.assignee_id && fAssignee.includes(t.assignee_id);
        });
      }
      // Feb 2026 · If the Team Roll-up widget is impersonating another
      // manager, restrict the list to that manager's subtree + the
      // manager themselves. `manager_id` is included so the impersonated
      // person's own tasks appear alongside their reports'.
      if (viewAsScope.is_impersonating && (viewAsScope.subtree_user_ids?.length || viewAsScope.manager_id)) {
        const allowed = new Set([...(viewAsScope.subtree_user_ids || []), viewAsScope.manager_id].filter(Boolean));
        rows = rows.filter((t) => t.assignee_id && allowed.has(t.assignee_id));
      }
      if (fQuery) {
        // R148j · Feb 2026 — Wide client-side match across every text-ish
        // field the search bar could plausibly want to hit. Case-insensitive,
        // whitespace-tolerant; blank fields fall through cleanly.
        const q = fQuery.toLowerCase().trim();
        const parts = q.split(/\s+/).filter(Boolean);
        const fields = (t) => [
          t.title, t.id, t.task_number, t.status, t.priority, t.health,
          t.risk_level, t.package_name, t.package_id,
          t.project_name, t.project_id, t.client_name, t.client_id,
          t.brand_name, t.brand_id, t.assignee_name, t.assignee_email,
          t.reporter_name, t.reviewer_name, t.milestone_name,
          t.package_line_id, t.parent_task_id, t.source, t.type,
          stripHtml(t.description), t.notes, t.close_notes,
          (t.tags || []).join(" "),
          (t.subtasks || []).map((s) => s?.title || "").join(" "),
          (t.watcher_names || []).join(" "),
          t.latest_comment_body, t.last_billing_note,
          t.start_date, t.due_date, t.actual_start_date, t.actual_completion_date,
        ].map((v) => (v == null ? "" : String(v).toLowerCase())).join(" · ");
        rows = rows.filter((t) => {
          const hay = fields(t);
          return parts.every((p) => hay.includes(p));
        });
      }
      // R138p-p9 · Hard task-id whitelist from Task Intelligence "Replans" drill
      if (initialTaskIdSet) rows = rows.filter((t) => initialTaskIdSet.has(t.id));
      // R158l · Feb 2026 — "Only recurring" filter chip: keep tasks that
      // have a recurrence rule (series head or spawned instance).
      if (onlyRecurring) rows = rows.filter((t) => t.recurrence || t.series_id);
      // R268llll · Feb 2026 — Enrich each task with department_id /
      // department_name from its project so the Department filter,
      // Department group, and Department chip on cards all work
      // without needing every endpoint to enrich this natively.
      rows = rows.map((t) => {
        const d = projectDeptMap.get(t.project_id);
        if (!d || t.department_id) return t;
        return { ...t, department_id: d.department_id, department_name: d.department_name };
      });
      // R268llll · Feb 2026 — Department multi-select filter.
      if (fDepartment && fDepartment.length > 0) {
        rows = rows.filter((t) => {
          if (fDepartment.includes("_none") && !t.department_id) return true;
          return t.department_id && fDepartment.includes(t.department_id);
        });
      }
      setTasks(rows);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load tasks"));
    } finally { setBusy(false); setFirstLoad(false); }
  };

  useEffect(() => { loadRefData(); }, [activeWorkspaceId]);
  useEffect(() => { loadTasks(); }, [activeWorkspaceId, fStatus, fClient, fProject, fAssignee, fTag, fQuery, showMine, fRisk, fBillable, includeArchived, onlyRecurring, fDepartment, projectDeptMap, viewAsScope, mobileCapActive]);

  useEffect(() => {
    // Iter 236 · Command Palette · scrub ?new=1 / ?bulk=1 after they open.
    if (openNew || searchParams.get("bulk") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      next.delete("bulk");
      setSearchParams(next, { replace: true });
    }
     
  }, []);

  // Feb 2026 · Dashboard pill deep-links. Honour `?scope=<v>` and
  // `?filter=overdue` on mount so /app/tasks?scope=assigned-me&filter=overdue
  // lands the user directly on their overdue queue. Scrubbed after read
  // so refresh doesn't force the same view forever.
  useEffect(() => {
    const urlScope = searchParams.get("scope");
    const urlFilter = searchParams.get("filter");
    if (!urlScope && !urlFilter) return;
    const validScopes = ["all", "involved", "assigned-me", "created-by-me", "watching", "mentioning-me"];
    if (urlScope && validScopes.includes(urlScope)) {
      setScope(urlScope);
      // Team pills route to scope=all so the RBAC-defined visible set
      // shows (manager's subtree, or every task the caller can view).
      // "showMine" hard-forces `/me/tasks` which would ignore scope, so
      // turn it off whenever the URL asks for anything other than mine.
      if (urlScope !== "assigned-me" && urlScope !== "involved") setShowMine(false);
    }
    if (urlFilter === "overdue") setQuickReport("overdue");
    const next = new URLSearchParams(searchParams);
    next.delete("scope");
    next.delete("filter");
    setSearchParams(next, { replace: true });
     
  }, []);

  // Feb 2026 R291b correctness · scope dropdown picks must disable
  // `showMine` whenever the chosen scope isn't assigned-me-shaped;
  // otherwise /me/tasks silently narrows to assignee=me and the user
  // sees "Created by me · 0" because /me/tasks never returns tasks
  // they only created.
  useEffect(() => {
    if (scope && scope !== "assigned-me" && showMine) setShowMine(false);
  }, [scope, showMine]);

  // R93 · When the task isn't in the currently-filtered list, fetch it
  // directly by id so links from other modules (My assignments, notifications,
  // calendar hover cards) always open the drawer.
  useEffect(() => {
    const taskId = searchParams.get("task");
    if (!taskId) return;
    let alive = true;
    const openIt = async () => {
      const found = tasks.find((t) => t.id === taskId);
      if (found) {
        openTaskDetail(found);
      } else {
        try {
          const { data } = await api.get(`/tasks/${taskId}`);
          if (alive && data && data.id) setDetailTask(data);
        } catch { /* task deleted or no access — ignore */ }
      }
      if (alive) {
        const next = new URLSearchParams(searchParams);
        next.delete("task");
        setSearchParams(next, { replace: true });
      }
    };
    openIt();
    return () => { alive = false; };
     
  }, [searchParams, tasks]);

  const projectsForClient = useMemo(() => {
    if (fClient.length !== 1) return projects;
    return projects.filter((p) => p.client_id === fClient[0]);
  }, [fClient, projects]);

  // Iter · Feb 2026 — When a user flips a task's status to `done`, open
  // the CloseTaskWithTimeDialog first so no task closes silently at
  // zero hours. Wired here so status change from BOTH the row-inline
  // dropdown and the TaskDetail Status picker triggers the guard.
  const [closeGuardTask, setCloseGuardTask] = useState(null);

  const patchTask = async (id, patch) => {
    // Iter · Feb 2026 — Guard on status→done. Skip when the caller is a
    // bulk-update flow (they'll pass `_skipCloseGuard: true`) or when
    // the task is already done.
    if (
      patch && patch.status === "done"
      && !patch._skipCloseGuard
      && !patch.date_change_reason  // date changes go through their own popup
    ) {
      const current = tasks.find((t) => t.id === id) || detailTask;
      if (current && current.status !== "done") {
        setCloseGuardTask({ ...current });
        return;
      }
    }
    if (patch && "_skipCloseGuard" in patch) {
      const { _skipCloseGuard, ...rest } = patch; void _skipCloseGuard;
      patch = rest;
    }
    const prev = tasks.find((t) => t.id === id) || detailTask;
    // R148d · Feb 2026 — Date-change justification.
    //   • First-time set (previous was blank) → no reason required.
    //   • Value changing to a DIFFERENT non-empty value → require a
    //     reason. The Task detail popup uses <TaskDateField> which pops
    //     a proper modal; inline table-cell edits still get a lightweight
    //     window.prompt as a fallback.
    const DATE_FIELDS = ["start_date", "due_date", "actual_start_date", "actual_end_date"];
    const changedDateKeys = DATE_FIELDS.filter((k) => {
      if (!(k in patch)) return false;
      const newV = patch[k] || null;
      const oldV = prev?.[k] || null;
      if (newV === oldV) return false;
      if (!oldV) return false;           // first-time set — no reason
      return true;                        // real change — reason required
    });
    if (changedDateKeys.length > 0 && !patch.date_change_reason) {
      // Feb 2026 R293j · Styled Dialog replaces the native window.prompt.
      const reason = await askDateChangeReason(changedDateKeys);
      if (reason === null) return;               // user cancelled
      patch = { ...patch, date_change_reason: reason };
    }
    // Iter Feb 2026 R47 · Optimistic single-task update.
    // R92 · Merge the SERVER RESPONSE back into state (not just the raw
    // patch) so denormalised fields (assignee_name, task_category_name,
    // dependency lookup rollups etc) refresh in the open drawer without
    // requiring a close/reopen cycle.
    setTasks((prevList) => prevList.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    if (detailTask && detailTask.id === id) {
      setDetailTask((d) => (d ? { ...d, ...patch } : d));
    }
    try {
      const { data: server } = await api.patch(`/tasks/${id}`, patch);
      // R148o · Feb 2026 — Reschedule Approval Workflow.
      // Backend returns `reschedule_pending` when plan-date change requires
      // approval. We must revert the optimistic date update so the UI
      // reflects the CURRENT persisted values (not the proposed ones).
      if (server && server.reschedule_pending) {
        const req = server.reschedule_pending;
        toast.success("Reschedule request submitted — pending approval");
        // Revert plan-date optimistic writes.
        const revert = {};
        for (const k of Object.keys(req.changes || {})) revert[k] = req.changes[k].from || null;
        setTasks((prevList) => prevList.map((t) => (t.id === id ? { ...t, ...revert, reschedule_pending: req } : t)));
        if (detailTask && detailTask.id === id) {
          setDetailTask((d) => (d ? { ...d, ...revert, reschedule_pending: req } : d));
        }
        return true;
      }
      if (server && typeof server === "object") {
        setTasks((prevList) => prevList.map((t) => (t.id === id ? { ...t, ...server } : t)));
        if (detailTask && detailTask.id === id) {
          setDetailTask((d) => (d ? { ...d, ...server } : d));
        }
      }
      if (patch.status === "done" && prev?.status !== "done") {
        celebrate("task_completed", { title: prev?.title || "Task" });
        // Feb 2026 · Reminders Overhaul — check for linked reminders and
        // ask if the user wants to close them alongside the task.
        try {
          const { data } = await api.get(`/tasks/${id}/linked-reminders`);
          const open = (Array.isArray(data) ? data : []).filter((r) => r.status === "open");
          if (open.length > 0) {
            const label = open.length === 1
              ? `"${open[0].title}"`
              : `${open.length} open reminders`;
            if (window.confirm(`Task done. Also mark ${label} complete?`)) {
              const autoNote = `Auto-closed with task "${prev?.title || "task"}".`;
              await Promise.all(open.map((r) => api.post(`/reminders/${r.id}/complete`, { response_note: autoNote }).catch(() => {})));
              toast.success(`Closed ${open.length} linked reminder${open.length === 1 ? "" : "s"}`);
              try { window.dispatchEvent(new CustomEvent("reminders:changed")); } catch { /* ignore */ }
            }
          }
        } catch { /* endpoint-level errors are silent — the task update itself already succeeded */ }
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Update failed"));
      // Roll back by refetching the true server state.
      loadTasks();
      // R268zzzz · Feb 2026 — Re-throw so callers (Description editor,
      // date pickers, etc.) don't silently mark drafts as saved when
      // the backend rejects the update.
      throw e;
    }
  };

  const clearFilters = () => {
    setFStatus([]); setFClient([]); setFProject([]); setFAssignee([]); setFTag([]); setFCategory([]); setFQuery(""); setShowMine(false); setFRisk([]); setFBillable(""); setOnlyRecurring(false); setFDepartment([]); setScope("involved");
  };

  const tagOptions = useMemo(() => {
    const set = new Set();
    for (const t of tasks) (t.tags || []).forEach((x) => set.add(x));
    return Array.from(set).sort();
  }, [tasks]);

  // Feb 2026 R292w · Fetch task categories once per workspace so the
  // filter chip is populated with the admin-managed label (not just
  // whatever's present on the current rows). Falls back silently when
  // the user can't read the catalogue.
  const [taskCategories, setTaskCategories] = useState([]);
  // Feb 2026 R292w · Kanban group-by selection — persisted per
  // workspace via localStorage so the user's preferred swim-lane
  // (Status / Category / Assignee / Priority) survives reloads.
  const [kanbanGroupBy, setKanbanGroupBy] = useState(() => {
    try {
      return localStorage.getItem("tasks-page-kanban-groupby") || "status";
    } catch { return "status"; }
  });
  useEffect(() => {
    try { localStorage.setItem("tasks-page-kanban-groupby", kanbanGroupBy); }
    catch { /* silent */ }
  }, [kanbanGroupBy]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/task-categories`)
      .then((r) => setTaskCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTaskCategories([]));
  }, [activeWorkspaceId]);

  // R138h · Feb 2026 — Report counts + filtered tasks based on the
  // currently active Quick Report chip. Client-side so switching chips
  // is instant without a round-trip.
  const _today = new Date().toISOString().slice(0, 10);
  const _in7 = new Date(Date.now() + 7 * 86400e3).toISOString().slice(0, 10);
  // Feb 2026 R291b correctness · reportCounts must reflect the active
  // scope. Previously counted across ALL tasks, so "scope=Watching +
  // Past due · 50" showed everyone's 50 overdue instead of just the
  // watcher's overdue. Compute scoped rows once and reuse for counts
  // and the quick-report filter.
  const scopedTasks = useMemo(() => {
    if (!scope || scope === "all" || !myUserId) return tasks;
    const _isCollab = (t) => Array.isArray(t.collaborators)
      && t.collaborators.some((c) => (c || {}).user_id === myUserId);
    return (tasks || []).filter((t) => {
      if (scope === "involved") {
        return t.assignee_id === myUserId
            || t.created_by === myUserId
            || t.reporter_id === myUserId
            || (Array.isArray(t.watcher_user_ids) && t.watcher_user_ids.includes(myUserId))
            || (Array.isArray(t.mention_user_ids) && t.mention_user_ids.includes(myUserId))
            || _isCollab(t);
      }
      if (scope === "assigned-me") return t.assignee_id === myUserId || _isCollab(t);
      if (scope === "created-by-me") return t.created_by === myUserId || t.reporter_id === myUserId;
      if (scope === "watching") return Array.isArray(t.watcher_user_ids) && t.watcher_user_ids.includes(myUserId);
      if (scope === "mentioning-me") return Array.isArray(t.mention_user_ids) && t.mention_user_ids.includes(myUserId);
      return true;
    });
  }, [tasks, scope, myUserId]);
  const reportCounts = useMemo(() => {
    const c = { overdue: 0, due_today: 0, upcoming: 0,
                effort_over: 0, effort_under: 0,
                due: 0, late: 0, ahead: 0 };
    for (const t of scopedTasks) {
      const done = (t.status || "") === "done";
      const d = (t.due_date || "").slice(0, 10);
      const ped = (t.planned_end_date || "").slice(0, 10);
      const aed = (t.actual_end_date || "").slice(0, 10);
      if (!done) {
        if (d) c.due += 1;
        if (d && d < _today) c.overdue += 1;
        if (d && d === _today) c.due_today += 1;
        if (d && d > _today && d <= _in7) c.upcoming += 1;
        // Feb 2026 R291t · "Late" aligned with the "Delayed by" column
        // so the quick-report never surfaces a row the column labels
        // "On time". Live tasks are late only if due_date has passed.
        if (d && d < _today) c.late += 1;
      } else {
        if (aed && ped && aed > ped) c.late += 1;
        if (aed && ped && aed < ped) c.ahead += 1;
      }
      const est = Number(t.est_hours || 0);
      const log = Number(t.timesheet_hours || t.logged_hours || 0);
      if (est > 0 && log > est * 1.2) c.effort_over += 1;
      if (est > 0 && log > 0 && log < est * 0.6 && !done) c.effort_under += 1;
    }
    return c;
  }, [scopedTasks, _today, _in7]);
  // R268llll · Feb 2026 — Live count for each Scope chip. Computed
  // from `tasks` (post-filter, pre-scope) so counts reflect what the
  // user would see if they picked that scope with all other filters
  // (status, project, department, …) still applied.
  const scopeCounts = useMemo(() => {
    const c = { all: (tasks || []).length, involved: 0, "assigned-me": 0,
                "created-by-me": 0, watching: 0, "mentioning-me": 0 };
    if (!myUserId) return c;
    for (const t of (tasks || [])) {
      const assigned = t.assignee_id === myUserId;
      const created  = t.created_by === myUserId || t.reporter_id === myUserId;
      const watching = Array.isArray(t.watcher_user_ids) && t.watcher_user_ids.includes(myUserId);
      const mention  = Array.isArray(t.mention_user_ids) && t.mention_user_ids.includes(myUserId);
      const collab   = Array.isArray(t.collaborators)
                       && t.collaborators.some((cc) => (cc || {}).user_id === myUserId);
      if (assigned || collab) c["assigned-me"] += 1;
      if (created)  c["created-by-me"] += 1;
      if (watching) c.watching += 1;
      if (mention)  c["mentioning-me"] += 1;
      if (assigned || created || watching || mention || collab) c.involved += 1;
    }
    return c;
  }, [tasks, myUserId]);

  const displayTasks = useMemo(() => {
    // R268llll · Feb 2026 — Quick-scope filter applied first (one-of),
    // then the existing Quick Report chip (overdue / due-today / …).
    let rows = tasks || [];
    if (scope && scope !== "all" && myUserId) {
      const _isCollab = (t) => Array.isArray(t.collaborators)
        && t.collaborators.some((c) => (c || {}).user_id === myUserId);
      rows = rows.filter((t) => {
        if (scope === "involved") {
          return t.assignee_id === myUserId
              || t.created_by === myUserId
              || t.reporter_id === myUserId
              || (Array.isArray(t.watcher_user_ids) && t.watcher_user_ids.includes(myUserId))
              || (Array.isArray(t.mention_user_ids) && t.mention_user_ids.includes(myUserId))
              || _isCollab(t);
        }
        if (scope === "assigned-me") return t.assignee_id === myUserId || _isCollab(t);
        if (scope === "created-by-me") return t.created_by === myUserId || t.reporter_id === myUserId;
        if (scope === "watching") return Array.isArray(t.watcher_user_ids) && t.watcher_user_ids.includes(myUserId);
        if (scope === "mentioning-me") return Array.isArray(t.mention_user_ids) && t.mention_user_ids.includes(myUserId);
        return true;
      });
    }
    if (!quickReport) return rows;
    return rows.filter((t) => {
      const done = (t.status || "") === "done";
      const d = (t.due_date || "").slice(0, 10);
      const sd = (t.start_date || "").slice(0, 10);
      const ped = (t.planned_end_date || "").slice(0, 10);
      const aed = (t.actual_end_date || "").slice(0, 10);
      const est = Number(t.est_hours || 0);
      const log = Number(t.timesheet_hours || t.logged_hours || 0);
      switch (quickReport) {
        case "overdue":      return d && d < _today && !done;
        case "due_today":    return d && d === _today && !done;
        case "upcoming":     return d && d > _today && d <= _in7 && !done;
        case "effort_over":  return est > 0 && log > est * 1.2;
        case "due":          return !done && !!d;
        case "late":         return !done
                                    ? (d && d < _today)
                                    : (aed && ped && aed > ped);
        case "ahead":        return done && aed && ped && aed < ped;
        case "effort_under": return est > 0 && log > 0 && log < est * 0.6 && !done;
        default: return true;
      }
    });
  }, [tasks, quickReport, scope, myUserId, _today, _in7]);

  // Feb 2026 · Count-chain inputs for TeamRollupWidget. Lets the KPI
  // tiles toggle between team-wide totals (what the roll-up fetched)
  // and the current filtered list the user is actually seeing. Also
  // powers the small "Team X → Scope Y → Filtered Z → Loaded W" chain
  // so the user can see which layer shaved the number down.
  const filteredKpis = useMemo(() => {
    const c = { active: 0, overdue: 0, due_today: 0, upcoming_7d: 0 };
    for (const t of displayTasks || []) {
      const done = (t.status || "").toLowerCase() === "done";
      const cancelled = (t.status || "").toLowerCase() === "cancelled";
      if (done || cancelled) continue;
      c.active += 1;
      const d = (t.due_date || "").slice(0, 10);
      if (!d) continue;
      if (d < _today) c.overdue += 1;
      else if (d === _today) c.due_today += 1;
      else if (d <= _in7) c.upcoming_7d += 1;
    }
    return c;
  }, [displayTasks, _today, _in7]);

  // Feb 2026 · Bulk CSV export of the currently visible Tasks list. CC
  // columns (Deliverable, Topic, "linked?") are included only when the
  // user has `content_calendar.view` so a Finance user downloading the
  // same list doesn't get bucket topics they shouldn't see.
  const _canSeeCC = hasPerm("content_calendar.view");
  const exportTasksCsv = () => {
    const rows = displayTasks || [];
    if (rows.length === 0) {
      toast.error("No tasks to export");
      return;
    }
    const headers = [
      "ID", "Title", "Status", "Priority",
      "Client", "Brand", "Project", "Phase",
      "Assignee", "Due date", "Planned end", "Actual end",
      "Est. hours", "Logged hours",
      "Tags", "Created at", "Updated at",
      ...(_canSeeCC ? ["Deliverable", "Topic", "Topic linked"] : []),
    ];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/\r?\n/g, " ").replaceAll('"', '""');
      return `"${s}"`;
    };
    const lines = [headers.map(esc).join(",")];
    for (const t of rows) {
      const cells = [
        t.id,
        t.title,
        t.status,
        t.priority,
        t.client_name || "",
        t.brand_name || "",
        t.project_name || "",
        t.phase_name || "",
        memberName(t.assignee_id),
        t.due_date || "",
        t.planned_end_date || "",
        t.actual_end_date || "",
        t.est_hours || "",
        t.timesheet_hours || t.logged_hours || "",
        (t.tags || []).join("|"),
        t.created_at || "",
        t.updated_at || "",
      ];
      if (_canSeeCC) {
        cells.push(t.cc_deliverable_name || "");
        cells.push(t.cc_topic || "");
        cells.push(t.cc_topic_linked ? "yes" : (t.cc_topic ? "no" : ""));
      }
      lines.push(cells.map(esc).join(","));
    }
    const csv = lines.join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
    a.download = `tasks-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} task${rows.length === 1 ? "" : "s"} to CSV`);
  };

  // Feb 2026 · "Sync topic names" — re-fetches tasks so each row re-reads
  // its linked Content-Calendar deliverable's current `cc_topic` /
  // `cc_deliverable_name`. The backend already enriches on each load
  // (see `enrich_tasks_with_cc` in `routes_pm.py`), so this is just a
  // forced reload with a confirmation toast that shows how many rows
  // ended up with a CC topic attached. Gated by `content_calendar.view`.
  const [syncingCc, setSyncingCc] = useState(false);
  const syncCcTopics = async () => {
    if (!_canSeeCC) return;
    setSyncingCc(true);
    try {
      await loadTasks();
      // Count after loadTasks completes — read directly from the next
      // state snapshot via a short microtask so we see the fresh rows.
      setTimeout(() => {
        const withTopic = (tasks || []).filter((t) => t.cc_topic).length;
        const withDeliverable = (tasks || []).filter((t) => t.cc_deliverable_name).length;
        toast.success(
          `Synced from Content Calendar · ${withDeliverable} deliverable${withDeliverable === 1 ? "" : "s"}, ${withTopic} topic${withTopic === 1 ? "" : "s"} attached`
        );
      }, 0);
    } finally {
      setSyncingCc(false);
    }
  };

  // R138l · Feb 2026 — Human-readable summary of active filters shown as
  // pills next to the "FILTERS" toggle so users see what's on without
  // expanding. Falls back to a "N active" number when the list is long.
  const filterSummary = useMemo(() => {
    const chips = [];
    if (fQuery) chips.push(`"${fQuery.slice(0, 18)}${fQuery.length > 18 ? "…" : ""}"`);
    const isMe = fAssignee.length === 1 && fAssignee[0] === myUserId;
    if (isMe) chips.push("Mine");
    fStatus.forEach((s) => chips.push(s.replace("_", " ")));
    fClient.forEach((cid) => {
      const c = clients.find((x) => x.id === cid);
      if (c) chips.push(c.name);
    });
    fProject.forEach((pid) => {
      const p = projects.find((x) => x.id === pid);
      if (p) chips.push(p.name);
    });
    if (!isMe) {
      fAssignee.forEach((aid) => {
        const m = members.find((x) => (x.user_id || x.id) === aid);
        if (m) chips.push(m.name || m.email || aid.slice(0, 6));
      });
    }
    fTag.forEach((t) => chips.push(`#${t}`));
    fRisk.forEach((r) => chips.push(r));
    return chips;
  }, [fQuery, fStatus, fClient, fProject, fAssignee, fTag, fRisk, clients, projects, members, myUserId]);

  const memberName = (id) => {
    if (!id) return "—";
    // R68 · Some invited-but-unregistered members have no `user_id`;
    // in that case the row's assignee_id holds the member `id`. Match
    // on both so the display never falls back to "—" right after a
    // fresh assignment.
    const m = members.find((x) => x.user_id === id || x.id === id);
    return m?.user?.name || m?.user?.email || m?.name || m?.email || "—";
  };

  const columns = useMemo(() => buildTaskColumns({
    members,
    patchTask,
    canManageTask,
    memberName,
    InlineDateCell,
    isOverdue,
    PRIORITIES,
    STATUSES,
    fmtRelative,
    PersonChip,
    TaskTitleCell,
    onOpenTask: (t) => setDetailTask(t),   // R148d — subtask preview click
    // Feb 2026 · Expose Deliverable + Topic columns (hidden by default,
    // enable via column selector) when the user can view the Content
    // Calendar. Same perm gates the fields on the Edit Task + Timesheet
    // popups so they stay in sync.
    canSeeCC: hasPerm("content_calendar.view"),
    onCommentPosted: () => loadTasks(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [members, hasPerm]);

  const _dead_columns_marker_removed_below = () => ([
    {
      key: "title", label: "Task", width: 320, sortable: true, filter: true, resizable: true,
      accessor: (t) => t.title,
      render: (t) => <TaskTitleCell task={t} onRename={(v) => patchTask(t.id, { title: v })} />,
    },
    // Iter 247 · Split the color-chip Client › Brand › Project into three
    // plain, sortable/filterable columns per user request.
    {
      key: "client_name", label: "Client", width: 160, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.client_name || "",
      render: (t) => t.client_name
        ? <span className="text-sm truncate" data-testid={`task-client-${t.id}`}>{t.client_name}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "brand_name", label: "Brand", width: 150, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.brand_name || "",
      render: (t) => t.brand_name
        ? <span className="text-sm truncate" data-testid={`task-brand-${t.id}`}>{t.brand_name}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "project_name", label: "Project", width: 170, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.project_name || "",
      render: (t) => t.project_name
        ? <span className="text-sm truncate" data-testid={`task-project-${t.id}`}>{t.project_name}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "priority", label: "Priority", width: 110, sortable: true, groupable: true, stopPropagation: true,
      filterType: "select", filterOptions: PRIORITIES.map((p) => ({ value: p, label: p })),
      accessor: (t) => t.priority,
      render: (t) => (
        <Select value={t.priority} onValueChange={(v) => patchTask(t.id, { priority: v })}>
          <SelectTrigger className="h-7 text-xs" data-testid={`task-priority-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      ),
    },
    {
      key: "progress_percent", label: "%", width: 110, sortable: true, type: "number", align: "right",
      accessor: (t) => t.progress_percent ?? 0,
      render: (t) => (
        <div className="flex items-center gap-1 min-w-0" data-testid={`task-progress-${t.id}`}>
          <div className="flex-1 h-1.5 bg-muted min-w-[40px]">
            <div className="h-full bg-primary transition-all"
                  style={{ width: `${t.progress_percent || 0}%` }} />
          </div>
          <span className="text-[10px] font-mono w-8 text-right">{t.progress_percent || 0}%</span>
        </div>
      ),
    },
    {
      key: "status", label: "Status", width: 140, sortable: true, groupable: true, stopPropagation: true,
      filterType: "select", filterOptions: STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") })),
      accessor: (t) => t.status,
      render: (t) => (
        <Select value={t.status} onValueChange={(v) => patchTask(t.id, { status: v })}>
          <SelectTrigger className="h-7 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
      ),
    },
    {
      key: "assignee", label: "Assignee", width: 160, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => memberName(t.assignee_id),
      render: (t) => (
        // R62 · Inline assignee dropdown — click cell to reassign without
        // opening the task detail drawer. Stops propagation so the row's
        // onClick (open detail) doesn't fire.
        <div onClick={(e) => e.stopPropagation()} className="w-full">
          <Select value={t.assignee_id || "_unassigned"}
                  onValueChange={(v) => patchTask(t.id, {
                    assignee_id: v === "_unassigned" ? null : v,
                  })}>
            <SelectTrigger className="h-6 text-xs border-0 bg-transparent hover:bg-secondary/40 px-1 rounded-none"
                            data-testid={`task-inline-assignee-${t.id}`}>
              <SelectValue>
                {t.assignee_id
                  ? <PersonChip userId={t.assignee_id} name={memberName(t.assignee_id)} />
                  : <span className="text-muted-foreground italic text-xs">unassigned</span>}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_unassigned">— Unassigned —</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                  {m.name || m.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ),
    },
    {
      // Feb 2026 · Read-only Department column. Populated by the backend
      // via `_enrich_task_assignee_department` (picks the assignee's
      // primary department). Hidden when the row has no assignee so the
      // list stays dense.
      key: "assignee_department", label: "Dept", width: 120, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.assignee_department_name || "",
      render: (t) => (
        t.assignee_department_name
          ? <span className="text-[11px] uppercase tracking-widest border border-border bg-muted/30 text-muted-foreground px-1.5 py-0.5"
                     title={`Assignee's primary department · ${t.assignee_department_name}`}
                     data-testid={`task-assignee-dept-${t.id}`}>
              {t.assignee_department_name}
            </span>
          : <span className="text-[11px] text-muted-foreground italic">—</span>
      ),
    },
    {
      key: "health", label: "Health", width: 110, sortable: true, groupable: true, visible: false,
      filterType: "select", filterOptions: [
        { value: "on_track", label: "🟢 On track" },
        { value: "at_risk", label: "🟡 At risk" },
        { value: "off_track", label: "🔴 Off track" },
      ],
      accessor: (t) => t.health || "on_track",
      render: (t) => {
        const h = t.health || "on_track";
        const emoji = h === "off_track" ? "🔴" : h === "at_risk" ? "🟡" : "🟢";
        return <span className="text-xs">{emoji} {h.replace("_", " ")}</span>;
      },
    },
    {
      key: "rating", label: "Rating", width: 100, sortable: true, align: "right", visible: false,
      filterType: "number-range",
      accessor: (t) => t.rating || 0,
      render: (t) => t.rating
        ? <span className="text-amber-500 font-mono text-xs" data-testid={`task-rating-${t.id}`}>{"★".repeat(t.rating)}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "start_date", label: "Planned start", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      accessor: (t) => t.start_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.start_date}
          onChange={(iso) => patchTask(t.id, { start_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-start-${t.id}`}
        />
      ),
    },
    {
      key: "due_date", label: "Planned end · Due", width: 150, sortable: true, align: "right", filterType: "date-range",
      accessor: (t) => t.due_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.due_date}
          onChange={(iso) => patchTask(t.id, { due_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-due-${t.id}`}
          overdue={isOverdue(t)}
        />
      ),
    },
    {
      key: "actual_start_date", label: "Actual start", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      accessor: (t) => t.actual_start_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.actual_start_date}
          onChange={(iso) => patchTask(t.id, { actual_start_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-actual-start-${t.id}`}
          mono
        />
      ),
    },
    {
      key: "actual_end_date", label: "Actual end", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      accessor: (t) => t.actual_end_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.actual_end_date}
          onChange={(iso) => patchTask(t.id, { actual_end_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-actual-end-${t.id}`}
          mono
        />
      ),
    },
    {
      key: "est_hours", label: "Est. hrs", width: 80, sortable: true, align: "right", type: "number", sum: true, visible: false,
      accessor: (t) => t.est_hours || 0,
      render: (t) => t.est_hours ? <span className="font-mono text-xs">{t.est_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "actual_hours", label: "Actual hrs", width: 80, sortable: true, align: "right", type: "number", sum: true, visible: false,
      // R138p · Feb 2026 — Backend annotates `logged_hours` (timesheet auto-
      // sum) and `timesheet_hours` (own + subtask). Fall back through those
      // so the column reflects real logged effort even when `actual_hours`
      // (manual override) is unset.
      accessor: (t) => t.actual_hours ?? t.logged_hours ?? t.timesheet_hours ?? 0,
      render: (t) => {
        const v = t.actual_hours ?? t.logged_hours ?? t.timesheet_hours ?? 0;
        return v ? <span className="font-mono text-xs">{v}</span> : <span className="text-muted-foreground">—</span>;
      },
    },
    {
      key: "manual_effort_hours", label: "Manual effort (h)", width: 130, sortable: true, align: "right", type: "number", sum: true,
      accessor: (t) => t.manual_effort_hours || 0,
      render: (t) => t.manual_effort_hours ? (
        <span className="font-mono text-xs" title={t.manual_effort_note || ""}>{t.manual_effort_hours}{t.manual_effort_note ? " •" : ""}</span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "timesheet_hours", label: "Timesheet (h)", width: 110, sortable: true, align: "right", type: "number", sum: true,
      accessor: (t) => t.timesheet_hours || 0,
      render: (t) => t.timesheet_hours ? <span className="font-mono text-xs text-primary">{t.timesheet_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "total_effort_hours", label: "Total effort (h)", width: 120, sortable: true, align: "right", type: "number", sum: true,
      accessor: (t) => t.total_effort_hours || 0,
      render: (t) => t.total_effort_hours ? <span className="font-mono text-xs font-semibold">{t.total_effort_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "billable", label: "Billable", width: 90, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.billable,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input
            type="checkbox"
            checked={!!t.billable}
            onChange={(e) => patchTask(t.id, { billable: e.target.checked })}
            className="h-4 w-4 accent-primary cursor-pointer"
            data-testid={`task-billable-${t.id}`}
          />
        </div>
      ),
    },
    {
      // Feb 2026 · "Ready to bill" is an intermediate hand-off flag
      // between Billable and Billed. PMs flip it when the work is
      // complete + reviewed and the task is ready for Finance to
      // invoice. Hidden by default so power users opt in via Columns.
      key: "ready_to_bill", label: "Ready to bill", width: 110, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.ready_to_bill,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input
            type="checkbox"
            checked={!!t.ready_to_bill}
            disabled={!t.billable}
            onChange={(e) => patchTask(t.id, { ready_to_bill: e.target.checked })}
            className="h-4 w-4 accent-amber-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            title={!t.billable ? "Mark as billable first" : "Hand off to Finance for invoicing"}
            data-testid={`task-ready-to-bill-${t.id}`}
          />
        </div>
      ),
    },
    {
      key: "billed", label: "Billed", width: 80, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.billed,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input
            type="checkbox"
            checked={!!t.billed}
            disabled={!t.billable}
            onChange={(e) => patchTask(t.id, { billed: e.target.checked })}
            className="h-4 w-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            title={!t.billable ? "Mark as billable first" : ""}
            data-testid={`task-billed-${t.id}`}
          />
        </div>
      ),
    },
    {
      key: "paid", label: "Paid", width: 80, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.paid,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input
            type="checkbox"
            checked={!!t.paid}
            disabled={!t.billed}
            onChange={(e) => patchTask(t.id, { paid: e.target.checked })}
            className="h-4 w-4 accent-emerald-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            title={!t.billed ? "Mark as billed first" : ""}
            data-testid={`task-paid-${t.id}`}
          />
        </div>
      ),
    },
    {
      key: "latest_comment_body", label: "Latest comment", width: 220, sortable: true, filterType: "text", visible: false,
      accessor: (t) => t.latest_comment_body || "",
      render: (t) => t.latest_comment_body ? (
        <div className="min-w-0">
          <div className="text-xs truncate italic">&ldquo;{t.latest_comment_body}&rdquo;</div>
          <div className="text-[10px] text-muted-foreground">{t.latest_comment_author_name} · {fmtRelative(t.latest_comment_at)}</div>
        </div>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "latest_comment_at", label: "Latest comment date", width: 150, sortable: true, filterType: "date", visible: false,
      accessor: (t) => t.latest_comment_at || "",
      render: (t) => t.latest_comment_at ? (
        <span className="text-xs text-muted-foreground" title={t.latest_comment_at}>{fmtRelative(t.latest_comment_at)}</span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "updated_by_name", label: "Updated by", width: 140, sortable: true, filterType: "text", visible: false,
      accessor: (t) => t.updated_by_name || "",
      render: (t) => (
        <div className="min-w-0">
          <div className="text-xs truncate">{t.updated_by_name || "—"}</div>
          <div className="text-[10px] text-muted-foreground">{t.updated_at ? fmtRelative(t.updated_at) : ""}</div>
        </div>
      ),
    },
    {
      key: "age", label: "Age", width: 90, sortable: true, align: "right",
      accessor: (t) => t.created_at,
      render: (t) => <span className="text-xs text-muted-foreground">{fmtRelative(t.created_at)}</span>,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  return (
    <>
      {!embedded && <PageHeader
        eyebrow="PM Triage"
        title="Tasks"
        density="dense"
        description="Filter by client, project, assignee, tag, status, or text. Click any task to thread comments and triage."
        testid="tasks-header"
        actions={activeWorkspaceId && (
          <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end w-full sm:w-auto"
               data-testid="tasks-header-actions">
            {/* Iter · Feb 2026 — Header compacted per UX audit. Compact /
                Archived / Risk Digest moved into the "⋯" overflow menu
                so the header is one calm line. The menu is always
                rendered now (not just mobile). */}
            <div className="hidden sm:flex items-center gap-2">
              {canAiRisk && <AiNarrateButton wsId={activeWorkspaceId} onDone={loadTasks} />}
              {canBulkCreate && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  onClick={() => setShowBulk(true)}
                  data-testid="tasks-header-bulk"
                >
                  <LayoutGrid className="w-3.5 h-3.5 mr-1.5" /> Bulk create
                </Button>
              )}
              {canFromTemplate && (
                <TaskTemplatePicker wsId={activeWorkspaceId} projects={projects}
                                         members={members} onCreated={loadTasks} />
              )}
            </div>
            {/* Overflow menu — bundles the three low-frequency header
                controls (density, archived, risk digest) plus mobile
                shortcuts to Bulk create. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-none"
                        data-testid="tasks-header-more-btn"
                        aria-label="More actions">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={() => setCompactMode((v) => !v)}
                                   data-testid="tasks-more-compact">
                  <span className="w-4 inline-flex justify-center mr-2">{compactMode ? "◐" : "◑"}</span>
                  {compactMode ? "Comfortable spacing" : "Compact spacing"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportTasksCsv}
                                   data-testid="tasks-more-export-csv">
                  <Download className="w-4 h-4 mr-2" /> Export CSV{_canSeeCC ? " (with CC)" : ""}
                </DropdownMenuItem>
                {_canSeeCC && (
                  <DropdownMenuItem
                    disabled={syncingCc}
                    onSelect={(e) => { e.preventDefault(); syncCcTopics(); }}
                    data-testid="tasks-more-sync-cc"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingCc ? "animate-spin" : ""}`} />
                    {syncingCc ? "Syncing topic names…" : "Sync topic names"}
                  </DropdownMenuItem>
                )}
                {(_admin || hasPerm("task.archive")) && (
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIncludeArchived((v) => !v); }}
                                     data-testid="tasks-more-archived">
                    <span className="w-4 inline-flex justify-center mr-2">{includeArchived ? "☑" : "☐"}</span>
                    Include archived tasks
                  </DropdownMenuItem>
                )}
                {canDigest && (
                  <DropdownMenuItem asChild data-testid="tasks-more-digest">
                    {/* RiskDigestButton renders its own button; wrap in
                        a <span> so DropdownMenuItem keeps its row style. */}
                    <span className="flex items-center cursor-pointer">
                      <RiskDigestButton wsId={activeWorkspaceId} compact />
                    </span>
                  </DropdownMenuItem>
                )}
                {canBulkCreate && (
                  <DropdownMenuItem onClick={() => setShowBulk(true)} className="sm:hidden"
                                      data-testid="tasks-header-more-bulk">
                    <LayoutGrid className="w-4 h-4 mr-2" /> Bulk create
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {canQuickAdd && (
              <QuickAddTaskPopover
                wsId={activeWorkspaceId}
                projects={projects}
                members={members}
                onCreated={loadTasks}
                defaultOpen={openNew}
                testid="tasks-header-quickadd"
              />
            )}
          </div>
        )}
      />}

      <div className="px-4 sm:px-6 md:px-10">
        {/* Feb 2026 · Slim animated progress strip pinned to the top of
            the content so users always see the app is doing work when
            they change scope, filter, or navigate here — even before the
            AdvancedTable's centered spinner kicks in. */}
        {busy && (
          <div
            className="sticky top-0 z-20 -mx-4 sm:-mx-6 md:-mx-10 h-1 bg-primary/10 overflow-hidden"
            role="status"
            aria-live="polite"
            data-testid="tasks-loading-strip"
          >
            <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-primary to-transparent animate-[shimmer_1.2s_ease-in-out_infinite]" />
            <style>{`@keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
          </div>
        )}

        {/* Feb 2026 · Team roll-up widget — surfaces every active task
            across the manager's reports subtree. Hidden entirely when
            the caller lacks `task.view_reports_tree` (component 403s
            silently and returns null). */}
        {!embedded && activeWorkspaceId && (_admin || hasPerm("task.view_reports_tree")) && (
          <TeamRollupWidget
            wsId={activeWorkspaceId}
            onFilterAssignees={(ids) => { if (Array.isArray(ids) && ids.length) { setFAssignee(ids); setShowMine(false); } }}
            onAsManagerChange={(scope) => { setViewAsScope(scope); if (scope?.is_impersonating) setShowMine(false); }}
            filteredKpis={filteredKpis}
            filteredTotal={displayTasks.length}
            scopeTotal={scopedTasks.length}
            rawTotal={tasks.length}
            onKpiClick={(key) => {
              // Feb 2026 · Clickable KPI tiles. Tap "Overdue 142" to
              // apply the matching Quick Report chip so the user
              // instantly sees those 142 rows. Active clears the
              // quick-report chip so the full scoped list reappears.
              if (key === "active") {
                setQuickReport("");
                return;
              }
              const map = { overdue: "past_due", due_today: "due_today", upcoming_7d: "upcoming_7d" };
              const chip = map[key];
              if (chip) setQuickReport((cur) => (cur === chip ? "" : chip));
            }}
            activeKpi={({ past_due: "overdue", due_today: "due_today", upcoming_7d: "upcoming_7d" })[quickReport] || null}
          />
        )}
        {/* R138k · Feb 2026 — Filter bar collapses on mobile so the tasks
            list gets the whole screen. Toggle is always visible; count
            hint next to it shows how many filters are active. */}
        <div className="border border-border bg-muted/40" data-testid="tasks-filterbar-wrap">
          <div className="flex items-stretch">
          <button type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  className="flex-1 min-w-0 flex items-center justify-between gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground hover:bg-muted/60"
                  data-testid="tasks-filterbar-toggle">
            <span className="inline-flex items-center gap-2 flex-1 min-w-0">
              <Filter className="w-3.5 h-3.5 shrink-0" /> Filters
              {filterSummary.length > 0 && (
                <span className="flex items-center gap-1 flex-wrap normal-case tracking-normal min-w-0"
                      data-testid="tasks-filter-summary">
                  {filterSummary.slice(0, 3).map((c, i) => (
                    <span key={i}
                          className="text-[10px] bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5 truncate max-w-[110px]"
                          data-testid={`tasks-filter-chip-${i}`}
                          title={c}>{c}</span>
                  ))}
                  {filterSummary.length > 3 && (
                    <span className="text-[10px] text-muted-foreground">+{filterSummary.length - 3} more</span>
                  )}
                </span>
              )}
            </span>
            <span className="text-xs shrink-0">{filtersOpen ? "▲" : "▼"}</span>
          </button>
          {/* Iter · Feb 2026 R230 — Always-visible one-tap chip to toggle
              completed items back into the current view. Sits right of
              the Filters toggle so users don't need to open the drawer
              just to peek at "done" work. */}
          {(() => {
            const includesDone = fStatus.includes("done");
            return (
              <button type="button"
                      onClick={() => {
                        if (includesDone) {
                          setFStatus((s) => s.filter((x) => x !== "done"));
                        } else {
                          // If empty (= "all statuses") we don't touch it —
                          // "done" is already visible. Otherwise append.
                          setFStatus((s) => (s.length === 0 ? s : Array.from(new Set([...s, "done"]))));
                        }
                      }}
                      className={
                        "shrink-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest border-l border-border inline-flex items-center gap-1.5 " +
                        (includesDone
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : "text-muted-foreground hover:bg-muted/60")
                      }
                      title={
                        fStatus.length === 0
                          ? "Completed tasks are already visible (no status filter)"
                          : includesDone
                            ? "Hide completed tasks from this view"
                            : "Show completed tasks in this view"
                      }
                      data-testid="tasks-show-completed-chip">
                <CheckCircle2 className="w-3 h-3" />
                <span className="normal-case tracking-normal">
                  {includesDone ? "Completed shown" : "Show completed"}
                </span>
              </button>
            );
          })()}
          </div>
          {filtersOpen && (
          <div className="border-t border-border p-2 sm:p-3 flex flex-wrap items-center gap-2" data-testid="tasks-filterbar">
          {/* Feb 2026 UX audit · Search uses a local "draft" string so
              typing doesn't rerun the filter on every keystroke. The
              value commits to `fQuery` on Enter, Apply click, or blur.
              Visibly separate state so the user sees exactly what they
              typed vs. what's filtering, and typing stays buttery-smooth
              on 500+-task boards. */}
          <SearchWithApply value={fQuery} onApply={setFQuery} />
          <MultiSelect
            options={STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") }))}
            value={fStatus}
            onChange={setFStatus}
            allLabel="All statuses"
            placeholder="Status"
            width="w-40"
            testid="tasks-filter-status"
          />
          <MultiSelect
            options={clients.map((c) => ({ value: c.id, label: c.name }))}
            value={fClient}
            onChange={(v) => { setFClient(v); setFProject([]); }}
            allLabel="All clients"
            placeholder="Client"
            width="w-48"
            testid="tasks-filter-client"
          />
          <MultiSelect
            options={projectsForClient.map((p) => ({ value: p.id, label: p.name }))}
            value={fProject}
            onChange={setFProject}
            allLabel="All projects"
            placeholder="Project"
            width="w-48"
            testid="tasks-filter-project"
          />
          <MultiSelect
            options={[
              { value: "_unassigned", label: "— Unassigned —" },
              ...members.map((m) => ({ value: m.user_id, label: m.user?.name || m.user?.email || m.user_id })),
            ]}
            value={fAssignee}
            onChange={setFAssignee}
            allLabel="Anyone"
            placeholder="Assignee"
            width="w-48"
            testid="tasks-filter-assignee"
          />
          {/* R268llll · Feb 2026 — Department filter. Values are dept
              ids; `_none` means "tasks whose project has no dept". */}
          <MultiSelect
            options={[
              { value: "_none", label: "— No department —" },
              ...departments.map((d) => ({ value: d.id, label: d.code ? `${d.name} · ${d.code}` : d.name })),
            ]}
            value={fDepartment}
            onChange={setFDepartment}
            allLabel="All departments"
            placeholder="Department"
            width="w-48"
            testid="tasks-filter-department"
          />
          {tagOptions.length > 0 && (
            <MultiSelect
              options={tagOptions.map((t) => ({ value: t, label: t }))}
              value={fTag}
              onChange={setFTag}
              allLabel="Any tag"
              placeholder="Tag"
              width="w-40"
              testid="tasks-filter-tag"
            />
          )}
          {/* Feb 2026 R292w · Task category multi-filter */}
          {taskCategories.length > 0 && (
            <MultiSelect
              options={[
                { value: "", label: "— Uncategorised —" },
                ...taskCategories.map((c) => ({ value: c.id, label: c.name })),
              ]}
              value={fCategory}
              onChange={setFCategory}
              allLabel="Any category"
              placeholder="Category"
              width="w-44"
              testid="tasks-filter-category"
            />
          )}
          <MultiSelect
            options={[
              { value: "high", label: "🔴 High" },
              { value: "med",  label: "🟡 Medium" },
              { value: "low",  label: "🟢 Low" },
            ]}
            value={fRisk}
            onChange={setFRisk}
            allLabel="Any risk"
            placeholder="Risk"
            width="w-36"
            testid="tasks-filter-risk"
          />
          {/* R148l · Feb 2026 — Billable / billing-lifecycle filter */}
          <Select value={fBillable || "_all"} onValueChange={(v) => setFBillable(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-36 text-xs rounded-none" data-testid="tasks-filter-billable">
              <SelectValue placeholder="Billing" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Any billing</SelectItem>
              <SelectItem value="billable">Billable</SelectItem>
              <SelectItem value="not_billable">Not billable</SelectItem>
              <SelectItem value="billed">Billed</SelectItem>
              <SelectItem value="unbilled">Unbilled (billable · not billed)</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unpaid">Unpaid (billed · not paid)</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant={onlyRecurring ? "default" : "outline"}
            onClick={() => setOnlyRecurring((v) => !v)}
            className="rounded-none h-8 text-xs"
            data-testid="tasks-filter-recurring"
            title="Show only recurring tasks (has a repeat rule)"
          >
            <Repeat className="w-3 h-3 mr-1" /> {onlyRecurring ? "Recurring ✓" : "Recurring"}
          </Button>
          <Button size="sm" variant="ghost" onClick={clearFilters} className="rounded-none h-8 text-xs" data-testid="tasks-filter-clear">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
          <span className="text-[10px] text-muted-foreground ml-auto flex items-center gap-1">
            {tasks.length} task{tasks.length === 1 ? "" : "s"}
            {/* Iter Feb2026 · Cap notice — server returns up to 5000
                rows. When we hit the 5000 ceiling the user might be
                missing tasks; nudge them to narrow filters. */}
            {tasks.length >= 5000 && (
              <span className="inline-flex items-center gap-1 border border-amber-500/60 bg-amber-500/10 text-amber-800 px-1.5 py-0.5 text-[10px] uppercase tracking-widest"
                    title="Server capped the response at 5000 rows. Narrow filters (project, scope, status) to see the full set.">
                capped
              </span>
            )}
          </span>
        </div>
          )}
        </div>
        {/* R268llll · Feb 2026 — Quick-scope chip row. One-of scope
            selector (All / Assigned to me / Created by me / Watching /
            Mentioning me) sitting ABOVE the compact-mode gate so it's
            always visible. Compressed to a native <select> on mobile.  */}
        <div className="mt-2 sm:hidden" data-testid="tasks-scope-mobile">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">Scope</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="w-full h-9 border border-border bg-background text-xs rounded-none px-2"
            data-testid="tasks-scope-select">
            {[
              { v: "all",           l: "All" },
              { v: "involved",      l: "My tasks (any role)" },
              { v: "assigned-me",   l: "Assigned to me" },
              { v: "created-by-me", l: "Created by me" },
              { v: "watching",      l: "Watching" },
              { v: "mentioning-me", l: "Mentioning me" },
            ].map((s) => {
              const count = scopeCounts[s.v];
              return <option key={s.v} value={s.v}>{s.l}{count != null ? ` · ${count}` : ""}</option>;
            })}
          </select>
        </div>
        <div className="mt-2 hidden sm:flex flex-wrap items-center gap-2"
             data-testid="tasks-scope-chips">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">Scope</span>
          {/* Feb 2026 R291f · Merged Scope + Quick-report into a single
              dropdown with two sections. Picking a QUICK REPORT also
              resets every row-level filter (status/client/project/
              assignee/tag/dept/risk/billable/recurring/text) so the
              count on the menu matches exactly what the user sees.
              Picking a SCOPE does NOT reset filters \u2014 the scope is
              intentionally orthogonal to filters. */}
          {(() => {
            const SCOPES = [
              { v: "all",           l: "All",            tint: "bg-foreground text-background border-foreground" },
              { v: "involved",      l: "My tasks",       tint: "bg-primary text-primary-foreground border-primary" },
              { v: "assigned-me",   l: "Assigned to me", tint: "bg-emerald-600 text-white border-emerald-600" },
              { v: "created-by-me", l: "Created by me",  tint: "bg-sky-600 text-white border-sky-600" },
              { v: "watching",      l: "Watching",       tint: "bg-violet-600 text-white border-violet-600" },
              { v: "mentioning-me", l: "Mentioning me",  tint: "bg-amber-600 text-white border-amber-600" },
            ];
            const REPORTS = [
              { v: "overdue",      l: "Past due",     n: reportCounts.overdue,     tint: "bg-rose-600 text-white border-rose-600" },
              { v: "due_today",    l: "Due today",    n: reportCounts.due_today,   tint: "bg-amber-600 text-white border-amber-600" },
              { v: "upcoming",     l: "Upcoming 7d",  n: reportCounts.upcoming,    tint: "bg-sky-600 text-white border-sky-600" },
              { v: "effort_over",  l: "Over budget",  n: reportCounts.effort_over, tint: "bg-violet-600 text-white border-violet-600" },
              { v: "due",          l: "Due",          n: reportCounts.due,          tint: "bg-slate-700 text-white border-slate-700" },
              { v: "late",         l: "Late",         n: reportCounts.late,         tint: "bg-orange-600 text-white border-orange-600" },
              { v: "ahead",        l: "Ahead",        n: reportCounts.ahead,        tint: "bg-emerald-600 text-white border-emerald-600" },
              { v: "effort_under", l: "Under effort", n: reportCounts.effort_under, tint: "bg-teal-600 text-white border-teal-600" },
            ];
            const activeScope = SCOPES.find((s) => s.v === scope) || SCOPES[0];
            const activeReport = REPORTS.find((r) => r.v === quickReport);
            const scopeN = scopeCounts[activeScope.v];
            const reportN = activeReport ? activeReport.n : scopeN;
            // Reset every row-level filter when switching into a report.
            // Scope stays — a user may still want "My past-due".
            const applyReport = (v) => {
              if (v && v !== quickReport) {
                setFStatus([]); setFClient([]); setFProject([]); setFAssignee([]);
                setFTag([]); setFQuery(""); setFRisk([]); setFBillable("");
                setOnlyRecurring(false); setFDepartment([]);
              }
              setQuickReport(v);
            };
            const btnTint = activeReport ? activeReport.tint : activeScope.tint;
            const btnLabel = activeReport
              ? `${activeScope.l} \u00b7 ${activeReport.l}`
              : activeScope.l;
            const btnCount = activeReport ? reportN : scopeN;
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button"
                          className={`text-[11px] px-2.5 py-1 border inline-flex items-center gap-1.5 ${btnTint}`}
                          data-testid={`tasks-scope-${activeScope.v}`}>
                    {btnLabel} · {btnCount} ▾
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72" data-testid="tasks-scope-menu">
                  <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-muted-foreground">Scope</div>
                  {SCOPES.map((s) => (
                    <DropdownMenuItem key={s.v}
                                        onClick={() => setScope(s.v)}
                                        className={scope === s.v ? "bg-muted/60 font-semibold" : ""}
                                        data-testid={`tasks-scope-item-${s.v}`}>
                      <span className="flex-1">{s.l}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{scopeCounts[s.v]}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
                    <span>Quick report</span>
                    <span className="text-[9px] normal-case text-muted-foreground/70 tracking-normal">resets filters</span>
                  </div>
                  <DropdownMenuItem onClick={() => applyReport("")}
                                     className={!quickReport ? "bg-muted/60 font-semibold" : ""}
                                     data-testid="tasks-report-all">
                    <span className="flex-1">None</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{scopeN}</span>
                  </DropdownMenuItem>
                  {REPORTS.map((r) => (
                    <DropdownMenuItem key={r.v}
                                        onClick={() => applyReport(r.v)}
                                        className={quickReport === r.v ? "bg-muted/60 font-semibold" : ""}
                                        data-testid={`tasks-report-${r.v}`}>
                      <span className="flex-1">{r.l}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{r.n}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
          {(scope !== "all" || quickReport) && (
            <button type="button"
                    onClick={() => { setScope("all"); setQuickReport(""); }}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                    data-testid="tasks-scope-clear">
              Clear
            </button>
          )}
          {quickReport && (
            <span className="text-[10px] text-muted-foreground ml-auto">
              Showing {displayTasks.length} of {tasks.length} loaded
            </span>
          )}
        </div>

        {/* R138h legacy quick-report strip removed per UX audit — the
            new "Quick report ▾" dropdown in the scope row (above) now
            covers all 9 reports with counts in one slot. */}
        {false && !compactMode && (
        <div className="mt-2 flex flex-wrap items-center gap-2"
             data-testid="tasks-quick-reports">
          <button type="button"
                  onClick={() => setQuickReport("")}
                  className={`text-[11px] px-2 py-1 border ${quickReport === "" ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}
                  data-testid="tasks-report-all">
            All · {tasks.length}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "overdue" ? "" : "overdue")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "overdue" ? "bg-rose-600 text-white border-rose-600" : "border-rose-300 text-rose-800 hover:bg-rose-50"}`}
                  data-testid="tasks-report-overdue">
            Past due · {reportCounts.overdue}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "due_today" ? "" : "due_today")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "due_today" ? "bg-amber-600 text-white border-amber-600" : "border-amber-300 text-amber-800 hover:bg-amber-50"}`}
                  data-testid="tasks-report-due-today">
            Due today · {reportCounts.due_today}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "upcoming" ? "" : "upcoming")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "upcoming" ? "bg-sky-600 text-white border-sky-600" : "border-sky-300 text-sky-800 hover:bg-sky-50"}`}
                  data-testid="tasks-report-upcoming">
            Upcoming 7d · {reportCounts.upcoming}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "effort_over" ? "" : "effort_over")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "effort_over" ? "bg-violet-600 text-white border-violet-600" : "border-violet-300 text-violet-800 hover:bg-violet-50"}`}
                  data-testid="tasks-report-effort-over"
                  title="Tasks where logged hours have exceeded the estimated budget (hours) by >20%">
            Over budget · {reportCounts.effort_over}
          </button>
          {/* Iter Feb2026 · Extra scope chips — Due / Late / Ahead / Under effort */}
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "due" ? "" : "due")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "due" ? "bg-slate-700 text-white border-slate-700" : "border-slate-300 text-slate-800 hover:bg-slate-100"}`}
                  data-testid="tasks-report-due"
                  title="Tasks with a due date set and not yet done">
            Due · {reportCounts.due}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "late" ? "" : "late")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "late" ? "bg-orange-600 text-white border-orange-600" : "border-orange-300 text-orange-800 hover:bg-orange-50"}`}
                  data-testid="tasks-report-late"
                  title="Not yet started despite the planned start date, or completed after the planned end">
            Late · {reportCounts.late}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "ahead" ? "" : "ahead")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "ahead" ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-300 text-emerald-800 hover:bg-emerald-50"}`}
                  data-testid="tasks-report-ahead"
                  title="Completed before the planned end date">
            Ahead · {reportCounts.ahead}
          </button>
          <button type="button"
                  onClick={() => setQuickReport(quickReport === "effort_under" ? "" : "effort_under")}
                  className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${quickReport === "effort_under" ? "bg-teal-600 text-white border-teal-600" : "border-teal-300 text-teal-800 hover:bg-teal-50"}`}
                  data-testid="tasks-report-effort-under"
                  title="Some effort logged but under 60% of the estimate">
            Under effort · {reportCounts.effort_under}
          </button>
          {quickReport && (
            <span className="text-[10px] text-muted-foreground ml-auto">
              Showing {displayTasks.length} of {tasks.length}
            </span>
          )}
        </div>
        )}
      </div>

      <div className="px-4 sm:px-6 md:px-10 py-6">
        <TaskViewSwitcher
          tasks={displayTasks}
          members={members}
          patchTask={patchTask}
          onOpenTask={(t) => openTaskDetail(t)}
          storageKey="tasks-page-view"
          wsId={activeWorkspaceId}
          projects={projects}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onCreated={loadTasks}
          allowedViews={canTaskView}
          taskCategories={taskCategories}
          kanbanGroupBy={kanbanGroupBy}
          onKanbanGroupByChange={setKanbanGroupBy}
          renderBilling={() => (
            <TaskBillingView
              wsId={activeWorkspaceId}
              onOpenTask={(t) => openTaskDetail(t)}
            />
          )}
          renderList={() => (
            <div className={compactMode ? "space-y-1" : "space-y-3"}>
              {/* Oct 2026 · Mobile cap banner. Only shows when the
                  initial fetch came back at exactly the capped size,
                  so users with <500 tasks don't see noise. */}
              {mobileCapActive && mobileCapTotal != null && mobileCapTotal >= 500 && (
                <div className="border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-[11px] flex items-center gap-2"
                     data-testid="tasks-mobile-cap-banner">
                  <span className="font-semibold">Mobile mode:</span>
                  <span>
                    Showing the first 500 to keep the page snappy. Use
                    filters to narrow or tap Load all.
                  </span>
                  <button
                    type="button"
                    onClick={() => setMobileCapActive(false)}
                    className="ml-auto underline underline-offset-2 font-semibold"
                    data-testid="tasks-mobile-cap-load-all"
                  >
                    Load all
                  </button>
                </div>
              )}
              <div className={`border border-border bg-card ${compactMode ? "p-1.5" : "p-3"}`} data-testid="tasks-list-quickadd-wrap">
                <QuickAddTaskInline
                  wsId={activeWorkspaceId}
                  projects={projects}
                  members={members}
                  onCreated={loadTasks}
                  testid="tasks-list-quickadd"
                  placeholder="+ New task…"
                />
              </div>
              {/* Feb 2026 · Thin "VIEWING AS …" banner shown when a
                  manager has impersonated another manager via the Team
                  Roll-up widget's "View as" picker. Reinforces whose
                  subtree the task list is currently scoped to — the
                  active + overdue counts come straight from the roll-up
                  payload so filters applied below don't confuse the
                  intent of the "View as" selection. */}
              {viewAsScope?.is_impersonating && viewAsScope?.manager_name && (
                <div
                  className="border-x border-t border-primary/40 bg-primary/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-primary flex items-center gap-2"
                  data-testid="tasks-viewas-banner"
                >
                  <Eye className="w-3 h-3" />
                  <span>Viewing as · {viewAsScope.manager_name}</span>
                  <span className="font-normal normal-case tracking-normal text-[11px] text-muted-foreground">
                    · {viewAsScope.totals_active || 0} active
                    {(viewAsScope.totals_overdue || 0) > 0 && (
                      <span className="text-rose-700 font-semibold"> · {viewAsScope.totals_overdue} overdue</span>
                    )}
                  </span>
                </div>
              )}
              {firstLoad && !displayTasks?.length ? (
                <TaskSkeletonLoader rows={12} />
              ) : (
              <AdvancedTable
                tableKey="tasks"
                title="Tasks"
                rows={displayTasks}
                columns={columns}
                compact={compactMode}
                /* Feb 2026 R291c perf · Auto-virtualize when the list
                   gets large. Uses AdvancedTable's native CSS virtualization
                   (`content-visibility: auto`) which lets the browser
                   skip layout + paint for offscreen rows. Threshold is
                   200 so small boards keep the plain-render path for
                   print-friendly fallback. */
                virtualizeRows={(displayTasks?.length || 0) > 200}
                loading={busy && tasks.length === 0}
                emptyIcon={ListChecks}
                emptyTitle="No tasks match"
                emptyDescription="Adjust filters or convert a comment to task from a review item."
                onRowClick={(t) => openTaskDetail(t)}
                selectable
                selectedIds={new Set(selectedIds)}
                onSelectionChange={(set) => setSelectedIds(Array.from(set))}
                /* Feb 2026 · Per-group quick-add. When the list is
                   grouped by Project or Assignee, each group header
                   now shows a "+ Quick add" chip that opens the
                   QuickAddTaskPopover pre-scoped to that project or
                   assignee. Resolves the id from the first item in
                   the group (group keys are display names, not ids). */
                groupSummary={(items, groupKey, groupBy) => {
                  // Feb 2026 · Per-group totals in the group header so a
                  // PM can see cost / effort per owner (or per project /
                  // client) without expanding the group. These chips
                  // read the same filtered rows the table shows, so
                  // they always tie out with the Σ Totals footer strip.
                  const stats = _groupStats(items);
                  const today = _today;
                  const totalChips = (
                    <span className="inline-flex items-center flex-wrap gap-1"
                          data-testid={`tasks-group-totals-${groupBy}-${groupKey}`}>
                      <GroupStatChip label="Billable"       value={stats.billable}
                                     tip={`${stats.billable} of ${items.length} task${items.length === 1 ? "" : "s"} marked billable in this group.`} />
                      <GroupStatChip label="Est hrs"        value={_fmtNum(stats.est_hours)}
                                     tip="Sum of Est. hrs in this group." />
                      <GroupStatChip label="Actual hrs"     value={_fmtNum(stats.actual_hours)}
                                     tip="Sum of Actual hrs (actual → logged → timesheet) in this group." />
                      <GroupStatChip label="Timesheet"      value={_fmtNum(stats.timesheet_hours)}
                                     tip="Sum of timesheet-logged hours in this group." />
                      <GroupStatChip label="Total effort"   value={_fmtNum(stats.total_effort)}
                                     tip="Sum of total effort (actual + manual) in this group." />
                      {stats.overdue > 0 && (
                        <GroupStatChip label="Overdue"      value={stats.overdue}  tone="rose"
                                       tip={`${stats.overdue} overdue task${stats.overdue === 1 ? "" : "s"} in this group (due date before ${today}, not done).`} />
                      )}
                      {stats.due_today > 0 && (
                        <GroupStatChip label="Due today"    value={stats.due_today} tone="amber"
                                       tip={`${stats.due_today} task${stats.due_today === 1 ? "" : "s"} due today in this group.`} />
                      )}
                      {stats.blocked > 0 && (
                        <GroupStatChip label="Blocked"      value={stats.blocked}  tone="rose"
                                       tip={`${stats.blocked} task${stats.blocked === 1 ? "" : "s"} currently blocked in this group.`} />
                      )}
                    </span>
                  );

                  // Quick-add button is still available when applicable
                  // — append it after the totals chips.
                  if (!canQuickAdd || !items?.length) return totalChips;
                  const sample = items[0];
                  let preset = null;
                  let labelSuffix = "";
                  if (groupBy === "project_name" && sample.project_id) {
                    preset = { project_id: sample.project_id };
                    labelSuffix = ` to ${groupKey}`;
                  } else if (groupBy === "assignee" && sample.assignee_id) {
                    preset = { assignee_id: sample.assignee_id };
                    labelSuffix = ` for ${groupKey}`;
                  } else if (groupBy === "client_name" && sample.client_id) {
                    // Bonus: client grouping also gets quick-add (same
                    // code path — no scope beyond the client-flavored
                    // project pick inside the popover).
                    preset = { client_id: sample.client_id };
                    labelSuffix = ` for ${groupKey}`;
                  }
                  const quickAdd = preset ? (
                    <QuickAddTaskPopover
                      wsId={activeWorkspaceId}
                      preset={preset}
                      projects={projects}
                      members={members}
                      onCreated={loadTasks}
                      testid={`tasks-group-quickadd-${groupBy}-${groupKey}`}
                      trigger={
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline border border-primary/30 hover:border-primary px-1.5 py-0.5 bg-primary/5"
                          title={`Quick add a task${labelSuffix}`}
                          data-testid={`tasks-group-quickadd-btn-${groupBy}-${groupKey}`}
                        >
                          <Plus className="w-2.5 h-2.5" /> Quick add
                        </button>
                      }
                    />
                  ) : null;
                  return (
                    <span className="inline-flex items-center flex-wrap gap-2">
                      {totalChips}
                      {quickAdd}
                    </span>
                  );
                }}
                // Iter · Feb 2026 — Persist the page-level pill filters in
                // saved Views alongside the AdvancedTable's own state so
                // "Save current view" captures the entire filter combo.
                extraLayoutState={{
                  fStatus, fClient, fProject, fAssignee, fTag, fRisk,
                  fBillable, fQuery, showMine, onlyRecurring, quickReport,
                  includeArchived,
                }}
                onApplyExtraLayoutState={(s) => {
                  if (!s || typeof s !== "object") return;
                  if (Array.isArray(s.fStatus)) setFStatus(s.fStatus);
                  if (Array.isArray(s.fClient)) setFClient(s.fClient);
                  if (Array.isArray(s.fProject)) setFProject(s.fProject);
                  if (Array.isArray(s.fAssignee)) setFAssignee(s.fAssignee);
                  if (Array.isArray(s.fTag)) setFTag(s.fTag);
                  if (Array.isArray(s.fRisk)) setFRisk(s.fRisk);
                  if (typeof s.fBillable === "string") setFBillable(s.fBillable);
                  if (typeof s.fQuery === "string") setFQuery(s.fQuery);
                  if (typeof s.showMine === "boolean") setShowMine(s.showMine);
                  if (typeof s.onlyRecurring === "boolean") setOnlyRecurring(s.onlyRecurring);
                  if (typeof s.quickReport === "string") setQuickReport(s.quickReport);
                  if (typeof s.includeArchived === "boolean") setIncludeArchived(s.includeArchived);
                }}
              />
              )}
              <TaskTotalsStrip tasks={displayTasks} />
            </div>
          )}
        />
      </div>

      <BulkActionBar
        wsId={activeWorkspaceId}
        selectedIds={selectedIds}
        members={members}
        projects={projects}
        allTasks={tasks}
        onCleared={() => setSelectedIds([])}
        onApplied={loadTasks}
      />

      {detailTask && (
        <TaskDetail
          task={detailTask}
          members={members}
          allTasks={tasks}
          onClose={() => setDetailTask(null)}
          onPatch={(patch) => patchTask(detailTask.id, patch)}
          onArchiveChanged={async () => {
            try {
              const { data } = await api.get(`/tasks/${detailTask.id}`);
              if (data && data.id) setDetailTask(data);
              loadTasks();
            } catch { /* ignore */ }
          }}
          currentUserId={user?.id}
        />
      )}

      {/* Iter · Feb 2026 — Close-task modal. Fires whenever a user flips
          a task's status to Done (from row inline dropdown, TaskDetail
          Status picker, etc.) — see patchTask() where the guard sets
          closeGuardTask. Ensures no task closes silently at zero hours. */}
      <CloseTaskWithTimeDialog
        open={!!closeGuardTask}
        task={closeGuardTask}
        onCancel={() => setCloseGuardTask(null)}
        onDone={(resp) => {
          const t = resp?.task;
          if (t?.id) {
            setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, ...t } : x));
            if (detailTask && detailTask.id === t.id) {
              setDetailTask((d) => (d ? { ...d, ...t } : d));
            }
          }
          setCloseGuardTask(null);
        }}
      />

      {showBulk && (
        <BulkTaskCreator
          open={showBulk}
          onClose={() => setShowBulk(false)}
          wsId={activeWorkspaceId}
          projects={projects}
          members={members}
          onCreated={() => { setShowBulk(false); loadTasks(); }}
        />
      )}
      {/* R138l · Feb 2026 — Floating Quick-Add FAB on phones. Sits above
          the bottom safe-area so scrolling long lists never buries the
          primary create action. Desktop keeps the header quick-add. */}
      {canQuickAdd && activeWorkspaceId && (
        <div className="sm:hidden fixed bottom-4 right-4 z-40"
             data-testid="tasks-fab-wrap">
          <QuickAddTaskPopover
            wsId={activeWorkspaceId}
            projects={projects}
            members={members}
            onCreated={loadTasks}
            testid="tasks-fab-quickadd"
            triggerClassName="rounded-full h-14 w-14 shadow-lg bg-primary text-primary-foreground flex items-center justify-center"
            triggerIconOnly
          />
        </div>
      )}
    </>
  );
}

export function isOverdue(t) {
  if (!t.due_date) return false;
  if (t.status === "done") return false;
  try { return new Date(t.due_date) < new Date(new Date().toDateString()); } catch { return false; }
}
function shortDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }); } catch { return s; }
}

// Feb 2026 · Per-group effort / billable rollup for the task table
// group headers. Mirrors the Σ Totals footer strip math so the chips
// in a group header always tie out with the overall totals.
function _groupStats(items) {
  const s = {
    billable: 0,
    est_hours: 0,
    actual_hours: 0,
    timesheet_hours: 0,
    manual_effort: 0,
    total_effort: 0,
    overdue: 0,
    due_today: 0,
    blocked: 0,
  };
  const today = new Date(new Date().toDateString()).toISOString().slice(0, 10);
  for (const t of (items || [])) {
    if (t.billable) s.billable += 1;
    s.est_hours      += Number(t.est_hours || 0);
    s.actual_hours   += Number(t.actual_hours ?? t.logged_hours ?? t.timesheet_hours ?? 0);
    s.timesheet_hours+= Number(t.timesheet_hours || 0);
    s.manual_effort  += Number(t.manual_effort_hours || 0);
    s.total_effort   += Number(t.total_effort_hours || 0);
    if ((t.status || "").toLowerCase() === "blocked") s.blocked += 1;
    const d = (t.due_date || "").slice(0, 10);
    if (d && (t.status || "").toLowerCase() !== "done") {
      if (d < today) s.overdue += 1;
      else if (d === today) s.due_today += 1;
    }
  }
  return s;
}

function _fmtNum(n) {
  if (!n) return "0";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function GroupStatChip({ label, value, tip, tone }) {
  const toneCls = tone === "rose"  ? "border-rose-300 text-rose-800 bg-rose-50"
                : tone === "amber" ? "border-amber-300 text-amber-800 bg-amber-50"
                :                    "border-border text-foreground/80 bg-background";
  return (
    <span className={`inline-flex items-baseline gap-1 text-[10px] px-1.5 py-0.5 border ${toneCls} font-mono`}
          title={tip || ""}>
      <span className="text-[9px] uppercase tracking-widest opacity-70 normal-case">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </span>
  );
}

/**
 * Feb 2026 · Totals footer strip under the task table.
 *
 * The AdvancedTable has a native grand-totals row that sums any column
 * marked `sum: true`, but that only shows totals for the columns the
 * user has visible — hide the Hours columns and the row disappears.
 * This strip is a persistent, always-visible rollup of the key
 * financial / effort numbers for the current filtered list so a PM
 * can see "what are we committing to and how much have we spent" at
 * a glance.  Updates live with every filter change (it reads the
 * same `displayTasks` the table does).
 */
function TaskTotalsStrip({ tasks }) {
  const stats = useMemo(() => {
    const s = {
      count: tasks.length,
      billable: 0,
      est_hours: 0,
      actual_hours: 0,
      timesheet_hours: 0,
      manual_effort: 0,
      total_effort: 0,
      est_cost: 0,
      actual_cost: 0,
      overdue: 0,
      due_today: 0,
      blocked: 0,
    };
    const today = new Date(new Date().toDateString()).toISOString().slice(0, 10);
    for (const t of tasks) {
      if (t.billable) s.billable += 1;
      s.est_hours      += Number(t.est_hours || 0);
      s.actual_hours   += Number(t.actual_hours ?? t.logged_hours ?? t.timesheet_hours ?? 0);
      s.timesheet_hours+= Number(t.timesheet_hours || 0);
      s.manual_effort  += Number(t.manual_effort_hours || 0);
      s.total_effort   += Number(t.total_effort_hours || 0);
      s.est_cost       += Number(t.est_cost || 0);
      s.actual_cost    += Number(t.actual_cost || 0);
      if ((t.status || "").toLowerCase() === "blocked") s.blocked += 1;
      const d = (t.due_date || "").slice(0, 10);
      if (d && (t.status || "").toLowerCase() !== "done") {
        if (d < today) s.overdue += 1;
        else if (d === today) s.due_today += 1;
      }
    }
    return s;
  }, [tasks]);
  if (!tasks.length) return null;
  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const cells = [
    { label: "Tasks",       value: fmt(stats.count),         tip: "Rows currently shown in the table (after scope + filters + search)." },
    { label: "Billable",    value: fmt(stats.billable),      tip: `${stats.billable} of ${stats.count} tasks marked billable.` },
    { label: "Σ Est. hrs",  value: fmt(stats.est_hours),     tip: "Sum of Est. hrs across the filtered list." },
    { label: "Σ Actual hrs",value: fmt(stats.actual_hours),  tip: "Sum of Actual hrs (uses actual → logged → timesheet)." },
    { label: "Σ Timesheet", value: fmt(stats.timesheet_hours), tip: "Sum of timesheet-logged hours (own + sub-tasks)." },
    { label: "Σ Manual eff",value: fmt(stats.manual_effort), tip: "Sum of manually captured effort hours." },
    { label: "Σ Total eff", value: fmt(stats.total_effort),  tip: "Sum of total effort (actual + manual effort)." },
    { label: "Overdue",     value: fmt(stats.overdue),       tip: "Rows with a due date before today whose status isn't done.", tone: stats.overdue ? "text-rose-700" : "" },
    { label: "Due today",   value: fmt(stats.due_today),     tip: "Rows due today (not done).", tone: stats.due_today ? "text-amber-700" : "" },
    { label: "Blocked",     value: fmt(stats.blocked),       tip: "Rows currently in blocked status.", tone: stats.blocked ? "text-rose-700" : "" },
  ];
  return (
    <div className="mt-1 border-t-2 border-foreground/30 bg-muted/40"
         data-testid="tasks-totals-strip">
      <div className="flex flex-wrap items-stretch gap-x-0 gap-y-0 overflow-x-auto">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-foreground bg-foreground/5 border-r border-border flex items-center">
          Σ Totals
        </div>
        {cells.map((c) => (
          <div
            key={c.label}
            title={c.tip}
            className="px-3 py-1.5 border-r border-border last:border-r-0 min-w-[88px]"
            data-testid={`tasks-totals-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
            <div className={`text-sm font-mono font-bold leading-tight ${c.tone || ""}`}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// Feb 2026 R97 · Inline date editor for the Tasks list. Click the cell → a
// tiny date input replaces the chip; Enter/Blur commits; Esc cancels.
// The parent `onChange` calls `patchTask()` which already prompts for a
// justification comment (R94) when a date actually shifts, so we don't
// need to duplicate that prompt here.
export function InlineDateCell({ value, onChange, canEdit, testid, mono = false, overdue = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState((value || "").slice(0, 10));
  useEffect(() => { setDraft((value || "").slice(0, 10)); }, [value]);
  const commit = () => {
    const next = draft || null;
    if ((next || null) !== (value || null)) onChange(next);
    setEditing(false);
  };
  const cancel = () => { setDraft((value || "").slice(0, 10)); setEditing(false); };
  if (!canEdit) {
    return value
      ? <span className={mono ? "text-xs font-mono" : "text-xs"} data-testid={testid}>{shortDate(value)}</span>
      : <span className="text-muted-foreground" data-testid={testid}>—</span>;
  }
  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="h-6 w-32 px-1 text-xs border border-primary rounded-none bg-background focus:outline-none"
        data-testid={testid + "-input"}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`text-xs px-1.5 py-0.5 border border-transparent hover:border-border hover:bg-muted/50 rounded-none
                  ${overdue ? "text-rose-700 font-semibold" : ""}
                  ${!value ? "text-muted-foreground italic" : ""}
                  ${mono ? "font-mono" : ""}`}
      title="Click to edit"
      data-testid={testid}
    >
      {value ? shortDate(value) : "add date"}
    </button>
  );
}

// R151 · Feb 2026 — Inline title editor for the Task detail dialog.
// Click / focus to enter edit mode, Enter or blur saves, Escape reverts.
// Empty submissions are ignored (title is required at the DB level).
function DialogTitleEditor({ task, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title || "");
  useEffect(() => { if (!editing) setDraft(task.title || ""); }, [task.title, editing]);
  const commit = async () => {
    const v = draft.trim();
    setEditing(false);
    if (!v || v === task.title) { setDraft(task.title || ""); return; }
    try { await onPatch({ title: v }); } catch { setDraft(task.title || ""); }
  };
  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setDraft(task.title || ""); setEditing(false); }
        }}
        onBlur={commit}
        className="h-7 text-sm font-semibold rounded-none"
        data-testid="task-detail-title-input"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Click to rename"
      className="text-left w-full hover:bg-muted/40 -mx-1 px-1 rounded-none border border-transparent hover:border-border transition break-words line-clamp-2 text-sm font-semibold leading-snug"
      data-testid="task-detail-title-btn"
    >
      {task.title || <span className="text-muted-foreground italic">Untitled task</span>}
      <Pencil className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-70 align-middle" />
    </button>
  );
}

// Iter · Feb 2026 — Sticky quick-glance row that surfaces the 4-5 most
// consulted fields of a task (assignee, dates, effort, priority). Each
// chip is clickable — it dispatches `acq:task-section:scroll-to` which
// the matching TaskSection listens for to open + scroll + focus.
function TaskKeyFactsRow({ task, members }) {
  const assignees = Array.isArray(task.assignee_ids) && task.assignee_ids.length
    ? task.assignee_ids : (task.assignee_id ? [task.assignee_id] : []);
  const assigneeLabels = assignees
    .map((uid) => (members || []).find((m) => (m.user_id || m.id) === uid))
    .filter(Boolean)
    .map((m) => m.name || m.email || "").filter(Boolean);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }) : "—";
  const est = Number(task.estimated_hours || 0);
  const act = Number(task.actual_hours || 0);
  const effortRatio = est > 0 ? (act / est) : 0;
  // Iter · Feb 2026 R230 — Colour rules per user spec:
  //   Overdue  = rose  (text + bg tint on the Due chip)
  //   Over-effort = amber (text + bg tint on the Effort chip). Threshold
  //     dropped from 1.15x to 1.00x so any actual > estimated is flagged.
  //   Urgent priority = rose border on the whole row (loud visual cue).
  const dueOverdue = task.due_date && new Date(task.due_date) < new Date() && (task.status || "").toLowerCase() !== "done";
  const overEffort = est > 0 && effortRatio > 1;
  const nearEffort = est > 0 && effortRatio > 0.9 && effortRatio <= 1;
  const priority = (task.priority || "medium").toLowerCase();
  const urgent = priority === "urgent";
  const jumpTo = (targetId, focusSelector) => {
    window.dispatchEvent(new CustomEvent("acq:task-section:scroll-to", { detail: { targetId, focusSelector } }));
  };
  const chip = "inline-flex items-center gap-1 px-1.5 py-0.5 border border-border hover:border-primary hover:bg-primary/5 hover:text-primary rounded-none text-[10.5px] leading-tight whitespace-nowrap";
  const dueChipCls = dueOverdue
    ? `${chip} !text-rose-700 !bg-rose-50 !border-rose-400 hover:!bg-rose-100 hover:!text-rose-800 hover:!border-rose-500`
    : chip;
  const effortChipCls = overEffort
    ? `${chip} !text-amber-800 !bg-amber-50 !border-amber-400 hover:!bg-amber-100 hover:!text-amber-900 hover:!border-amber-500`
    : chip;
  const effortTextCls = overEffort ? "font-semibold" : nearEffort ? "text-amber-700" : "text-emerald-700";
  const priorityChipCls = urgent
    ? `${chip} !text-rose-700 !border-rose-500 hover:!border-rose-600`
    : chip;
  const rowCls = urgent
    ? "bg-background/95 border-t border-b border-rose-500 px-3 py-1 flex items-center gap-1 flex-wrap"
    : "bg-background/95 border-b border-border px-3 py-1 flex items-center gap-1 flex-wrap";
  return (
    <div
      className={rowCls}
      data-testid="task-detail-key-facts-row"
    >
      <button type="button" onClick={() => jumpTo("who", 'button[data-testid^="task-assignee-picker-"], [data-testid^="task-assignee-picker"]')}
              className={chip} title="Assignees — tap to edit"
              data-testid="task-kf-assignee">
        <Users className="w-3 h-3" />
        <span className="truncate max-w-[140px]">{assigneeLabels.length ? assigneeLabels.slice(0, 2).join(", ") + (assigneeLabels.length > 2 ? ` +${assigneeLabels.length - 2}` : "") : "Unassigned"}</span>
      </button>
      <button type="button" onClick={() => jumpTo("plan", 'input[type="date"], [data-testid$="-start-date"]')}
              className={chip} title="Start date — tap to edit"
              data-testid="task-kf-start">
        <Calendar className="w-3 h-3" /> Start {fmtDate(task.start_date)}
      </button>
      <button type="button" onClick={() => jumpTo("plan", 'input[type="date"], [data-testid$="-due-date"]')}
              className={dueChipCls}
              title={dueOverdue ? "OVERDUE — tap to edit" : "Due date — tap to edit"}
              data-testid="task-kf-due">
        <Calendar className="w-3 h-3" /> Due {fmtDate(task.due_date)}
      </button>
      <button type="button" onClick={() => jumpTo("effort", 'input[type="number"]')}
              className={effortChipCls}
              title={overEffort ? "Over budget — actual > estimated" : "Estimated vs actual hours"}
              data-testid="task-kf-effort">
        <Clock className="w-3 h-3" />
        <span className={effortTextCls}>{act}h / {est || "—"}h</span>
      </button>
      <button type="button" onClick={() => jumpTo("prio-progress", '[data-testid="task-detail-priority"]')}
              className={priorityChipCls}
              title={urgent ? "URGENT priority — tap to edit" : "Priority — tap to edit"}
              data-testid="task-kf-priority">
        <PriorityIcon priority={priority} className="w-3 h-3" />
        <span className="capitalize">{priority}</span>
      </button>
    </div>
  );
}


function TaskDetailWatchToggle({ task }) {
  const { activeWorkspaceId } = useAuth();
  const [watched, setWatched] = useState(false);
  const [busy, setBusy] = useState(false);

  const sync = React.useCallback(async () => {
    if (!activeWorkspaceId || !task?.id) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/task-watch/ids`);
      setWatched((data?.task_ids || []).includes(task.id));
    } catch { /* silent */ }
  }, [activeWorkspaceId, task?.id]);

  useEffect(() => { sync(); }, [sync]);
  useEffect(() => {
    const h = () => sync();
    window.addEventListener("acq:watch-refresh", h);
    return () => window.removeEventListener("acq:watch-refresh", h);
  }, [sync]);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    // Optimistic flip; toggleWatch fires acq:watch-refresh event.
    setWatched((v) => !v);
    await toggleWatch(activeWorkspaceId, task.id, watched);
    refreshWatchList();
    setBusy(false);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold h-8 px-2 border ${watched ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}
      title={watched ? "Remove from watch list" : "Add to watch list"}
      data-testid="task-detail-watch-toggle"
    >
      {watched ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
      {watched ? "Watching" : "Watch"}
    </button>
  );
}

export function TaskDetail({ task, members, allTasks = [], onClose, onPatch: onPatchRaw, onArchiveChanged }) {
  // Feb 2026 · `content_calendar.view` gates the Content Calendar
  // context block below. TaskDetail is defined as a top-level
  // component so we need its own `useWorkspacePerms()` call — the
  // parent `Tasks` component's `hasPerm` isn't in scope here.
  const { hasPerm } = useWorkspacePerms();
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [includeClient, setIncludeClient] = useState(true);
  // R159f · Feb 2026 — Mobile panel toggle. On phones the Fields and
  // Thread columns compete for vertical room; this state lets the user
  // shrink whichever they aren't editing. Values: "both" | "fields" |
  // "thread". Defaults to showing fields on load so nothing changes
  // for the "just opened" case.
  const [mobilePanel, setMobilePanel] = useState("both");
  // Iter · Feb 2026 — Single-button toggle-all for every TaskSection.
  // Ref keeps the *current* value so the click handler always reads the
  // latest snapshot even when state batching lags.
  const [allExpanded, setAllExpanded] = useState(true);
  const allExpandedRef = useRef(true);
  // R136 · Feb 2026 — Local member-name lookup for the Collaborators
  // chip strip. Members may be invited-but-unregistered so fall back
  // through user_id → member.id.
  const memberName = (uid) => {
    if (!uid) return "";
    const m = (members || []).find((mm) => (mm.user_id || mm.id) === uid);
    return m?.user?.name || m?.user?.email || m?.name || m?.email || uid.slice(0, 6);
  };
  // R69c · finance.view gate for the Task-detail Billing panel.
  // `hasPerm` is already provided by the useWorkspacePerms() call above.
  const canBilling = hasPerm("finance.view") || hasPerm("finance.manage") ||
    hasPerm("workspace.admin") || hasPerm("workspace.manage");

  const load = async () => {
    try {
      const { data } = await api.get(`/tasks/${task.id}/comments`, { params: { include_client: includeClient } });
      setThread(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id, includeClient]);

  // Iter 253z-2 · Wrap onPatch so the thread reloads after every field edit
  // — that way the system-audit row lands in the visible feed immediately.
  const onPatch = async (patch) => {
    const r = await onPatchRaw(patch);
    // Small debounce so the backend has time to insert the system row.
    setTimeout(() => { load(); }, 250);
    return r;
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { body, visibility });
      setBody("");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't post"));
    } finally { setBusy(false); }
  };

  const addTag = () => {
    const t = (tagDraft || "").trim().toLowerCase();
    if (!t) return;
    const next = Array.from(new Set([...(task.tags || []), t]));
    onPatch({ tags: next });
    setTagDraft("");
  };
  const removeTag = (tag) => {
    onPatch({ tags: (task.tags || []).filter((x) => x !== tag) });
  };

  // Iter · Feb 2026 — Sticky mobile header + floating scroll-to-top button.
  // The DialogContent is the scroll container on phones (`overflow-y-auto`),
  // so we track its scrollTop via a ref and toggle a FAB when the user has
  // scrolled past ~200px. Threshold intentionally low so the FAB appears
  // early on short devices (e.g. iPhone SE @ 667px height).
  const scrollRef = React.useRef(null);
  // Iter · Feb 2026 — Desktop uses the inner fields column as its
  // scroller (DialogContent is `lg:overflow-hidden`). Wire a second
  // ref so the scroll-to-top FAB works in both layouts.
  const fieldsColRef = React.useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const targets = [scrollRef.current, fieldsColRef.current].filter(Boolean);
    if (!targets.length) return undefined;
    const onScroll = () => {
      const anyScrolled = targets.some((el) => (el.scrollTop || 0) > 200);
      setShowScrollTop(anyScrolled);
    };
    targets.forEach((el) => el.addEventListener("scroll", onScroll, { passive: true }));
    onScroll();
    return () => targets.forEach((el) => el.removeEventListener("scroll", onScroll));
  }, [task.id]);
  const scrollToTop = () => {
    for (const el of [scrollRef.current, fieldsColRef.current]) {
      if (el) el.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        ref={scrollRef}
        className="max-w-5xl w-[96vw] max-h-[95vh] lg:max-h-[90vh] overflow-y-auto lg:overflow-hidden flex flex-col gap-0 !duration-75 p-0 lg:p-6"
        data-testid="task-detail-dialog"
        onEscapeKeyDown={onClose}
        hideClose
      >
        {/* Iter · Feb 2026 R230 — Header + Key Facts row are wrapped in a
            SINGLE sticky container so they stick together as one unit
            (no offset math, no crop gaps). On desktop (lg+) the wrapper
            becomes static because DialogContent has `lg:overflow-hidden`
            and the fields column is the true scroller. `-mx-*` cancels
            DialogContent's lg:p-6 so the sticky strip runs edge-to-edge
            just like the header used to. */}
        <div
          className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border lg:static lg:bg-transparent lg:backdrop-blur-0 lg:border-0 lg:-mx-6 lg:-mt-6 lg:px-6 lg:pt-6"
          data-testid="task-detail-sticky-wrap"
        >
        <DialogHeader
          className="px-3 pt-3 pb-1.5 lg:px-0 lg:pt-0 lg:pb-0"
          data-testid="task-detail-sticky-header"
        >
          {/* Iter · Feb 2026 — Title row is now: [priority][title][toggle-all][X].
              Space-efficient one-row header for phones; the standalone
              action strip below only carries Run SOP / Watch / Archive
              actions (no more Expand/Collapse pair). */}
          <DialogTitle className="pr-2">
            <span className="inline-flex items-start gap-1.5 w-full min-w-0">
              <PriorityIcon priority={task.priority || "medium"} className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span className="flex-1 min-w-0 break-words">
                <DialogTitleEditor task={task} onPatch={onPatch} />
              </span>
              {/* Sections toggle — icon-only, saves horizontal space
                  on phones and doubles as the single-button toggle. */}
              <button
                type="button"
                onClick={() => {
                  const next = !allExpandedRef.current;
                  allExpandedRef.current = next;
                  setAllExpanded(next);
                  window.dispatchEvent(new CustomEvent("acq:task-section:toggle-all", { detail: { open: next } }));
                }}
                className="shrink-0 w-7 h-7 inline-flex items-center justify-center border border-border hover:bg-muted rounded-none"
                title={allExpanded ? "Collapse all sections" : "Expand all sections"}
                data-testid="task-detail-sections-toggle-btn"
              >
                {allExpanded
                  ? <ChevronUp className="w-3.5 h-3.5" />
                  : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-7 h-7 inline-flex items-center justify-center border border-border bg-background/70 hover:bg-muted text-muted-foreground hover:text-foreground rounded-none"
                aria-label="Close task"
                title="Close (Esc)"
                data-testid="task-detail-close-x"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          </DialogTitle>
        </DialogHeader>
        {/* Iter · Feb 2026 R230 — Key Facts row sits inside the SAME sticky
            wrapper as the header so both stick together as one unit and
            no vertical gap can cause the row to be cropped behind the
            header when scrolling. */}
        <TaskKeyFactsRow task={task} members={members} />
        </div>
        {/* Feb 2026 · Compact unified action strip. Previously each
            component shipped its own button sizing (Run SOP was tall
            icon-plus-label, Watch was an uppercase chip, Archive was
            tiny) and the row towered over the Key Facts row above it.
            `.task-action-strip` applies a uniform h-7 / rounded-none /
            tight padding to every direct child button via :global so
            we don't need to touch three separate components. */}
        <div className="task-action-strip flex items-center gap-1.5 mb-2 flex-wrap px-3 lg:px-0 pt-1.5 lg:pt-0 pb-0.5"
             data-testid="task-detail-action-strip">
          <RunSOPButton linkKind="task" linkId={task.id} testKey={`task-${task.id}`} label="Run SOP" />
          <TaskDetailWatchToggle task={task} />
          {(hasPerm("workspace.admin") || hasPerm("workspace.manage") || hasPerm("task.archive")) && (
            <ArchiveToggle
              kind="task"
              entityId={task.id}
              archivedAt={task.archived_at}
              onDone={onArchiveChanged}
            />
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4 flex-1 min-h-0 lg:overflow-hidden px-4 pb-4 lg:px-0 lg:pb-0">
          {/* LEFT — fields.
              R159f · Feb 2026 — On phones (< lg) each column flows
              naturally (no per-panel overflow-y) so the Thread flows
              *below* the fields instead of becoming a sticky sidebar
              that eats vertical room. The mobile chevron below lets
              users hide the whole fields column when they only need
              the thread. */}
          <div ref={fieldsColRef} className={`space-y-3 lg:overflow-y-auto min-w-0 pr-1 lg:border-r lg:border-border lg:pr-4 pb-16 lg:pb-0 ${mobilePanel === "thread" ? "hidden lg:block" : "block"}`} data-testid="task-detail-fields-col">
            <div className="lg:hidden flex items-center gap-2 border border-border px-2 py-1 mb-2 bg-muted/40" data-testid="task-detail-mobile-fields-header">
              <ChevronDown className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] uppercase tracking-widest font-semibold">Details</span>
              <button type="button" onClick={() => setMobilePanel("thread")} className="ml-auto text-[10px] text-primary hover:underline" data-testid="task-detail-mobile-hide-fields">Show thread only</button>
            </div>
          <TaskSection id="description" title="Description" icon={MessageSquare} defaultOpen testid="task-section-description">
            <TaskDescriptionEditor task={task} onPatch={onPatch} />
          </TaskSection>

          {/* Iter 249 · CBP dropdowns — editors can move a task to a different
              Client, Brand, or Project inline. Cascades: choosing a Client
              filters the Brand list; choosing a Brand filters the Project list. */}
          <TaskSection id="cbp" title="What & Where · Client · Brand · Project" icon={FolderKanban} defaultOpen testid="task-section-cbp">
            <TaskCbpEditor task={task} onPatch={onPatch} />
          </TaskSection>

          <TaskSection id="who" title="Who · Assignee & Collaborators" icon={Users} defaultOpen testid="task-section-who">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow mb-1">Assignee</div>
              {/* Iter 247 · Searchable, sorted directory picker so a workspace
                  with 100+ members isn't a scrollathon.
                  Iter Feb 2026 R70 · `key` bound to the current assignee value
                  forces a fresh render whenever it changes — earlier the
                  Radix `PopoverTrigger asChild` button occasionally held the
                  stale label until the popup was closed and reopened, so the
                  user saw "saved in backend but dropdown shows old person".
                  Also honour invited-but-unregistered members (no user_id
                  yet) by falling back to their member `id` — matches the
                  inline row dropdown and `memberName()` lookup. */}
              <SearchableSelect
                key={`assignee-${task.id}-${task.assignee_id || "none"}`}
                value={task.assignee_id || ""}
                onValueChange={(v) => onPatch({ assignee_id: v || null })}
                options={[
                  { value: "", label: "Unassigned" },
                  ...(members || [])
                    .map((m) => ({
                      value: m.user_id || m.id,
                      // R148d · Feb 2026 — Hide email in the assignee
                      // dropdown so the list is scannable at a glance.
                      // Email still appears in profile drawers / mention
                      // popovers where it adds value.
                      label: m.user?.name || m.name || "Unknown",
                    }))
                    .filter((o) => o.value)
                    .sort((a, b) => String(a.label).localeCompare(String(b.label))),
                ]}
                placeholder="Search a teammate…"
                sort="none"
                clearable
                triggerClassName="h-8 text-xs w-full"
                data-testid="task-detail-assignee"
                width="100%"
              />
              {/* Feb 2026 · Read-only Department of the current assignee.
                  Primary source is the enriched task
                  (`assignee_department_name` set by the backend). When
                  the user changes the assignee locally — without saving
                  yet — we fall back to the member record which carries
                  `department_name` from the `/members` endpoint. Hidden
                  when the task is unassigned. */}
              {task.assignee_id && (() => {
                const localDept =
                  task.assignee_department_name ||
                  (members || []).find((m) => m.user_id === task.assignee_id)?.department_name ||
                  "";
                return (
                  <div className="mt-1.5 text-[10px] flex items-center gap-1.5"
                        data-testid="task-detail-assignee-department">
                    <span className="uppercase tracking-widest text-muted-foreground">Department</span>
                    <span className={localDept ? "font-medium text-foreground" : "italic text-muted-foreground"}>
                      {localDept || "—"}
                    </span>
                  </div>
                );
              })()}
            </div>
            {/* R136 · Feb 2026 — Collaborators: extra teammates who help
                on this task. Each has their own personal deadline +
                logs their own hours. Assignee remains the accountable
                owner. Managed via /tasks/{id}/collaborators endpoints. */}
            <div className="col-span-1 sm:col-span-2" data-testid="task-detail-collaborators">
              <div className="label-eyebrow mb-1 flex items-center gap-2 flex-wrap">
                Collaborators
                <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">
                  · own deadline + timesheet per person
                </span>
              </div>
              <div className="border border-border overflow-hidden mb-1">
                {(!task.collaborators || task.collaborators.length === 0) ? (
                  <div className="px-2 py-2 text-[10px] text-muted-foreground italic">
                    No collaborators yet — add a teammate below.
                  </div>
                ) : (
                  <table className="w-full text-[11px]"
                          data-testid="task-detail-collab-table">
                    <thead className="bg-muted/60">
                      <tr>
                        <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground">Teammate</th>
                        <th className="text-left px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[130px]">Personal deadline</th>
                        <th className="text-center px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[60px]">Can edit</th>
                        <th className="text-right px-2 py-1 font-semibold text-[9px] uppercase tracking-widest text-muted-foreground w-[80px]">Added</th>
                        <th className="w-[30px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(task.collaborators || []).map((c) => {
                        const name = memberName(c.user_id) || c.user_id.slice(0, 6);
                        return (
                          <tr key={c.user_id}
                              className="border-t border-border hover:bg-muted/30"
                              data-testid={`task-collab-${c.user_id}`}>
                            <td className="px-2 py-1 font-medium truncate max-w-[180px]"
                                data-testid={`task-collab-name-${c.user_id}`}>
                              {name}
                            </td>
                            <td className="px-2 py-1">
                              <input type="date"
                                     value={c.personal_due_date || ""}
                                     onChange={async (e) => {
                                       try {
                                         const r = await api.patch(`/tasks/${task.id}/collaborators/${c.user_id}`, {
                                           user_id: c.user_id,
                                           personal_due_date: e.target.value || null,
                                           can_edit: !!c.can_edit,
                                         });
                                         onPatch({ collaborators: r.data.collaborators || [] });
                                       } catch (err) { toast.error("Update failed"); }
                                     }}
                                     className="text-[11px] bg-transparent border-0 outline-none w-[115px]"
                                     title="Personal deadline"
                                     data-testid={`task-collab-date-${c.user_id}`} />
                            </td>
                            <td className="px-2 py-1 text-center">
                              <input type="checkbox" checked={!!c.can_edit}
                                     onChange={async (e) => {
                                       try {
                                         const r = await api.patch(`/tasks/${task.id}/collaborators/${c.user_id}`, {
                                           user_id: c.user_id,
                                           personal_due_date: c.personal_due_date || null,
                                           can_edit: e.target.checked,
                                         });
                                         onPatch({ collaborators: r.data.collaborators || [] });
                                       } catch (err) { toast.error("Update failed"); }
                                     }}
                                     title="Can this collaborator edit task fields?"
                                     data-testid={`task-collab-canedit-${c.user_id}`} />
                            </td>
                            <td className="px-2 py-1 text-right text-[10px] text-muted-foreground"
                                data-testid={`task-collab-added-${c.user_id}`}>
                              {c.added_at ? shortISO(c.added_at) : "—"}
                            </td>
                            <td className="px-1 py-1 text-right">
                              <button type="button"
                                      onClick={async () => {
                                        if (!window.confirm(`Remove ${name} as collaborator?`)) return;
                                        try {
                                          await api.delete(`/tasks/${task.id}/collaborators/${c.user_id}`);
                                          onPatch({ collaborators: (task.collaborators || []).filter((x) => x.user_id !== c.user_id) });
                                        } catch (err) { toast.error("Remove failed"); }
                                      }}
                                      className="text-rose-600 hover:text-rose-800 text-[13px] leading-none"
                                      data-testid={`task-collab-remove-${c.user_id}`}
                                      title="Remove">×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <SearchableSelect
                key={`add-collab-${task.id}-${(task.collaborators || []).length}`}
                value=""
                onValueChange={async (v) => {
                  if (!v) return;
                  try {
                    const r = await api.post(`/tasks/${task.id}/collaborators`, {
                      user_id: v, personal_due_date: null, can_edit: false,
                    });
                    onPatch({ collaborators: r.data.collaborators || [] });
                    toast.success("Collaborator added");
                  } catch (err) {
                    toast.error(apiErrorMessage(err, "Add failed"));
                  }
                }}
                options={(members || [])
                  .map((m) => ({
                    value: m.user_id || m.id,
                    label: m.user?.name || m.name || "Unknown",   // R148d · name only
                  }))
                  .filter((o) => o.value
                    && o.value !== task.assignee_id
                    && !(task.collaborators || []).some((c) => c.user_id === o.value))
                  .sort((a, b) => String(a.label).localeCompare(String(b.label)))}
                placeholder="+ Add collaborator…"
                sort="none"
                triggerClassName="h-7 text-[11px] w-full"
                data-testid="task-detail-collab-add"
                width="100%"
              />
            </div>
            {/* R137 · Feb 2026 — Per-collaborator hours & personal-deadline
                timeline. Hidden when nobody's logged time yet AND there
                are zero collaborators (see component early-return). */}
            <div className="col-span-1 sm:col-span-2">
              <CollaboratorHoursPanel task={task} />
            </div>
            {/* R137 · Feb 2026 — Subtasks: break a task into smaller
                actionable pieces without cluttering the main project board.
                Roll-up progress + inline drawer open on click. */}
            <div className="col-span-1 sm:col-span-2">
              <TaskSubtasksPanel task={task} members={members} />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Status</div>
              <Select value={task.status} onValueChange={(v) => onPatch({ status: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="task-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Iter Feb2026 · Phase picker inside the task detail dialog. */}
            {task.project_id && (
              <div>
                <div className="label-eyebrow mb-1">Phase</div>
                <PhasePicker
                  projectId={task.project_id}
                  value={task.phase_id}
                  onChange={(next) => onPatch({ phase_id: next })}
                  size="field"
                  testid="task-detail-phase-picker"
                />
              </div>
            )}
            <div>
              <div className="label-eyebrow mb-1">Health</div>
              <Select value={task.health || "on_track"} onValueChange={(v) => onPatch({ health: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="task-detail-health"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_track">🟢 On track</SelectItem>
                  <SelectItem value="at_risk">🟡 At risk</SelectItem>
                  <SelectItem value="off_track">🔴 Off track</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="label-eyebrow mb-1">Rating</div>
              <div className="flex items-center gap-0.5" data-testid="task-detail-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onPatch({ rating: task.rating === n ? null : n })}
                    className={`text-lg leading-none ${(task.rating || 0) >= n ? "text-amber-500" : "text-muted-foreground/40"} hover:text-amber-500`}
                    data-testid={`task-detail-rating-${n}`}
                    aria-label={`${n} star`}
                  >
                    ★
                  </button>
                ))}
                {task.rating && (
                  <button type="button" onClick={() => onPatch({ rating: null })} className="ml-1 text-[10px] text-muted-foreground hover:text-foreground" data-testid="task-detail-rating-clear">
                    clear
                  </button>
                )}
              </div>
            </div>
            {/* Iter 253w · Admin-managed category autocomplete. Spans both
                columns so long category names don't wrap the label row. */}
            <div className="col-span-1 sm:col-span-2">
              <div className="label-eyebrow mb-1">Category</div>
              <TaskCategoryPicker task={task} onPatch={onPatch} />
            </div>
          </div>
          </TaskSection>

          {/* R151 · Feb 2026 — Plan section (all date-related fields
              together). Extracted from the Who section so scheduling
              intent + variance live in one focused block, separate from
              the "who is doing what" concern. */}
          <TaskSection id="plan" title="Plan and Progress" icon={TrendingUp} defaultOpen testid="task-section-plan">
          <div className="space-y-3">
            {/* Feb 2026 · Priority + % complete sits at the top of the
                combined Plan and Progress block (merged from the former
                standalone "Priority & Progress" section). Keeps all
                intent + progress + schedule in one scroll-free panel
                and shaves ~90px off the modal height. */}
            <div className="bg-muted/10 p-3" data-testid="task-detail-progress-block">
              <div className="label-eyebrow mb-2">Priority &amp; progress</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="label-eyebrow text-[9px] mb-1">Priority</div>
                  <Select value={task.priority || "medium"} onValueChange={(v) => onPatch({ priority: v })}>
                    <SelectTrigger className="h-7 text-xs rounded-none px-2 min-w-0" data-testid="task-detail-priority">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {["urgent", "high", "medium", "low"].map((p) => (
                        <SelectItem key={p} value={p}>
                          <span className="inline-flex items-center gap-1.5 capitalize text-xs">
                            <PriorityIcon priority={p} className="w-3 h-3" />
                            {p}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="label-eyebrow text-[9px] mb-1">% Complete</div>
                  <div className="flex items-center gap-2">
                    <Input type="number" min="0" max="100" step="5"
                            defaultValue={task.progress_percent ?? 0}
                            key={`pp-${task.id}-${task.progress_percent ?? "_"}`}
                            onBlur={(e) => {
                              const v = e.target.value === "" ? null : Math.max(0, Math.min(100, Number(e.target.value)));
                              if (v !== (task.progress_percent ?? null)) onPatch({ progress_percent: v });
                            }}
                            className="h-7 text-xs w-14 sm:w-16 shrink-0"
                            data-testid="task-detail-progress" />
                    <div className="flex-1 h-2 bg-muted overflow-hidden min-w-0">
                      <div className="h-full bg-primary transition-all"
                            style={{ width: `${task.progress_percent || 0}%` }} />
                    </div>
                    <span className="text-xs font-mono w-10 text-right shrink-0">{task.progress_percent || 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* R148d · Feb 2026 — Grouped Plan dates (Start + Due) with a
                block border and a Plan eyebrow. Uses TaskDateField which
                handles the empty → first-set case and pops a reason
                dialog for real changes. */}
            <div className="bg-muted/10 p-3" data-testid="task-detail-plan-block">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow">Scheduled</div>
                <PlanVsActualChip task={task} />
              </div>
              {/* R148o · Pending reschedule badge */}
              {task.reschedule_pending && task.reschedule_pending.status === "pending" && (
                <div className="mb-2 border border-amber-400 bg-amber-50 px-2 py-1.5 text-[11px]" data-testid="task-reschedule-pending">
                  <div className="flex items-center gap-1 text-amber-800 font-semibold uppercase tracking-widest text-[10px]">
                    <Clock className="w-3 h-3" /> Pending approval
                  </div>
                  <ul className="mt-1 space-y-0.5 text-[11px] text-amber-900">
                    {Object.entries(task.reschedule_pending.changes || {}).map(([k, ch]) => (
                      <li key={k}>
                        <b>{k.replace(/_/g, " ")}</b>: <span className="line-through opacity-60">{ch.from || "—"}</span> → <b>{ch.to || "—"}</b>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-0.5 text-[10px] text-amber-900/80">
                    Requested by {task.reschedule_pending.requested_by_name || "—"} · reason: {task.reschedule_pending.reason || "—"}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="label-eyebrow text-[9px] mb-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3 shrink-0" />Start date</div>
                  <TaskDateField
                    task={task}
                    field="start_date"
                    value={task.start_date}
                    inputType="date"
                    onCommit={onPatch}
                    testid="task-detail-start"
                  />
                </div>
                <div>
                  <div className="label-eyebrow text-[9px] mb-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3 shrink-0" />Due date</div>
                  <TaskDateField
                    task={task}
                    field="due_date"
                    value={task.due_date}
                    inputType="date"
                    onCommit={onPatch}
                    testid="task-detail-due"
                  />
                </div>
              </div>
            </div>

            {/* R148d · Feb 2026 — Grouped Actual dates (Actual start +
                Actual completion) with block border, eyebrow, and per-field
                early/late chips. */}
            <div className="bg-muted/10 p-3" data-testid="task-detail-actual-block">
              <div className="label-eyebrow mb-2">Actual · logged</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="label-eyebrow text-[9px] mb-1 flex flex-wrap items-baseline gap-x-1">Actual start <span className="text-muted-foreground text-[9px] normal-case tracking-normal">(timestamp)</span></div>
                  <TaskDateField
                    task={task}
                    field="actual_start_date"
                    value={task.actual_start_date}
                    inputType="datetime-local"
                    onCommit={onPatch}
                    testid="task-detail-actual-start"
                  />
                  {task.start_variance_days != null && (
                    <div className={`text-[10px] mt-1 font-semibold ${task.start_variance_days > 0 ? "text-red-600" : task.start_variance_days < 0 ? (task.actual_start_date ? "text-emerald-600" : "text-sky-700") : "text-muted-foreground"}`}
                         data-testid="task-detail-start-variance">
                      {task.start_variance_days > 0 ? `${task.start_variance_days}d late` :
                       task.start_variance_days < 0
                         ? (task.actual_start_date ? `${-task.start_variance_days}d early` : `${-task.start_variance_days}d to go`)
                         : "on time"}
                    </div>
                  )}
                </div>
                <div>
                  <div className="label-eyebrow text-[9px] mb-1 flex flex-wrap items-baseline gap-x-1">Actual completion <span className="text-muted-foreground text-[9px] normal-case tracking-normal">(timestamp)</span></div>
                  <TaskDateField
                    task={task}
                    field="actual_end_date"
                    value={task.actual_end_date}
                    inputType="datetime-local"
                    onCommit={onPatch}
                    testid="task-detail-actual-end"
                  />
                  {task.completion_variance_days != null && (
                    <div className={`text-[10px] mt-1 font-semibold ${task.completion_variance_days > 0 ? "text-red-600" : task.completion_variance_days < 0 ? (task.actual_end_date ? "text-emerald-600" : "text-sky-700") : "text-muted-foreground"}`}
                         data-testid="task-detail-completion-variance">
                      {task.completion_variance_days > 0 ? `${task.completion_variance_days}d late` :
                       task.completion_variance_days < 0
                         ? (task.actual_end_date ? `${-task.completion_variance_days}d early` : `${-task.completion_variance_days}d to go`)
                         : "on time"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </TaskSection>

          <TaskSection id="effort" title="Efforts · Time Tracking" icon={Clock} defaultOpen testid="task-section-effort">
          <div className="p-3 bg-muted/20" data-testid="task-effort-panel">
            <div className="label-eyebrow mb-2">Effort tracking</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Estimated (h)</div>
                <Input type="number" min="0" step="0.25"
                        defaultValue={task.est_hours ?? ""}
                        key={`est-${task.id}-${task.est_hours ?? "_"}`}
                        onBlur={(e) => {
                          const v = e.target.value === "" ? null : Number(e.target.value);
                          if (v !== (task.est_hours ?? null)) onPatch({ est_hours: v });
                        }}
                        className="h-8 text-xs" placeholder="0"
                        data-testid="task-detail-est-hours" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Manual effort (h)</div>
                <Input
                  type="number"
                  min="0"
                  step="0.25"
                  defaultValue={task.manual_effort_hours ?? ""}
                  key={`me-${task.id}-${task.manual_effort_hours ?? "_"}`}
                  onBlur={(e) => {
                    const v = e.target.value === "" ? null : Number(e.target.value);
                    if (v !== (task.manual_effort_hours ?? null)) onPatch({ manual_effort_hours: v });
                  }}
                  className="h-8 text-xs"
                  placeholder="0"
                  data-testid="task-detail-manual-effort"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Timesheet (h)</div>
                <div className="h-8 flex items-center px-2 text-xs font-mono bg-background border border-border text-primary" data-testid="task-detail-timesheet-hours">
                  {task.timesheet_hours ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Total (h)</div>
                <div className="h-8 flex items-center px-2 text-xs font-mono bg-background border border-border font-semibold" data-testid="task-detail-total-effort">
                  {task.total_effort_hours ?? ((task.manual_effort_hours || 0) + (task.timesheet_hours || 0))}
                </div>
              </div>
              {task.effort_variance_hours != null && (
                <div className="col-span-2 sm:col-span-4 border-t border-border pt-2 flex items-center gap-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Effort variance</div>
                  <div className={`text-sm font-mono font-semibold ${task.effort_variance_hours > 0 ? "text-red-600" : task.effort_variance_hours < 0 ? "text-emerald-600" : ""}`}
                       data-testid="task-detail-effort-variance">
                    {task.effort_variance_hours > 0 ? "+" : ""}{task.effort_variance_hours}h
                    {task.effort_variance_pct != null && ` (${task.effort_variance_pct > 0 ? "+" : ""}${task.effort_variance_pct}%)`}
                  </div>
                  <div className="text-[10px] text-muted-foreground ml-auto">
                    {task.effort_variance_hours > 0 ? "Over budget" : task.effort_variance_hours < 0 ? "Under budget" : "On budget"}
                  </div>
                </div>
              )}
              <div className="col-span-2 sm:col-span-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Manual effort note</div>
                <Input
                  defaultValue={task.manual_effort_note ?? ""}
                  key={`men-${task.id}-${task.manual_effort_note ?? "_"}`}
                  onBlur={(e) => {
                    const v = e.target.value || null;
                    if (v !== (task.manual_effort_note ?? null)) onPatch({ manual_effort_note: v });
                  }}
                  placeholder="Where did these hours come from? e.g. Imported from Trello time tracking"
                  className="h-8 text-xs"
                  data-testid="task-detail-manual-effort-note"
                />
              </div>
            </div>
          </div>

          {/* R69c · Billing lifecycle — finance.view gated. Kept flat with
              three checkboxes (billable → billed → paid) that cascade
              enable-state so states can't skip. Anyone without the perm
              simply doesn't see this panel. */}
          {canBilling && (
            <div className="border border-border p-3 bg-muted/10" data-testid="task-detail-billing-panel">
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2">
                <div className="label-eyebrow">Billing</div>
                <div className="text-[10px] text-muted-foreground">
                  {task.paid ? "Paid"
                   : task.billed ? "Billed · awaiting payment"
                   : task.ready_to_bill ? "Ready to bill · awaiting invoice"
                   : task.billable ? "Billable · in progress"
                   : "Not billable"}
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2 sm:grid sm:grid-cols-4 sm:gap-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="task-detail-billable-label">
                  <input
                    type="checkbox"
                    checked={!!task.billable}
                    onChange={(e) => {
                      const on = e.target.checked;
                      // Unchecking billable clears every downstream flag —
                      // a "non-billable" task can't be ready-to-bill,
                      // billed or paid.
                      const patch = { billable: on };
                      if (!on) { patch.ready_to_bill = false; patch.billed = false; patch.paid = false; }
                      onPatch(patch);
                    }}
                    className="h-4 w-4 accent-primary cursor-pointer"
                    data-testid="task-detail-billable"
                  />
                  <span>Billable</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="task-detail-ready-to-bill-label">
                  <input
                    type="checkbox"
                    checked={!!task.ready_to_bill}
                    disabled={!task.billable}
                    onChange={(e) => {
                      const on = e.target.checked;
                      // Unchecking Ready to bill does NOT auto-unbill a
                      // billed task — Finance may have already invoiced
                      // despite the PM flipping it back. We only prevent
                      // ticking Ready-to-bill when the task is not
                      // billable in the first place.
                      onPatch({ ready_to_bill: on });
                    }}
                    className="h-4 w-4 accent-amber-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                    title={!task.billable ? "Mark as billable first" : "Hand off to Finance for invoicing"}
                    data-testid="task-detail-ready-to-bill"
                  />
                  <span className={!task.billable ? "text-muted-foreground/60" : ""}>Ready to bill</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="task-detail-billed-label">
                  <input
                    type="checkbox"
                    checked={!!task.billed}
                    disabled={!task.billable}
                    onChange={(e) => {
                      const on = e.target.checked;
                      const patch = { billed: on };
                      if (!on) patch.paid = false;
                      onPatch(patch);
                    }}
                    className="h-4 w-4 accent-sky-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                    title={!task.billable ? "Mark as billable first" : ""}
                    data-testid="task-detail-billed"
                  />
                  <span className={!task.billable ? "text-muted-foreground/60" : ""}>Billed</span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="task-detail-paid-label">
                  <input
                    type="checkbox"
                    checked={!!task.paid}
                    disabled={!task.billed}
                    onChange={(e) => onPatch({ paid: e.target.checked })}
                    className="h-4 w-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                    title={!task.billed ? "Mark as billed first" : ""}
                    data-testid="task-detail-paid"
                  />
                  <span className={!task.billed ? "text-muted-foreground/60" : ""}>Paid</span>
                </label>
              </div>
              {/* Feb 2026 · Date stamps for the Billed / Paid flags.
                  Backend auto-fills with today when a flag is first
                  flipped, but admins can retro-date here. Hidden
                  until the matching flag is set. */}
              {(task.billed || task.paid) && (
                <div className="flex flex-wrap gap-x-5 gap-y-2 pt-2 border-t border-border/60 mt-2">
                  {task.billed && (
                    <label className="flex items-center gap-2 text-xs" data-testid="task-detail-billed-date-label">
                      <span className="text-muted-foreground">Billed on</span>
                      <input
                        type="date"
                        value={task.billed_date || ""}
                        onChange={(e) => onPatch({ billed_date: e.target.value || null })}
                        className="h-7 text-xs px-2 border border-border rounded-none bg-background"
                        data-testid="task-detail-billed-date"
                      />
                    </label>
                  )}
                  {task.paid && (
                    <label className="flex items-center gap-2 text-xs" data-testid="task-detail-paid-date-label">
                      <span className="text-muted-foreground">Paid on</span>
                      <input
                        type="date"
                        value={task.paid_date || ""}
                        onChange={(e) => onPatch({ paid_date: e.target.value || null })}
                        className="h-7 text-xs px-2 border border-border rounded-none bg-background"
                        data-testid="task-detail-paid-date"
                      />
                    </label>
                  )}
                </div>
              )}
              {/* Feb 2026 R293s · Billing month — the accounting bucket for
                  this task. Lives OUTSIDE the "billed || paid" guard so
                  PMs can pre-set the bucket before Finance flips Billed.
                  Backend auto-fills from Billed on if left blank. */}
              {task.billable && (
                <div className="flex flex-wrap gap-x-5 gap-y-2 pt-2 border-t border-border/60 mt-2">
                  <label className="flex items-center gap-2 text-xs" data-testid="task-detail-billing-month-label">
                    <span className="text-muted-foreground">Billing month</span>
                    <input
                      type="month"
                      value={task.billing_month || ""}
                      onChange={(e) => onPatch({ billing_month: e.target.value || null })}
                      className="h-7 text-xs px-2 border border-border rounded-none bg-background"
                      data-testid="task-detail-billing-month"
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Iter 253w · Inline time-entry widget lives right under the effort
              panel so logging time doesn't require jumping to Timesheet. */}
          <TaskTimeEntryInline task={task} onLogged={() => setTimeout(load, 250)} />

          {/* Feb 2026 · Recurrence — mirrors Meetings pattern. */}
          <RecurrenceEditor
            value={task.recurrence}
            mode={task.recurrence_mode || "on_completion"}
            onChange={(rule, m) => {
              if (rule === null) onPatch({ clear_recurrence: true });
              else onPatch({ recurrence: rule, recurrence_mode: m });
            }}
            testid="task-detail-recurrence"
          />
          {task.series_id && (
            <div className="text-[10px] text-muted-foreground italic" data-testid="task-detail-series-badge">
              Part of a recurring series ({task.series_index === 0 ? "series head" : `occurrence ${task.series_index + 1}`}).
              {task.recurrence_mode === "on_completion" && task.status !== "done" && " Next task will be created when this one is marked done."}
            </div>
          )}
          </TaskSection>

          {/* Feb 2026 · Content-calendar context — surfaced as an always-visible
              block (not inside a collapsed section) so the Deliverable name,
              Topic, and "Open in Calendar" shortcut are visible the moment
              TaskDetail opens. Gated by `content_calendar.view` AND the task
              actually being linked to a deliverable. Previously nested inside
              the collapsed-by-default "Files · Checklist · Links" section
              which made it invisible on first open (flagged by Feb 2026 QA). */}
          {hasPerm("content_calendar.view") && (task.cc_deliverable_name || task.cc_topic) && (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-3"
                 data-testid="task-detail-cc-context">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <CalendarDays className="w-3 h-3" /> Content Calendar
                </span>
                {task.content_item_id && (
                  <Link
                    to={`/app/content-calendar?open=${encodeURIComponent(task.content_item_id)}`}
                    className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-semibold text-primary hover:underline border border-primary/30 hover:border-primary px-1 py-0.5 bg-primary/5"
                    title="Open the parent deliverable in the Content Calendar"
                    data-testid="task-detail-cc-open"
                  >
                    <CalendarDays className="w-2.5 h-2.5" /> Open in Calendar →
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Deliverable</div>
                  <div className="font-medium break-words" data-testid="task-detail-cc-deliverable">
                    {task.cc_deliverable_name || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Topic</div>
                  <div className={`break-words ${task.cc_topic_linked ? "font-bold text-emerald-800" : "font-semibold italic text-muted-foreground"}`}
                       data-testid="task-detail-cc-topic">
                    {task.cc_topic || "—"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <TaskSection id="files" title="Files · Checklist · Links" icon={FileText} defaultOpen={false} testid="task-section-files">
          {/* Iter 253z-4 · Checklist + Attachments + External links */}
          <TaskChecklistBlock task={task} onChange={() => setTimeout(load, 250)} />
          {hasPerm("task.attachments.view") && (
            <TaskAttachmentsBlock task={task} onChange={() => setTimeout(load, 250)} />
          )}
          <TaskLinksBlock task={task} onChange={() => setTimeout(load, 250)} />

          {/* Iter 207b · Linked Social Media Calendar deliverable */}
          {task.linked_content_item && (
            <div className="border border-purple-500/40 bg-purple-500/5 p-3" data-testid="task-detail-linked-content">
              <div className="text-[10px] uppercase tracking-widest text-purple-700 mb-1 flex items-center gap-2">
                <TagIcon className="w-3 h-3" /> Linked Social Media Deliverable
              </div>
              <div className="flex items-center gap-3">
                {task.linked_content_item.thumbnail_url && (
                  <img src={task.linked_content_item.thumbnail_url} alt=""
                        className="w-14 h-14 object-cover border border-border" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {task.linked_content_item.title || task.linked_content_item.id.slice(0,8)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {task.linked_content_item.brand_name && `${task.linked_content_item.brand_name} · `}
                    {task.linked_content_item.channel || "—"}
                    {task.linked_content_item.post_type && ` · ${task.linked_content_item.post_type}`}
                    {task.linked_content_item.planned_date && ` · scheduled ${task.linked_content_item.planned_date}`}
                    {task.linked_content_item.status && ` · ${task.linked_content_item.status}`}
                  </div>
                </div>
                <Link to={`/app/content-bucket?content_item=${task.linked_content_item.id}`}
                       className="text-xs text-primary underline shrink-0"
                       data-testid="task-detail-linked-content-open">
                  Open →
                </Link>
              </div>
            </div>
          )}

          </TaskSection>

          <TaskSection id="meta" title="Tags & Dependencies" icon={Link2} defaultOpen={false} testid="task-section-meta">
          <div>
            <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5"><TagIcon className="w-3 h-3 shrink-0" />Tags</div>
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {(task.tags || []).map((tg) => (
                <span key={tg} className="chip chip-pending text-[10px] inline-flex items-center gap-1" data-testid={`task-detail-tag-${tg}`}>
                  {tg}
                  <button onClick={() => removeTag(tg)} className="hover:text-red-500" data-testid={`task-detail-tag-remove-${tg}`}>×</button>
                </span>
              ))}
              <Input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag…"
                className="h-7 w-32 text-xs"
                data-testid="task-detail-tag-input"
              />
            </div>
          </div>

          <TaskDependenciesEditor task={task} allTasks={allTasks} onPatch={onPatch} />

          {task.review_item_id && (
            <Link to={`/app/review-items/${task.review_item_id}`} className="text-xs text-primary underline" data-testid="task-detail-open-review">
              Open source review item →
            </Link>
          )}

          {/* Iter 207 · Audit strip */}
          <div className="border-t border-border pt-3 mt-3 grid grid-cols-2 gap-2 text-[10px] text-muted-foreground" data-testid="task-detail-audit">
            <div>
              <span className="uppercase tracking-widest text-[9px] font-semibold">Created</span>
              <div className="text-foreground text-[11px]">
                {task.created_by_name || task.created_by || "—"}
                {task.created_at && (
                  <span className="text-muted-foreground"> · {String(task.created_at).slice(0, 16).replace("T", " ")}</span>
                )}
              </div>
            </div>
            <div>
              <span className="uppercase tracking-widest text-[9px] font-semibold">Updated</span>
              <div className="text-foreground text-[11px]">
                {task.updated_by_name || "—"}
                {task.updated_at && (
                  <span className="text-muted-foreground"> · {String(task.updated_at).slice(0, 16).replace("T", " ")}</span>
                )}
              </div>
            </div>
          </div>
          </TaskSection>
          </div>

          {/* RIGHT — comment thread */}
          <div className={`space-y-3 lg:overflow-y-auto min-w-0 pr-1 pb-16 lg:pb-0 ${mobilePanel === "fields" ? "hidden lg:block" : "block"}`} data-testid="task-detail-thread-col">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-2 flex-wrap">
              <MessageSquare className="w-3 h-3 shrink-0" />Thread · {thread.length}
              <button type="button" onClick={() => setMobilePanel(mobilePanel === "thread" ? "both" : "fields")} className="lg:hidden text-[10px] text-primary hover:underline font-normal normal-case tracking-normal" data-testid="task-detail-mobile-hide-thread">
                {mobilePanel === "thread" ? "Show details" : "Show details only"}
              </button>
              <label className="ml-auto inline-flex items-center gap-1 text-[10px] normal-case tracking-normal font-normal text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeClient}
                  onChange={(e) => setIncludeClient(e.target.checked)}
                  data-testid="task-detail-show-client"
                />
                Show client-visible
              </label>
            </div>
            <ul className="space-y-3 mb-3" data-testid="task-detail-thread">
              {thread.map((c) => {
                // R158j · Feb 2026 — System rows now mirror the manual
                // comment layout (avatar → header row → body) so the
                // feed reads consistently. A dedicated "SYSTEM" chip and
                // slightly muted styling keep them distinguishable.
                const isSystem = c.kind === "system" || c.visibility === "system";
                if (isSystem) {
                  const initials = (c.author_name || "SY").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                  return (
                    <li
                      key={c.id}
                      className="flex gap-2.5 border-l-2 border-l-primary/40 bg-muted/30 border-y border-r border-border/60 px-3 py-2"
                      data-testid={`task-comment-system-${c.id}`}
                    >
                      <div
                        className="w-7 h-7 shrink-0 rounded-full text-[10px] font-semibold flex items-center justify-center bg-primary/10 text-primary/80"
                        title="System"
                      >
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[11px] mb-1">
                          <span className="font-semibold text-foreground/85 truncate">{c.author_name || "System"}</span>
                          <span className="text-muted-foreground whitespace-nowrap" title={c.created_at}>
                            {fmtAbsRelative(c.created_at)}
                          </span>
                          <span
                            className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 border bg-primary/10 text-primary/80 border-primary/30"
                          >
                            system
                          </span>
                        </div>
                        {/* Iter · Feb 2026 — Render system messages
                            with friendly chips for status/priority
                            transitions and prettified ISO datetimes
                            (e.g. "2026-08-17T11:57:54.891000+00:00" →
                            "17-Aug-26 05:27:54 PM"). Falls back to the
                            raw text for anything that doesn't parse. */}
                        <div className="text-foreground/80">
                          {formatSystemActivity(c.body_text || c.body)}
                        </div>
                      </div>
                    </li>
                  );
                }
                // R152 · Feb 2026 — Redesigned comment card:
                //   • Avatar circle on the left so scanning by person is fast
                //   • Author + timestamp on ONE compact header row
                //   • Body has more line-height & padding so it reads well
                //   • Client-visible comments carry a coloured left rail +
                //     tinted background instead of a full border swap
                const initials = (c.author_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                const isClient = c.visibility === "client";
                return (
                  <li
                    key={c.id}
                    className={`flex gap-2.5 border-l-2 px-3 py-2 ${isClient
                      ? "border-l-primary bg-primary/[0.04] border-y border-r border-primary/20"
                      : "border-l-border/60 bg-background border-y border-r border-border"}`}
                    data-testid={`task-comment-${c.id}`}
                  >
                    <div
                      className={`w-7 h-7 shrink-0 rounded-full text-[10px] font-semibold flex items-center justify-center ${isClient ? "bg-primary/15 text-primary" : "bg-muted text-foreground/70"}`}
                      title={c.author_name}
                      data-testid={`task-comment-avatar-${c.id}`}
                    >
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] mb-1">
                        <span className="font-semibold text-foreground truncate">{c.author_name}</span>
                        <span className="text-muted-foreground whitespace-nowrap" title={c.created_at}>
                          {fmtAbsRelative(c.created_at)}
                        </span>
                        <span
                          className={`ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${isClient ? "bg-primary/10 text-primary border-primary/30" : "bg-muted text-muted-foreground border-border"}`}
                          data-testid={`task-comment-vis-${c.id}`}
                        >
                          {isClient ? "client-visible" : "internal"}
                        </span>
                      </div>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">{c.body}</div>
                    </div>
                  </li>
                );
              })}
              {thread.length === 0 && <li className="text-xs text-muted-foreground italic p-4 text-center border border-dashed border-border">No comments yet. Start the conversation.</li>}
            </ul>
            <form onSubmit={send} className="space-y-2 lg:sticky lg:bottom-0 bg-background pt-2 border-t border-border">
              <div className="inline-flex border border-border" data-testid="task-detail-visibility-toggle">
                <button
                  type="button"
                  onClick={() => setVisibility("internal")}
                  className={`px-2 py-1 text-[10px] font-semibold border-r border-border ${visibility === "internal" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                  data-testid="task-detail-vis-internal"
                >
                  🔒 Internal only
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility("client")}
                  className={`px-2 py-1 text-[10px] font-semibold ${visibility === "client" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  data-testid="task-detail-vis-client"
                >
                  👤 Visible to client
                </button>
              </div>
              <div className="flex gap-2 relative">
                <MentionAwareTextarea
                  value={body}
                  onChange={setBody}
                  members={members}
                  task={task}
                  visibility={visibility}
                />
                <Button type="submit" disabled={busy || !body.trim()} className="rounded-none h-auto" data-testid="task-detail-comment-submit">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            </form>
          </div>
          </div>
        </div>
        <DialogFooter className="hidden lg:flex">
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="task-detail-close">Close</Button>
        </DialogFooter>
        {/* Iter · Feb 2026 — Scroll-to-top FAB. Appears once the user
            has scrolled ~400px inside the dialog on phones (the whole
            content is one scroller there). Hidden on desktop where the
            inner columns own their own scroll. */}
        {showScrollTop && (
          <button
            type="button"
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-30 w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-transform"
            title="Scroll to top"
            aria-label="Scroll to top of task detail"
            data-testid="task-detail-scroll-top"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function shortISO(s) {
  if (!s) return "";
  try { return new Date(s).toISOString().slice(0, 10); } catch { return ""; }
}

// R148k · Feb 2026 — Collapsible section wrapper for the Task Edit popup.
// Every top-level block in the left column gets wrapped so the whole
// popup becomes navigable at a glance. Collapse state persists per user
// keyed by section id so re-opening the same task remembers your view.
// R152 · Feb 2026 — When the parent broadcasts a global expand/collapse
// via a CustomEvent, each TaskSection listens and flips accordingly.
// LocalStorage state is preserved so per-section preferences survive
// dialog close/reopen.
function TaskSection({ id, title, icon: Icon, defaultOpen = true, testid, children }) {
  const LS = `acq.task-edit-section.${id}`;
  const sectionRef = useRef(null);
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(LS);
      if (raw === "0") return false;
      if (raw === "1") return true;
    } catch { /* noop */ }
    return defaultOpen;
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(LS, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  useEffect(() => {
    const onBroadcast = (e) => {
      const wanted = !!e?.detail?.open;
      setOpen(wanted);
      try { localStorage.setItem(LS, wanted ? "1" : "0"); } catch { /* noop */ }
    };
    // Iter · Feb 2026 — Scroll-to & focus event from the key-facts row.
    // Detail: { targetId, focusSelector? }. Opens the section, scrolls
    // it into view smoothly, then focuses the requested field.
    const onScrollTo = (e) => {
      if (e?.detail?.targetId !== id) return;
      setOpen(true);
      try { localStorage.setItem(LS, "1"); } catch { /* noop */ }
      // Wait a tick so the body renders before scrolling.
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        const sel = e?.detail?.focusSelector;
        if (sel) {
          const el = sectionRef.current?.querySelector(sel);
          if (el) {
            el.focus?.();
            if (el.select) el.select();
          }
        }
      }, 60);
    };
    window.addEventListener("acq:task-section:toggle-all", onBroadcast);
    window.addEventListener("acq:task-section:scroll-to", onScrollTo);
    return () => {
      window.removeEventListener("acq:task-section:toggle-all", onBroadcast);
      window.removeEventListener("acq:task-section:scroll-to", onScrollTo);
    };
  }, [LS, id]);
  return (
    <section ref={sectionRef} className="bg-background scroll-mt-[80px]" data-testid={testid || `task-section-${id}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-2 px-2 py-2 border-l-4 border-primary bg-primary/5 hover:bg-primary/10 transition-colors text-left"
        data-testid={`${testid || `task-section-${id}`}-toggle`}
      >
        <span className="text-primary text-xs w-3">{open ? "▾" : "▸"}</span>
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <span className="text-[13px] uppercase tracking-wider font-bold text-foreground">{title}</span>
      </button>
      {open && (
        <div className="p-3 space-y-3" data-testid={`${testid || `task-section-${id}`}-body`}>
          {children}
        </div>
      )}
    </section>
  );
}

/** Iter 253w · Convert an ISO datetime OR date string to the format that
 * <input type="datetime-local"> expects (YYYY-MM-DDTHH:mm). Empty string
 * clears the input, matching how the shadcn Input handles reset. */
function toLocalDatetime(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return "";
    // strip timezone → local wall-clock
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60_000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}

/** Iter 253w · Category autocomplete — combines a dropdown of existing
 * workspace categories with an inline "+ Add" that POSTs a new row and
 * assigns it in one click. Admin dedupe is enforced server-side. */
function TaskCategoryPicker({ task, onPatch }) {
  const [rows, setRows] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const wsId = task.workspace_id;
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/task-categories`);
      setRows(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const matches = query.trim()
    ? rows.filter((r) => r.name.toLowerCase().includes(query.trim().toLowerCase()))
    : rows;
  const exact = rows.find((r) => r.name.toLowerCase() === query.trim().toLowerCase());
  const canCreate = query.trim() && !exact;

  const assign = async (cid, cname) => {
    setOpen(false);
    setQuery("");
    if (cid !== task.task_category_id) {
      onPatch({ task_category_id: cid });
    }
  };
  const createAndAssign = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/task-categories`,
                                    { name: query.trim() });
      setRows((prev) => prev.some((x) => x.id === r.data.id) ? prev : [...prev, r.data]);
      await assign(r.data.id, r.data.name);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Add category failed"));
    } finally { setSaving(false); }
  };

  const selected = rows.find((r) => r.id === task.task_category_id);

  return (
    <div className="relative" data-testid="task-detail-category-picker">
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              className="w-full h-8 border border-input bg-background px-2 text-xs text-left flex items-center justify-between"
              data-testid="task-detail-category-trigger">
        <span className={selected ? "" : "text-muted-foreground"}>
          {selected?.name || task.task_category_name || "— none —"}
        </span>
        <span className="text-muted-foreground">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-border bg-background shadow-md max-h-56 overflow-auto"
             data-testid="task-detail-category-popover">
          <input type="text" value={query}
                 onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search or add new…"
                 className="w-full h-8 px-2 text-xs border-b border-border bg-transparent outline-none"
                 autoFocus data-testid="task-detail-category-input" />
          {task.task_category_id && (
            <button type="button"
                    onClick={() => { onPatch({ task_category_id: null }); setOpen(false); setQuery(""); }}
                    className="w-full text-left text-xs px-2 py-1.5 hover:bg-muted text-rose-600"
                    data-testid="task-detail-category-clear">Clear</button>
          )}
          {matches.map((r) => (
            <button key={r.id} type="button"
                    onClick={() => assign(r.id, r.name)}
                    className={`w-full text-left text-xs px-2 py-1.5 hover:bg-muted ${r.id === task.task_category_id ? "bg-muted font-semibold" : ""}`}
                    data-testid={`task-detail-category-opt-${r.id}`}>
              {r.name}
            </button>
          ))}
          {canCreate && (
            <button type="button" onClick={createAndAssign} disabled={saving}
                    className="w-full text-left text-xs px-2 py-1.5 hover:bg-primary/10 border-t border-border text-primary font-medium"
                    data-testid="task-detail-category-add">
              {saving ? "Adding…" : `+ Add "${query.trim()}"`}
            </button>
          )}
          {matches.length === 0 && !canCreate && (
            <div className="text-[11px] text-muted-foreground px-2 py-2 italic">No categories yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

/** Iter 253w · Inline time-entry widget for the task modal. Persists via
 * `/tasks/{tid}/time-entries`, refreshes the last-25 list, and updates
 * `logged_hours` on the task in the same round-trip.
 * Iter 253z-2 · Adds start/end time inputs; respects the workspace's
 * `timesheet.capture_mode` setting (hours_only | start_end | start_end_optional). */
function TaskTimeEntryInline({ task, onLogged }) {
  const { activeWorkspaceId } = useAuth();
  const [entries, setEntries] = React.useState([]);
  const [hours, setHours] = React.useState("");
  const [entryDate, setEntryDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = React.useState("");
  const [endTime, setEndTime] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [captureMode, setCaptureMode] = React.useState("start_end_optional");
  const load = async () => {
    try {
      const r = await api.get(`/tasks/${task.id}/time-entries`);
      setEntries(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id]);
  React.useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const r = await api.get(`/workspaces/${activeWorkspaceId}/settings/timesheet`);
        setCaptureMode(r.data?.capture_mode || "start_end_optional");
      } catch { setCaptureMode("start_end_optional"); }
    })();
  }, [activeWorkspaceId]);
  // Auto-compute hours when both start & end are picked.
  React.useEffect(() => {
    if (startTime && endTime) {
      const s = new Date(`${entryDate}T${startTime}`);
      const e = new Date(`${entryDate}T${endTime}`);
      if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e > s) {
        const h = ((e - s) / 3600000).toFixed(2);
        setHours(h);
      }
    }
  }, [startTime, endTime, entryDate]);
  const needsRange = captureMode === "start_end";
  const submit = async () => {
    if (!hours || Number(hours) <= 0) { toast.error("Enter hours > 0"); return; }
    if (needsRange && (!startTime || !endTime)) {
      toast.error("Start & end time are required by this workspace");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        entry_date: entryDate, hours: Number(hours), description,
      };
      if (startTime && endTime) {
        payload.start_time = new Date(`${entryDate}T${startTime}`).toISOString();
        payload.end_time = new Date(`${entryDate}T${endTime}`).toISOString();
      }
      await api.post(`/tasks/${task.id}/time-entries`, payload);
      toast.success(`Logged ${hours}h`);
      setHours(""); setDescription(""); setStartTime(""); setEndTime("");
      await load();
      onLogged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Log time failed"));
    } finally { setSaving(false); }
  };
  const totalLogged = entries.reduce((a, e) => a + (Number(e.hours) || 0), 0);
  const showRange = captureMode === "start_end" || captureMode === "start_end_optional";
  return (
    <div className="p-3 space-y-2 bg-muted/10" data-testid="task-time-entry">
      <div className="flex items-center gap-2">
        <span className="label-eyebrow">Log time</span>
        {needsRange && <span className="text-[9px] uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5">start & end required</span>}
        <span className="text-[10px] text-muted-foreground ml-auto">
          {entries.length} entrie{entries.length === 1 ? "" : "s"} · {totalLogged.toFixed(2)}h total
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[100px,1fr] gap-2 items-end">
        <Input type="date" value={entryDate}
                 onChange={(e) => setEntryDate(e.target.value)}
                 className="h-8 text-xs" data-testid="task-time-entry-date" />
        <Input type="text" value={description}
                 onChange={(e) => setDescription(e.target.value)}
                 placeholder="What did you work on?" className="h-8 text-xs"
                 data-testid="task-time-entry-desc" />
      </div>
      <div className={`grid ${showRange ? "grid-cols-1 sm:grid-cols-[100px,100px,80px,auto]" : "grid-cols-1 sm:grid-cols-[100px,auto]"} gap-2 items-end`}>
        {showRange && (
          <>
            <div>
              <div className="text-[9px] uppercase text-muted-foreground tracking-widest mb-0.5">Start</div>
              <Input type="time" value={startTime}
                       onChange={(e) => setStartTime(e.target.value)}
                       className="h-8 text-xs" data-testid="task-time-entry-start"
                       required={needsRange} />
            </div>
            <div>
              <div className="text-[9px] uppercase text-muted-foreground tracking-widest mb-0.5">End</div>
              <Input type="time" value={endTime}
                       onChange={(e) => setEndTime(e.target.value)}
                       className="h-8 text-xs" data-testid="task-time-entry-end"
                       required={needsRange} />
            </div>
          </>
        )}
        <div>
          <div className="text-[9px] uppercase text-muted-foreground tracking-widest mb-0.5">Hours</div>
          <Input type="number" min="0.25" step="0.25" value={hours}
                   onChange={(e) => setHours(e.target.value)}
                   placeholder="hrs" className="h-8 text-xs"
                   data-testid="task-time-entry-hours"
                   disabled={needsRange && !!(startTime && endTime)}
                   title={needsRange && startTime && endTime ? "Auto-computed from start & end" : ""} />
        </div>
        <Button size="sm" className="h-8 rounded-none w-full sm:w-auto self-end" onClick={submit}
                  disabled={saving}
                  data-testid="task-time-entry-submit">
          {saving ? "…" : "Log"}
        </Button>
      </div>
      {entries.length > 0 && (
        <div className="mt-1 max-h-24 overflow-auto text-[11px] divide-y divide-border border border-border">
          {entries.slice(0, 8).map((e) => (
            <div key={e.id} className="px-2 py-1 flex items-center gap-2 hover:bg-muted/40"
                   data-testid={`task-time-entry-row-${e.id}`}>
              <span className="font-mono text-muted-foreground w-20">{e.entry_date}</span>
              {e.start_time && e.end_time ? (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {new Date(e.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–{new Date(e.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              ) : null}
              <span className="font-semibold w-12">{e.hours}h</span>
              <span className="flex-1 truncate">{e.description || <span className="text-muted-foreground italic">no note</span>}</span>
              <span className="text-muted-foreground truncate max-w-[80px]">{e.user_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// R68 · Local-buffered description editor with explicit Save button.
// Users were losing work when they closed the dialog while the previous
// auto-save-on-every-keystroke was still batching — this variant keeps
// the draft locally and only PATCHes on Save (or Ctrl+S).
function TaskDescriptionEditor({ task, onPatch }) {
  const [draft, setDraft] = useState(task.description || "");
  const [savedRef, setSavedRef] = useState(task.description || "");
  const [busy, setBusy] = useState(false);

  // Sync when the task changes (open a different task, or server-side
  // patch by someone else).
  useEffect(() => {
    setDraft(task.description || "");
    setSavedRef(task.description || "");
    // eslint-disable-next-line
  }, [task.id]);

  const dirty = draft !== savedRef;
  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      await onPatch({ description: draft });
      setSavedRef(draft);
    } catch {
      // R268zzzz · Feb 2026 — onPatch now re-throws on backend failure.
      // Keep the draft `dirty` so the user sees "unsaved" and doesn't
      // lose their work. Error toast has already been shown by patchTask.
    }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="label-eyebrow mb-1 flex items-center gap-2">
        Description
        {dirty && <span className="text-[10px] text-amber-600 normal-case tracking-normal">· unsaved</span>}
        <span className="ml-auto normal-case tracking-normal text-[10px] text-muted-foreground font-normal">rich text · Ctrl+S to save</span>
      </div>
      <div
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
            e.preventDefault(); save();
          }
        }}
      >
        <SmartRichText
          value={draft}
          onChange={setDraft}
          minRows={4}
          maxHeight={240}
          mediaMode="url-only"
          placeholder="Describe the task with headings, lists, links, code…"
          testid="task-detail-description"
        />
      </div>
      <div className="mt-1 flex items-center gap-2 justify-end">
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(savedRef)}
            className="text-[11px] text-muted-foreground hover:text-foreground underline"
            data-testid="task-detail-description-discard"
          >
            Discard
          </button>
        )}
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || busy}
          className="rounded-none h-7"
          data-testid="task-detail-description-save"
        >
          {busy ? "Saving…" : dirty ? "Save description" : "Saved"}
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────── @MENTION TEXTAREA (R137 · Phase 2) ───────────────────────
// Comment box for the Task Detail drawer with an inline "@" autocomplete
// popover. Task collaborators + primary assignee bubble to the top of
// suggestions so replies stay within the huddle actually working on the
// task; the rest of the workspace shows underneath for wider mentions.
function MentionAwareTextarea({ value, onChange, members, task, visibility }) {
  const taRef = React.useRef(null);
  const [openMention, setOpenMention] = React.useState(false);
  const [mentionQuery, setMentionQuery] = React.useState("");
  const [mentionStart, setMentionStart] = React.useState(-1);
  const [highlight, setHighlight] = React.useState(0);

  const priorityIds = React.useMemo(() => {
    const ids = new Set();
    if (task?.assignee_id) ids.add(task.assignee_id);
    for (const c of (task?.collaborators || [])) {
      if (c?.user_id) ids.add(c.user_id);
    }
    return ids;
  }, [task?.assignee_id, JSON.stringify(task?.collaborators || [])]);

  const allOptions = React.useMemo(() => {
    const out = [];
    for (const m of (members || [])) {
      const uid = m.user_id || m.id;
      if (!uid) continue;
      out.push({
        id: uid,
        name: m.user?.name || m.user?.email || m.name || m.email || uid.slice(0, 6),
        email: m.user?.email || m.email || "",
        priority: priorityIds.has(uid),
      });
    }
    return out;
  }, [members, priorityIds]);

  const filtered = React.useMemo(() => {
    const q = (mentionQuery || "").toLowerCase();
    const pool = allOptions.filter((o) =>
      !q || (o.name || "").toLowerCase().includes(q)
         || (o.email || "").toLowerCase().includes(q));
    pool.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    return pool.slice(0, 8);
  }, [allOptions, mentionQuery]);

  const detectMention = (raw, caret) => {
    const upto = raw.slice(0, caret);
    const m = upto.match(/(^|\s)@([\w.-]{0,30})$/);
    if (m) {
      setMentionStart(caret - m[2].length);
      setMentionQuery(m[2]);
      setOpenMention(true);
      setHighlight(0);
    } else {
      setOpenMention(false);
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    onChange(raw);
    detectMention(raw, e.target.selectionStart || raw.length);
  };

  const applyMention = (opt) => {
    if (!opt || mentionStart < 0) return;
    const ta = taRef.current;
    const caret = ta ? (ta.selectionStart || value.length) : value.length;
    const before = value.slice(0, mentionStart);
    const after = value.slice(caret);
    const inserted = `@${opt.name} `;
    const next = `${before}${inserted}${after}`;
    onChange(next);
    setOpenMention(false);
    // Restore caret after the inserted mention.
    setTimeout(() => {
      if (ta) {
        const pos = (before + inserted).length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!openMention) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" || e.key === "Tab") {
      if (filtered[highlight]) { e.preventDefault(); applyMention(filtered[highlight]); }
    } else if (e.key === "Escape") {
      e.preventDefault(); setOpenMention(false);
    }
  };

  return (
    <div className="relative flex-1" data-testid="task-detail-comment-wrap">
      <Textarea
        ref={taRef}
        rows={2}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={visibility === "client" ? "Message visible to the client…  ( @ to mention )" : "Internal note (team only)…  ( @ to mention )"}
        data-testid="task-detail-comment-input"
      />
      {openMention && filtered.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 z-50 w-64 border border-border bg-popover shadow-lg"
             data-testid="task-detail-mention-popover">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground px-2 py-1 border-b border-border bg-muted/40">
            Mention teammate
          </div>
          <ul>
            {filtered.map((o, i) => (
              <li key={o.id}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); applyMention(o); }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left px-2 py-1 text-xs flex items-center gap-2 ${i === highlight ? "bg-primary/10" : "hover:bg-muted/40"}`}
                  data-testid={`task-detail-mention-opt-${o.id}`}
                >
                  <span className="flex-1 truncate">
                    <span className="font-medium">{o.name}</span>
                    {o.email ? <span className="ml-1 text-[10px] text-muted-foreground">{o.email}</span> : null}
                  </span>
                  {o.priority && (
                    <span className="text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1"
                          data-testid={`task-detail-mention-priority-${o.id}`}>
                      on task
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}



// A compact list of sub-tasks under any parent. Each row has its own
// assignee, deadline, and status; the parent's roll-up (done/total) is
// shown at the top. Timesheet logging works through the shared TaskDetail
// drawer that opens when a subtask row is clicked.
function TaskSubtasksPanel({ task, members }) {
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [openSub, setOpenSub] = useState(null);
  const [addAssignee, setAddAssignee] = useState("");
  const [addDue, setAddDue] = useState("");
  const [view, setView] = useState("list"); // "list" | "kanban"
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dragOverCol, setDragOverCol] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/tasks/${task.id}/subtasks`);
      setSubs(Array.isArray(data) ? data : []);
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id]);

  const memberName = (uid) => {
    if (!uid) return "";
    const m = (members || []).find((mm) => (mm.user_id || mm.id) === uid);
    return m?.user?.name || m?.user?.email || m?.name || m?.email || uid.slice(0, 6);
  };

  const addSubtask = async () => {
    const t = (title || "").trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/tasks/${task.id}/subtasks`, {
        title: t,
        assignee_id: addAssignee || null,
        due_date: addDue || null,
      });
      setSubs((prev) => [...prev, data]);
      setTitle(""); setAddAssignee(""); setAddDue("");
      toast.success("Subtask added");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add subtask"));
    }
  };

  // R137b · Bulk paste. Splits on newlines, strips common list bullets
  // ("1.", "1)", "-", "*", "•") + optional leading whitespace, and creates
  // one subtask per non-empty line via serial POSTs (keeps UI feedback
  // snappy and lets a single failure surface without wiping the batch).
  const bulkAdd = async () => {
    const lines = (bulkText || "")
      .split(/\r?\n/)
      .map((ln) => ln.replace(/^\s*(?:\d+[.)]|[-*•])\s+/, "").trim())
      .filter(Boolean);
    if (lines.length === 0) {
      toast.error("Paste at least one subtask title.");
      return;
    }
    setBulkBusy(true);
    const created = [];
    let failed = 0;
    for (const t of lines) {
      try {
        const { data } = await api.post(`/tasks/${task.id}/subtasks`, { title: t });
        created.push(data);
      } catch { failed += 1; }
    }
    setSubs((prev) => [...prev, ...created]);
    setBulkBusy(false);
    setBulkText("");
    setBulkOpen(false);
    toast.success(`Added ${created.length} subtask${created.length === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
  };

  const patchSub = async (sid, patch) => {
    // Optimistic UI first — makes drag-drop feel instant on Kanban.
    setSubs((prev) => prev.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
    try {
      const { data } = await api.patch(`/tasks/${sid}`, patch);
      setSubs((prev) => prev.map((s) => (s.id === sid ? { ...s, ...patch, ...data } : s)));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't update"));
      load();
    }
  };

  const deleteSub = async (sid) => {
    if (!window.confirm("Delete this subtask?")) return;
    try {
      await api.delete(`/tasks/${sid}`);
      setSubs((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't delete")); }
  };

  const done = subs.filter((s) => s.status === "done").length;
  const total = subs.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const rolledHours = Number(task.timesheet_hours ?? 0);
  const subHours = Number(task.subtask_timesheet_hours ?? 0);

  const KANBAN_COLS = [
    { key: "open", label: "Open", tint: "border-slate-300 bg-slate-50/50" },
    { key: "in_progress", label: "In progress", tint: "border-amber-300 bg-amber-50/40" },
    { key: "done", label: "Done", tint: "border-emerald-300 bg-emerald-50/40" },
  ];

  return (
    <div data-testid="task-detail-subtasks">
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="label-eyebrow">Subtasks</span>
        {total > 0 && (
          <span className="text-[10px] text-muted-foreground normal-case tracking-normal font-normal"
                data-testid="task-detail-subtasks-rollup">
            · {done}/{total} done · {pct}%
          </span>
        )}
        {subHours > 0 && (
          <span className="text-[10px] text-muted-foreground normal-case tracking-normal font-normal"
                data-testid="task-detail-subtasks-hours"
                title="Timesheet hours logged directly on subtasks">
            · {subHours}h from subtasks / {rolledHours}h total
          </span>
        )}
        <div className="ml-auto inline-flex border border-border">
          <button type="button"
                  onClick={() => setView("list")}
                  className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest ${view === "list" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                  data-testid="subtask-view-list">List</button>
          <button type="button"
                  onClick={() => setView("kanban")}
                  className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest border-l border-border ${view === "kanban" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                  data-testid="subtask-view-kanban">Kanban</button>
        </div>
        <button type="button"
                onClick={() => setBulkOpen(true)}
                className="text-[10px] underline text-muted-foreground hover:text-foreground"
                data-testid="subtask-bulk-open">
          Bulk add
        </button>
      </div>
      {total > 0 && (
        <div className="h-1 bg-muted mb-2 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all"
               style={{ width: `${pct}%` }}
               data-testid="task-detail-subtasks-progress" />
        </div>
      )}

      {view === "list" ? (
        <div className="space-y-1 mb-2">
          {busy && subs.length === 0 && (
            <div className="text-[11px] text-muted-foreground italic">Loading…</div>
          )}
          {!busy && subs.length === 0 && (
            <div className="text-[11px] text-muted-foreground italic"
                 data-testid="task-detail-subtasks-empty">
              No subtasks yet. Break work into smaller actionable pieces below.
            </div>
          )}
          {subs.map((s) => (
            <div key={s.id}
                 className="flex items-center gap-2 border border-border px-2 py-1 bg-background hover:bg-muted/30 flex-wrap sm:flex-nowrap"
                 data-testid={`subtask-row-${s.id}`}>
              <input type="checkbox"
                     checked={s.status === "done"}
                     onChange={(e) => patchSub(s.id, { status: e.target.checked ? "done" : "open" })}
                     className="h-3.5 w-3.5 accent-emerald-600 cursor-pointer shrink-0"
                     data-testid={`subtask-check-${s.id}`} />
              <button type="button"
                      onClick={() => setOpenSub(s)}
                      className={`text-xs text-left flex-1 min-w-0 basis-full sm:basis-auto truncate ${s.status === "done" ? "line-through text-muted-foreground" : ""}`}
                      data-testid={`subtask-title-${s.id}`}>
                {s.title}
              </button>
              {/* R268aaaa · Feb 2026 — Assignee is now a proper editable
                  dropdown (was a static <span>) and shares row width
                  with the date field via a flexible basis. Both keep a
                  `min-w-0` so text can truncate instead of overflow-
                  crashing into the due-date input. */}
              <div className="w-[130px] shrink-0 min-w-0" data-testid={`subtask-assignee-wrap-${s.id}`}>
                <SearchableSelect
                  value={s.assignee_id || ""}
                  onValueChange={(v) => patchSub(s.id, { assignee_id: v || null })}
                  options={[{ value: "", label: "Unassigned" }, ...((members || [])
                    .map((m) => {
                      const uid = m.user_id || m.id;
                      const name = m.user?.name || m.user?.email || m.name || m.email || (uid ? uid.slice(0, 6) : "");
                      return uid ? { value: uid, label: name } : null;
                    })
                    .filter(Boolean))]}
                  placeholder="Unassigned"
                  clearable
                  triggerClassName="rounded-none h-6 text-[10px]"
                  data-testid={`subtask-assignee-${s.id}`}
                />
              </div>
              <input type="date"
                     value={s.due_date ? s.due_date.slice(0, 10) : ""}
                     onChange={(e) => patchSub(s.id, { due_date: e.target.value || null })}
                     className="h-6 text-[10px] w-[120px] shrink-0 border border-border bg-transparent px-1"
                     data-testid={`subtask-due-${s.id}`} />
              <button type="button"
                      onClick={() => deleteSub(s.id)}
                      className="text-rose-600 hover:text-rose-800 text-[13px] leading-none px-1 shrink-0"
                      data-testid={`subtask-delete-${s.id}`}
                      title="Delete">×</button>
            </div>
          ))}
        </div>
      ) : (
        // R137b · Kanban view — 3 columns, HTML5 drag & drop between them.
        <div className="grid grid-cols-3 gap-2 mb-2" data-testid="subtask-kanban">
          {KANBAN_COLS.map((col) => {
            const rows = subs.filter((s) => (s.status || "open") === col.key
              || (col.key === "open" && !["in_progress", "done"].includes(s.status)));
            return (
              <div key={col.key}
                   onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
                   onDragLeave={() => setDragOverCol(null)}
                   onDrop={(e) => {
                     e.preventDefault();
                     const sid = e.dataTransfer.getData("text/subtask-id");
                     setDragOverCol(null);
                     if (sid) {
                       const cur = subs.find((s) => s.id === sid);
                       if (cur && (cur.status || "open") !== col.key) {
                         patchSub(sid, { status: col.key });
                       }
                     }
                   }}
                   className={`border ${col.tint} p-1 min-h-[80px] ${dragOverCol === col.key ? "ring-2 ring-primary" : ""}`}
                   data-testid={`subtask-kanban-col-${col.key}`}>
                <div className="flex items-center justify-between text-[9px] uppercase tracking-widest font-semibold text-muted-foreground mb-1 px-1">
                  <span>{col.label}</span>
                  <span data-testid={`subtask-kanban-count-${col.key}`}>{rows.length}</span>
                </div>
                <div className="space-y-1">
                  {rows.map((s) => (
                    <div key={s.id}
                         draggable
                         onDragStart={(e) => { e.dataTransfer.setData("text/subtask-id", s.id); }}
                         className="bg-background border border-border px-1.5 py-1 text-[11px] cursor-grab active:cursor-grabbing hover:bg-muted/30"
                         data-testid={`subtask-kanban-card-${s.id}`}>
                      <button type="button"
                              onClick={() => setOpenSub(s)}
                              className={`text-left w-full truncate ${col.key === "done" ? "line-through text-muted-foreground" : ""}`}
                              data-testid={`subtask-kanban-title-${s.id}`}>
                        {s.title}
                      </button>
                      <div className="flex items-center gap-1 mt-0.5 text-[9px] text-muted-foreground">
                        <span className="truncate flex-1">{s.assignee_id ? memberName(s.assignee_id) : "—"}</span>
                        {s.due_date && <span>{s.due_date.slice(0, 10)}</span>}
                      </div>
                    </div>
                  ))}
                  {rows.length === 0 && (
                    <div className="text-[10px] text-muted-foreground italic px-1 py-2 text-center">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Iter Feb 2026 · Two-row layout so the clearable chip on the
          Assignee select never overlaps the Due date input. Title owns
          the full first row (common case: user types + Enter). The
          second row is a flex-wrap bar of Assignee → Due → Add so the
          Add button and Due field stay visible at any modal width. */}
      <div className="border border-dashed border-border p-2 bg-muted/20 space-y-2">
        <Input value={title}
               onChange={(e) => setTitle(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubtask(); } }}
               placeholder="Add a subtask…"
               className="h-8 text-xs w-full"
               data-testid="subtask-new-title" />
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[160px] flex-1 sm:flex-none sm:w-[200px]" data-testid="subtask-new-assignee-wrap">
            <SearchableSelect
              value={addAssignee}
              onValueChange={(v) => setAddAssignee(v || "")}
              options={[{ value: "", label: "Unassigned" }, ...((members || [])
                .map((m) => {
                  const uid = m.user_id || m.id;
                  const name = m.user?.name || m.user?.email || m.name || m.email || (uid ? uid.slice(0, 6) : "");
                  return uid ? { value: uid, label: name } : null;
                })
                .filter(Boolean))]}
              placeholder="Unassigned"
              clearable
              triggerClassName="rounded-none h-8 text-[11px] w-full"
              data-testid="subtask-new-assignee"
            />
          </div>
          <input type="date"
                 value={addDue}
                 onChange={(e) => setAddDue(e.target.value)}
                 className="h-8 text-[11px] border border-border bg-transparent px-2 w-[150px]"
                 data-testid="subtask-new-due" />
          <Button size="sm"
                  onClick={addSubtask}
                  disabled={!(title || "").trim()}
                  className="rounded-none h-8 text-[11px] px-3 ml-auto"
                  data-testid="subtask-add-btn">
            Add
          </Button>
        </div>
      </div>

      {bulkOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setBulkOpen(false); }}>
          <DialogContent className="max-w-lg" data-testid="subtask-bulk-dialog">
            <DialogHeader>
              <DialogTitle>Bulk add subtasks</DialogTitle>
            </DialogHeader>
            <div className="text-xs text-muted-foreground mb-2">
              Paste a list — one subtask per line. Leading bullets (<code>1.</code>, <code>-</code>, <code>*</code>, <code>•</code>) are stripped automatically.
            </div>
            <Textarea
              rows={9}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={"1. Draft the outline\n2. Get client feedback\n3. Ship v1"}
              className="text-xs font-mono"
              data-testid="subtask-bulk-textarea"
            />
            <DialogFooter>
              <Button variant="outline" className="rounded-none"
                      onClick={() => setBulkOpen(false)}
                      data-testid="subtask-bulk-cancel">Cancel</Button>
              <Button onClick={bulkAdd}
                      disabled={bulkBusy || !bulkText.trim()}
                      className="rounded-none"
                      data-testid="subtask-bulk-submit">
                {bulkBusy ? "Adding…" : "Add subtasks"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {openSub && (
        <TaskDetail
          task={openSub}
          members={members}
          allTasks={subs}
          onClose={() => { setOpenSub(null); load(); }}
          onPatch={async (patch) => {
            const { data } = await api.patch(`/tasks/${openSub.id}`, patch);
            setOpenSub((cur) => (cur ? { ...cur, ...patch, ...data } : cur));
            setSubs((prev) => prev.map((s) => (s.id === openSub.id ? { ...s, ...patch, ...data } : s)));
            return data;
          }}
        />
      )}
    </div>
  );
}


// ────────────────── COLLABORATOR HOURS PANEL (R137) ──────────────────
// Shows every user tied to a task (assignee + collaborators + anyone who
// logged time) with their contributed hours and personal deadline. A
// tiny inline timeline plots each deadline against the task's main due
// date so team leads can spot slippage in one glance.
function CollaboratorHoursPanel({ task }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get(`/tasks/${task.id}/collaborator-hours`);
      setData(r);
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id, task.assignee_id,
    JSON.stringify(task.collaborators || [])]);

  if (busy && !data) return null;
  if (!data || (data.rows || []).length === 0) return null;

  // Build a mini-timeline over all personal deadlines + task due date.
  const dates = [];
  (data.rows || []).forEach((r) => { if (r.personal_due_date) dates.push(r.personal_due_date); });
  if (data.task_due_date) dates.push(data.task_due_date);
  const parsed = dates.map((d) => new Date(d).getTime()).filter((n) => !isNaN(n));
  const minMs = parsed.length ? Math.min(...parsed) : 0;
  const maxMs = parsed.length ? Math.max(...parsed) : 0;
  const span = Math.max(1, maxMs - minMs);
  const pctFor = (d) => {
    if (!d) return null;
    const t = new Date(d).getTime();
    if (isNaN(t)) return null;
    return ((t - minMs) / span) * 100;
  };

  return (
    <div className="p-2 bg-muted/10" data-testid="task-detail-collab-hours">
      <div className="label-eyebrow mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        Hours &amp; timeline
        <span className="text-[10px] text-muted-foreground normal-case tracking-normal font-normal">
          · {data.total_hours || 0}h logged across {data.rows.length} teammate{data.rows.length === 1 ? "" : "s"}
        </span>
      </div>
      {/* R268aaaa · Feb 2026 — Tabular / grid-lines layout so the
          per-teammate rows read like a spreadsheet. Uses the same
          `report-grid` class as the Daily Completion Report so a
          workspace's `data-table-style="minimal"` reset does not
          strip the cell borders. */}
      <table className="w-full text-[11px] border-collapse report-grid" data-testid="collab-hours-table">
        <thead>
          <tr className="bg-muted/40 text-[9px] uppercase tracking-widest text-muted-foreground">
            <th className="text-left px-1.5 py-1 w-4"></th>
            <th className="text-left px-1.5 py-1">Teammate</th>
            <th className="text-right px-1.5 py-1 w-14">Hours</th>
            <th className="text-left px-1.5 py-1 w-16">Role</th>
            <th className="text-left px-1.5 py-1 hidden sm:table-cell">Timeline</th>
            <th className="text-right px-1.5 py-1 w-[90px]">Due</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.user_id} data-testid={`collab-hours-row-${r.user_id}`}>
              <td className="px-1.5 py-1">
                <span className={`inline-block w-1.5 h-1.5 shrink-0 ${r.role === "assignee" ? "bg-emerald-600" : r.role === "collaborator" ? "bg-sky-600" : "bg-muted-foreground"}`}></span>
              </td>
              <td className="px-1.5 py-1 font-medium truncate">{r.user_name}</td>
              <td className="px-1.5 py-1 text-right font-mono"
                    data-testid={`collab-hours-value-${r.user_id}`}>
                {r.hours}h
              </td>
              <td className="px-1.5 py-1 text-[9px] text-muted-foreground uppercase tracking-widest">
                {r.role}
              </td>
              <td className="hidden sm:table-cell px-1.5 py-1">
                <div className="relative h-3 bg-muted/40 min-w-[80px]">
                  {r.personal_due_date && pctFor(r.personal_due_date) != null && (
                    <span className={`absolute top-0 h-3 w-0.5 ${r.role === "assignee" ? "bg-emerald-600" : "bg-sky-600"}`}
                          style={{ left: `${pctFor(r.personal_due_date)}%` }}
                          title={`${r.user_name}: ${r.personal_due_date}`}
                          data-testid={`collab-timeline-marker-${r.user_id}`}></span>
                  )}
                  {data.task_due_date && pctFor(data.task_due_date) != null && (
                    <span className="absolute top-0 h-3 w-px bg-amber-600 opacity-70"
                          style={{ left: `${pctFor(data.task_due_date)}%` }}
                          title={`Task due: ${data.task_due_date}`}></span>
                  )}
                </div>
              </td>
              <td className="px-1.5 py-1 text-right text-[10px] text-muted-foreground">
                {r.personal_due_date ? r.personal_due_date.slice(0, 10) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}




function TaskDependenciesEditor({ task, allTasks, onPatch }) {
  const deps = task.depends_on_task_ids || [];
  const options = (allTasks || [])
    .filter((t) => t.id !== task.id && !deps.includes(t.id))
    .map((t) => ({
      value: t.id,
      label: t.title || "(untitled)",
    }));
  const depTasks = deps
    .map((id) => (allTasks || []).find((t) => t.id === id))
    .filter(Boolean);

  const addDep = (id) => {
    if (!id || deps.includes(id)) return;
    onPatch({ depends_on_task_ids: [...deps, id] });
  };
  const removeDep = (id) => {
    onPatch({ depends_on_task_ids: deps.filter((x) => x !== id) });
  };

  return (
    <div data-testid="task-detail-dependencies">
      <div className="label-eyebrow mb-1 flex items-center gap-1">
        <ListChecks className="w-3 h-3 inline" />Depends on
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {depTasks.length === 0 && (
          <span className="text-[11px] text-muted-foreground italic">No dependencies</span>
        )}
        {depTasks.map((t) => (
          <span
            key={t.id}
            className="chip chip-pending text-[10px] inline-flex items-center gap-1 max-w-[220px]"
            data-testid={`task-dep-chip-${t.id}`}
          >
            <span className="truncate">{t.title}</span>
            <button
              type="button"
              onClick={() => removeDep(t.id)}
              className="hover:text-red-500"
              data-testid={`task-dep-remove-${t.id}`}
              aria-label="Remove dependency"
            >×</button>
          </span>
        ))}
      </div>
      <SearchableSelect
        value=""
        onValueChange={addDep}
        options={options}
        placeholder="+ Add dependency…"
        data-testid="task-dep-picker"
        triggerClassName="w-full h-8 text-xs"
        width="min(520px,90vw)"
      />
    </div>
  );
}

// Iter 249 · Editable Client · Brand · Project cascade inside TaskDetail.
function TaskCbpEditor({ task, onPatch }) {
  const { activeWorkspaceId: wsId } = useAuth();
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    clientsLookup(wsId).then((rows) => setClients(rows || []));
  }, [wsId]);
  useEffect(() => {
    if (!wsId) return;
    // Nov 2026 · Load all brands once (cached), filter client-side by
    // the selected client_id. Avoids a new network round-trip every
    // time the user cycles the client dropdown.
    brandsLookup(wsId).then((rows) => {
      const list = rows || [];
      setBrands(task.client_id ? list.filter((b) => b.client_id === task.client_id) : list);
    });
  }, [wsId, task.client_id]);
  useEffect(() => {
    if (!wsId) return;
    projectsLookup(wsId).then((rows) => {
      let list = rows || [];
      if (task.brand_id) list = list.filter((p) => p.brand_id === task.brand_id);
      else if (task.client_id) list = list.filter((p) => p.client_id === task.client_id);
      setProjects(list);
    });
  }, [wsId, task.client_id, task.brand_id]);

  // Iter Feb 2026 R31 · If the task has a CBP linked to an entity the
  // user doesn't have list access to (deleted, archived, or filtered
  // out by RBAC), the SearchableSelect would render as empty because
  // `value` doesn't match any option. Ensure the current selection
  // ALWAYS appears in the dropdown — falling back to the denormalized
  // client_name / brand_name / project_name stored on the task row.
  const clientOpts = [
    { value: "", label: "— No client —" },
    ...(task.client_id && !clients.some((c) => c.id === task.client_id)
      ? [{ value: task.client_id, label: task.client_name || `(client ${task.client_id.slice(0, 6)})`, hint: "inaccessible" }]
      : []),
    ...clients.map((c) => ({ value: c.id, label: c.name })).sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const brandOpts = [
    { value: "", label: "— No brand —" },
    ...(task.brand_id && !brands.some((b) => b.id === task.brand_id)
      ? [{ value: task.brand_id, label: task.brand_name || `(brand ${task.brand_id.slice(0, 6)})`, hint: "inaccessible" }]
      : []),
    ...brands.map((b) => ({ value: b.id, label: b.name, hint: b.client_name })).sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const projectOpts = [
    { value: "", label: "— No project —" },
    ...(task.project_id && !projects.some((p) => p.id === task.project_id)
      ? [{ value: task.project_id, label: task.project_name || `(project ${task.project_id.slice(0, 6)})`, hint: "inaccessible" }]
      : []),
    ...projects.map((p) => ({ value: p.id, label: p.name, hint: p.client_name || p.brand_name || "" }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const setClient = (v) => onPatch({ client_id: v || null, brand_id: null, project_id: null });
  const setBrand = (v) => onPatch({ brand_id: v || null, project_id: null });
  const setProject = (v) => onPatch({ project_id: v || null });

  return (
    <div className="border border-border bg-muted/20 p-3 space-y-2" data-testid="task-detail-cbp">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        Client · Brand · Project
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <div className="label-eyebrow mb-1">Client</div>
          <SearchableSelect
            value={task.client_id || ""}
            onValueChange={setClient}
            options={clientOpts}
            placeholder="Search a client…"
            triggerClassName="h-8 text-xs"
            data-testid="task-detail-client-select"
            width="100%"
          />
        </div>
        <div>
          <div className="label-eyebrow mb-1">Brand</div>
          <SearchableSelect
            value={task.brand_id || ""}
            onValueChange={setBrand}
            options={brandOpts}
            placeholder="Search a brand…"
            triggerClassName="h-8 text-xs"
            data-testid="task-detail-brand-select"
            width="100%"
          />
        </div>
        <div>
          <div className="label-eyebrow mb-1">Project</div>
          <SearchableSelect
            value={task.project_id || ""}
            onValueChange={setProject}
            options={projectOpts}
            placeholder="Search a project…"
            triggerClassName="h-8 text-xs"
            data-testid="task-detail-project-select"
            width="100%"
          />
        </div>
      </div>
    </div>
  );
}



// Iter 253x · Task template picker + inline manager.
// Compact dropdown next to Quick Add — one-click spawn from a template plus
// a small in-modal manager so admins don't need a full separate page.
function TaskTemplatePicker({ wsId, projects, members, onCreated }) {
  const [open, setOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [spawnTpl, setSpawnTpl] = React.useState(null);
  const [manageOpen, setManageOpen] = React.useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/task-templates`);
      setTemplates(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { if (open || manageOpen) load(); /* eslint-disable-next-line */ }, [open, manageOpen, wsId]);
  return (
    <>
      <div className="relative">
        <Button size="sm" variant="outline" className="rounded-none"
                onClick={() => setOpen((v) => !v)} data-testid="tasks-header-template-picker">
          <Copy className="w-3.5 h-3.5 mr-1.5" /> From template
        </Button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-72 border border-border bg-background shadow-md z-40"
                data-testid="tasks-template-menu"
                onMouseLeave={() => setOpen(false)}>
            <div className="px-2 py-1.5 border-b border-border flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>Task templates</span>
              <button className="ml-auto underline hover:text-foreground"
                      onClick={() => { setOpen(false); setManageOpen(true); }}
                      data-testid="tasks-template-manage-btn">
                Manage
              </button>
            </div>
            {templates.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                No templates yet — <button className="underline" onClick={() => { setOpen(false); setManageOpen(true); }}>create one</button>
              </div>
            ) : (
              <ul className="max-h-72 overflow-auto divide-y divide-border">
                {templates.map((t) => (
                  <li key={t.id}>
                    <button className="w-full text-left px-3 py-2 hover:bg-muted/60"
                            onClick={() => { setOpen(false); setSpawnTpl(t); }}
                            data-testid={`tasks-template-pick-${t.id}`}>
                      <div className="text-xs font-semibold">{t.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {t.title_template} · {t.priority}
                        {t.est_hours ? ` · ${t.est_hours}h` : ""}
                        {t.checklist?.length ? ` · ${t.checklist.length} steps` : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {spawnTpl && (
        <SpawnTaskFromTemplateDialog
          wsId={wsId} template={spawnTpl} projects={projects} members={members}
          onClose={() => setSpawnTpl(null)}
          onCreated={() => { setSpawnTpl(null); onCreated?.(); toast.success("Task created from template"); }}
        />
      )}
      {manageOpen && (
        <TaskTemplateManagerDialog wsId={wsId} onClose={() => { setManageOpen(false); load(); }} />
      )}
    </>
  );
}

function SpawnTaskFromTemplateDialog({ wsId, template, projects, members, onClose, onCreated }) {
  const [project_id, setProjectId] = React.useState("");
  const [assignee_id, setAssigneeId] = React.useState(template.default_assignee_id || "");
  const [due_date, setDueDate] = React.useState("");
  const [title_override, setTitle] = React.useState(template.title_template || "");
  const [busy, setBusy] = React.useState(false);
  const spawn = async () => {
    setBusy(true);
    try {
      await api.post(`/task-templates/${template.id}/spawn`, {
        project_id: project_id || null,
        assignee_id: assignee_id || null,
        due_date: due_date || null,
        title_override: title_override && title_override !== template.title_template ? title_override : null,
      });
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Spawn failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="tasks-template-spawn-dialog">
        <DialogHeader>
          <DialogTitle>Create task from “{template.name}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="label-eyebrow mb-1">Title</div>
            <Input value={title_override} onChange={(e) => setTitle(e.target.value)}
                    className="h-9 rounded-none" data-testid="tasks-template-spawn-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label-eyebrow mb-1">Project</div>
              <select value={project_id} onChange={(e) => setProjectId(e.target.value)}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="tasks-template-spawn-project">
                <option value="">— No project —</option>
                {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Assignee</div>
              <select value={assignee_id} onChange={(e) => setAssigneeId(e.target.value)}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="tasks-template-spawn-assignee">
                <option value="">— Unassigned —</option>
                {(members || []).map((m) => <option key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email || m.user_id}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Due date</div>
            <Input type="date" value={due_date} onChange={(e) => setDueDate(e.target.value)}
                    className="h-9 rounded-none" data-testid="tasks-template-spawn-due" />
          </div>
          {template.checklist?.length ? (
            <div className="border border-border p-2 bg-muted/40">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Checklist ({template.checklist.length})</div>
              <ul className="text-xs list-disc list-inside">
                {template.checklist.map((c, i) => (
                  <li key={i}>{c.label}{c.is_required ? " *" : ""}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={spawn} disabled={busy}
                   data-testid="tasks-template-spawn-submit">
            {busy ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskTemplateManagerDialog({ wsId, onClose }) {
  const [templates, setTemplates] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/task-templates?include_archived=true`);
      setTemplates(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);
  const startNew = () => setEditing({
    name: "", title_template: "", description: "",
    priority: "medium", est_hours: "", default_assignee_id: "",
    checklist: [], tags: [],
  });
  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim() || !editing.title_template?.trim()) {
      toast.error("Name and title template are required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        name: editing.name, description: editing.description || "",
        title_template: editing.title_template, priority: editing.priority,
        est_hours: editing.est_hours ? Number(editing.est_hours) : null,
        default_assignee_id: editing.default_assignee_id || null,
        checklist: (editing.checklist || []).filter((c) => c.label),
        tags: editing.tags || [],
      };
      if (editing.id) {
        await api.patch(`/task-templates/${editing.id}`, payload);
      } else {
        await api.post(`/workspaces/${wsId}/task-templates`, payload);
      }
      toast.success("Template saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Archive template "${t.name}"?`)) return;
    try {
      await api.patch(`/task-templates/${t.id}`, { is_active: false });
      toast.success("Archived");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Archive failed")); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto" data-testid="tasks-template-manager">
        <DialogHeader>
          <DialogTitle>Task templates</DialogTitle>
        </DialogHeader>
        {editing ? (
          <div className="space-y-3">
            <div>
              <div className="label-eyebrow mb-1">Template name</div>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      className="h-9 rounded-none" data-testid="tasks-tpl-edit-name" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Task title template</div>
              <Input value={editing.title_template}
                      onChange={(e) => setEditing({ ...editing, title_template: e.target.value })}
                      className="h-9 rounded-none" data-testid="tasks-tpl-edit-title" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label-eyebrow mb-1">Priority</div>
                <select value={editing.priority}
                        onChange={(e) => setEditing({ ...editing, priority: e.target.value })}
                        className="h-9 px-2 border border-border w-full bg-background text-sm">
                  <option value="urgent">Urgent</option><option value="high">High</option>
                  <option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Estimated hours</div>
                <Input type="number" step="0.25" min="0" value={editing.est_hours}
                        onChange={(e) => setEditing({ ...editing, est_hours: e.target.value })}
                        className="h-9 rounded-none" />
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Description (optional)</div>
              <Textarea rows={2} value={editing.description || ""}
                          onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                          className="rounded-none text-xs" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Checklist</div>
              <div className="space-y-1">
                {(editing.checklist || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={c.label}
                            onChange={(e) => {
                              const next = [...editing.checklist];
                              next[i] = { ...c, label: e.target.value };
                              setEditing({ ...editing, checklist: next });
                            }}
                            className="h-8 rounded-none flex-1" placeholder={`Step ${i + 1}`} />
                    <label className="text-[10px] flex items-center gap-1">
                      <input type="checkbox" checked={c.is_required}
                              onChange={(e) => {
                                const next = [...editing.checklist];
                                next[i] = { ...c, is_required: e.target.checked };
                                setEditing({ ...editing, checklist: next });
                              }} />
                      req
                    </label>
                    <Button size="sm" variant="ghost" className="rounded-none h-8 px-2"
                             onClick={() => setEditing({ ...editing,
                                 checklist: editing.checklist.filter((_, x) => x !== i) })}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                         onClick={() => setEditing({ ...editing,
                             checklist: [...(editing.checklist || []), { label: "", is_required: false }] })}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add step
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="rounded-none" onClick={save} disabled={busy} data-testid="tasks-tpl-edit-save">
                {busy ? "Saving…" : "Save template"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{templates.length} templates</div>
              <Button size="sm" className="rounded-none h-8" onClick={startNew} data-testid="tasks-tpl-new">
                <Plus className="w-3.5 h-3.5 mr-1" /> New template
              </Button>
            </div>
            {templates.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border">
                No templates yet.
              </div>
            ) : (
              <div className="border border-border divide-y divide-border">
                {templates.map((t) => (
                  <div key={t.id} className={`px-3 py-2 flex items-center gap-2 text-sm ${t.is_active === false ? "opacity-60" : ""}`}
                        data-testid={`tasks-tpl-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{t.name}{t.is_active === false && " (archived)"}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{t.title_template}</div>
                    </div>
                    <div className="text-[10px] text-muted-foreground w-16">{t.priority}</div>
                    <div className="text-[10px] text-muted-foreground w-14">{(t.checklist || []).length} steps</div>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                             onClick={() => setEditing({ ...t, est_hours: t.est_hours ?? "" })}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    {t.is_active !== false && (
                      <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive"
                               onClick={() => remove(t)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}



// Iter 253z-4 · Task completion checklist. Required items block "done" status.
function TaskChecklistBlock({ task, onChange }) {
  const { activeWorkspaceId } = useAuth();
  const [items, setItems] = React.useState([]);
  const [draft, setDraft] = React.useState("");
  const [draftRequired, setDraftRequired] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // Iter 253z-5 · Checklist template packs.
  const [packs, setPacks] = React.useState([]);
  const [pickedPack, setPickedPack] = React.useState("");
  const [showPackMgr, setShowPackMgr] = React.useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/tasks/${task.id}/checklist`);
      setItems(r.data || []);
    } catch { /* silent */ }
  };
  const loadPacks = async () => {
    if (!activeWorkspaceId) return;
    try {
      const r = await api.get(`/workspaces/${activeWorkspaceId}/checklist-packs`);
      setPacks(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); loadPacks(); /* eslint-disable-next-line */ }, [task.id]);
  const add = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/checklist`,
                          { label: draft.trim(), is_required: draftRequired });
      setDraft(""); setDraftRequired(false);
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setBusy(false); }
  };
  const applyPack = async (pid) => {
    if (!pid) return;
    setPickedPack("");
    try {
      const r = await api.post(`/tasks/${task.id}/checklist/apply-pack/${pid}`);
      toast.success(`Added ${r.data?.added || 0} step${r.data?.added === 1 ? "" : "s"} from "${r.data?.pack_name}"`);
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply pack failed")); }
  };
  const toggle = async (item) => {
    try {
      await api.patch(`/tasks/${task.id}/checklist/${item.id}`,
                            { is_done: !item.is_done });
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const remove = async (item) => {
    try {
      await api.delete(`/tasks/${task.id}/checklist/${item.id}`);
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const requiredOpen = items.filter((i) => i.is_required && !i.is_done).length;
  const doneCount = items.filter((i) => i.is_done).length;
  return (
    <div className="border border-border p-3 space-y-2 bg-muted/10" data-testid="task-checklist-block">
      <div className="flex items-center gap-2">
        <span className="label-eyebrow">Completion checklist</span>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {doneCount}/{items.length} done
          {requiredOpen > 0 && <span className="ml-2 text-amber-700 font-semibold">· {requiredOpen} required open</span>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <select className="h-7 text-[11px] px-1 border border-border bg-background flex-1"
                value={pickedPack} onChange={(e) => { const v = e.target.value; setPickedPack(v); applyPack(v); }}
                data-testid="task-checklist-apply-pack">
          <option value="">Apply checklist pack…</option>
          {packs.map((p) => (
            <option key={p.id} value={p.id}>{p.name} ({(p.items || []).length} steps)</option>
          ))}
        </select>
        <button type="button"
                onClick={() => setShowPackMgr(true)}
                className="text-[10px] uppercase tracking-widest underline text-muted-foreground hover:text-foreground"
                data-testid="task-checklist-manage-packs">
          Manage packs
        </button>
      </div>
      {items.length > 0 && (
        <ul className="border border-border divide-y divide-border">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-muted/40"
                 data-testid={`task-checklist-item-${it.id}`}>
              <input type="checkbox" checked={!!it.is_done}
                       onChange={() => toggle(it)}
                       data-testid={`task-checklist-toggle-${it.id}`} />
              <span className={`flex-1 truncate ${it.is_done ? "line-through text-muted-foreground" : ""}`}>{it.label}</span>
              {it.is_required && (
                <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-200 px-1">required</span>
              )}
              <button className="text-muted-foreground hover:text-destructive"
                      onClick={() => remove(it)}
                      data-testid={`task-checklist-delete-${it.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                placeholder="Add checklist step (Enter to add)…"
                className="h-8 text-xs flex-1" data-testid="task-checklist-input" />
        <label className="text-[10px] flex items-center gap-1 shrink-0">
          <input type="checkbox" checked={draftRequired}
                   onChange={(e) => setDraftRequired(e.target.checked)}
                   data-testid="task-checklist-req-toggle" />
          req
        </label>
        <Button size="sm" className="h-8 rounded-none" onClick={add} disabled={busy || !draft.trim()}
                 data-testid="task-checklist-add">
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      {showPackMgr && (
        <ChecklistPackManagerDialog wsId={activeWorkspaceId}
                                          onClose={() => { setShowPackMgr(false); loadPacks(); }} />
      )}
    </div>
  );
}

// Iter 253z-5 · Manage reusable checklist packs (Design QA, Launch Ops, …).
function ChecklistPackManagerDialog({ wsId, onClose }) {
  const [packs, setPacks] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/checklist-packs`);
      setPacks(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);
  const startNew = () => setEditing({ name: "", description: "", items: [] });
  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description || "",
        items: (editing.items || []).filter((i) => i.label?.trim()),
      };
      if (editing.id) {
        await api.patch(`/checklist-packs/${editing.id}`, payload);
      } else {
        await api.post(`/workspaces/${wsId}/checklist-packs`, payload);
      }
      toast.success("Pack saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete pack "${p.name}"?`)) return;
    try {
      await api.delete(`/checklist-packs/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto"
                        data-testid="checklist-pack-manager">
        <DialogHeader>
          <DialogTitle>Checklist packs</DialogTitle>
        </DialogHeader>
        {editing ? (
          <div className="space-y-3">
            <div>
              <div className="label-eyebrow mb-1">Pack name</div>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      placeholder="e.g. Design QA · Launch Ops"
                      className="h-9 rounded-none" data-testid="checklist-pack-name" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Description</div>
              <Textarea rows={2} value={editing.description || ""}
                          onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                          className="rounded-none text-xs" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Steps</div>
              <div className="space-y-1">
                {(editing.items || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={c.label}
                            onChange={(e) => {
                              const next = [...editing.items];
                              next[i] = { ...c, label: e.target.value };
                              setEditing({ ...editing, items: next });
                            }}
                            placeholder={`Step ${i + 1}`}
                            className="h-8 rounded-none flex-1" />
                    <label className="text-[10px] flex items-center gap-1">
                      <input type="checkbox" checked={!!c.is_required}
                              onChange={(e) => {
                                const next = [...editing.items];
                                next[i] = { ...c, is_required: e.target.checked };
                                setEditing({ ...editing, items: next });
                              }} />
                      req
                    </label>
                    <Button size="sm" variant="ghost" className="rounded-none h-8 px-2"
                             onClick={() => setEditing({ ...editing,
                                 items: editing.items.filter((_, x) => x !== i) })}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                         onClick={() => setEditing({ ...editing,
                             items: [...(editing.items || []), { label: "", is_required: false }] })}
                         data-testid="checklist-pack-add-step">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add step
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="rounded-none" onClick={save} disabled={busy}
                       data-testid="checklist-pack-save">
                {busy ? "Saving…" : "Save pack"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{packs.length} packs</div>
              <div className="flex gap-1.5">
                <ChecklistPackLibraryButton wsId={wsId} onCloned={load} />
                <Button size="sm" className="rounded-none h-8" onClick={startNew}
                         data-testid="checklist-pack-new">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New pack
                </Button>
              </div>
            </div>
            {packs.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border">
                No checklist packs yet.
              </div>
            ) : (
              <div className="border border-border divide-y divide-border">
                {packs.map((p) => (
                  <div key={p.id} className="px-3 py-2 flex items-center gap-2 text-sm"
                        data-testid={`checklist-pack-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      {p.description && (
                        <div className="text-[10px] text-muted-foreground truncate">{p.description}</div>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground w-14">{(p.items || []).length} steps</div>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                             onClick={() => setEditing({ ...p })}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive"
                             onClick={() => remove(p)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Iter 253z-4 · File attachments — Label + Note per item.
function TaskAttachmentsBlock({ task, onChange }) {
  const [items, setItems] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const fileRef = React.useRef(null);
  const load = async () => {
    try {
      const r = await api.get(`/tasks/${task.id}/attachments`);
      setItems(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id]);
  // Iter 253z-6 · Upload multiple files (from picker or drag-drop) in
  // parallel — one POST per file since the backend accepts single uploads.
  const uploadMany = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    let ok = 0, failed = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/tasks/${task.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
        });
        ok += 1;
      } catch { failed += 1; }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
    if (failed) toast.error(`${failed} file${failed === 1 ? "" : "s"} failed`);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    await load(); onChange?.();
  };
  const onPick = (e) => uploadMany(e.target.files);
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    uploadMany(e.dataTransfer.files);
  };
  const patchMeta = async (aid, patch) => {
    try {
      await api.patch(`/tasks/${task.id}/attachments/${aid}`, patch);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const remove = async (aid) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/tasks/${task.id}/attachments/${aid}`);
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  // Feb 2026 R293zzh · Attachment downloads + inline previews need to
  // carry the JWT bearer. Anchors / <img src> / <iframe src> can't
  // send Authorization headers, so we fetch each attachment as a
  // blob and expose an object URL for viewing / downloading. Image
  // and PDF previews reuse the same blob URL to avoid double fetches.
  const [blobUrls, setBlobUrls] = React.useState({}); // { [attachmentId]: objectURL }

  React.useEffect(() => {
    let cancelled = false;
    // Prefetch previewable files (image + pdf) once per list change
    // so thumbnails / iframes render right away.
    const needing = items.filter((a) => {
      if (blobUrls[a.id]) return false;
      const isImage = (a.content_type || "").startsWith("image/");
      const isPdf = (a.content_type === "application/pdf")
        || (a.filename || "").toLowerCase().endsWith(".pdf");
      return isImage || isPdf;
    });
    (async () => {
      for (const a of needing) {
        try {
          const r = await api.get(
            `/tasks/${task.id}/attachments/${a.id}/download`,
            { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(r.data);
          setBlobUrls((prev) => ({ ...prev, [a.id]: url }));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, task.id]);

  React.useEffect(() => () => {
    // Revoke every blob URL on unmount to keep memory tidy.
    Object.values(blobUrls).forEach((u) => { try { URL.revokeObjectURL(u); } catch {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authedDownload = async (a) => {
    try {
      // Reuse an already-fetched blob URL if we have one for a preview.
      let url = blobUrls[a.id];
      if (!url) {
        const r = await api.get(
          `/tasks/${task.id}/attachments/${a.id}/download`,
          { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        setBlobUrls((prev) => ({ ...prev, [a.id]: url }));
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error("Download failed"); }
  };
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className={`border p-3 space-y-2 ${dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/10"}`}
          data-testid="task-attachments-block"
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
          onDrop={onDrop}>
      <div className="flex items-center gap-2">
        <span className="label-eyebrow">Attachments</span>
        <span className="text-[10px] text-muted-foreground ml-auto">{items.length} file{items.length === 1 ? "" : "s"}</span>
      </div>
      {items.length > 0 && (
        <ul className="border border-border divide-y divide-border">
          {items.map((a) => (
            <li key={a.id} className="px-2 py-1.5 text-xs space-y-1"
                 data-testid={`task-attachment-${a.id}`}>
              <div className="flex items-center gap-2">
                <button type="button"
                        onClick={() => authedDownload(a)}
                        className="font-semibold text-primary underline truncate flex-1 text-left"
                        data-testid={`task-attachment-download-${a.id}`}>
                  {a.filename}
                </button>
                <span className="text-[10px] text-muted-foreground shrink-0">{Math.round((a.size || 0) / 1024)} KB</span>
                <button className="text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => remove(a.id)}
                        data-testid={`task-attachment-delete-${a.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {/* Iter 253z-5 · Inline preview: image thumbnails + PDF iframe.
                   Feb 2026 R293zzh · Uses authenticated blob URLs so the
                   preview no longer fails with "missing bearer token". */}
              {(a.content_type || "").startsWith("image/") && blobUrls[a.id] && (
                <img src={blobUrls[a.id]} alt={a.filename}
                      className="max-h-40 w-auto border border-border object-contain bg-muted/40"
                      data-testid={`task-attachment-preview-img-${a.id}`} />
              )}
              {(a.content_type === "application/pdf" ||
                (a.filename || "").toLowerCase().endsWith(".pdf")) && blobUrls[a.id] && (
                <details className="border border-border" data-testid={`task-attachment-preview-pdf-${a.id}`}>
                  <summary className="cursor-pointer px-2 py-1 text-[10px] uppercase tracking-widest bg-muted/40 hover:bg-muted/60">
                    Show PDF preview
                  </summary>
                  <iframe src={`${blobUrls[a.id]}#toolbar=0`}
                            title={a.filename}
                            className="w-full h-64 border-0" />
                </details>
              )}
              <Input defaultValue={a.label || ""} placeholder="Label (e.g. Design brief v3)"
                      className="h-7 text-[11px]"
                      onBlur={(e) => { if (e.target.value !== (a.label || "")) patchMeta(a.id, { label: e.target.value }); }}
                      data-testid={`task-attachment-label-${a.id}`} />
              <Input defaultValue={a.note || ""} placeholder="Note (optional)"
                      className="h-7 text-[11px]"
                      onBlur={(e) => { if (e.target.value !== (a.note || "")) patchMeta(a.id, { note: e.target.value }); }}
                      data-testid={`task-attachment-note-${a.id}`} />
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" multiple onChange={onPick} className="hidden" data-testid="task-attachment-file-input" />
        <Button size="sm" variant="outline" className="rounded-none h-8"
                 disabled={uploading}
                 onClick={() => fileRef.current?.click()}
                 data-testid="task-attachment-upload-btn">
          {uploading ? "Uploading…" : "Upload files"}
        </Button>
        <span className="text-[10px] text-muted-foreground">
          {dragOver ? "Drop to upload" : "Drag & drop or click · Max 20 MB each"}
        </span>
      </div>
    </div>
  );
}

// Iter 253z-4 · External reference links — Label + Note per item.
function TaskLinksBlock({ task, onChange }) {
  const [items, setItems] = React.useState([]);
  const [url, setUrl] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/tasks/${task.id}/links`);
      setItems(r.data || []);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [task.id]);
  const add = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/links`, {
        url: url.trim(), label: label.trim() || null, note: note.trim() || null,
      });
      setUrl(""); setLabel(""); setNote("");
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add link failed")); }
    finally { setBusy(false); }
  };
  const patchMeta = async (lid, patch) => {
    try {
      await api.patch(`/tasks/${task.id}/links/${lid}`, patch);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const remove = async (lid) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await api.delete(`/tasks/${task.id}/links/${lid}`);
      await load(); onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="border border-border p-3 space-y-2 bg-muted/10" data-testid="task-links-block">
      <div className="flex items-center gap-2">
        <span className="label-eyebrow">External references</span>
        <span className="text-[10px] text-muted-foreground ml-auto">{items.length} link{items.length === 1 ? "" : "s"}</span>
      </div>
      {items.length > 0 && (
        <ul className="border border-border divide-y divide-border">
          {items.map((l) => (
            <li key={l.id} className="px-2 py-1.5 text-xs space-y-1"
                 data-testid={`task-link-${l.id}`}>
              <div className="flex items-center gap-2">
                <a href={l.url} target="_blank" rel="noreferrer"
                    className="font-semibold text-primary underline truncate flex-1"
                    data-testid={`task-link-open-${l.id}`}>
                  {l.label || l.url}
                </a>
                <button className="text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => remove(l.id)}
                        data-testid={`task-link-delete-${l.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {!l.label && <div className="text-[10px] text-muted-foreground truncate font-mono">{l.url}</div>}
              <Input defaultValue={l.label || ""} placeholder="Label"
                      className="h-7 text-[11px]"
                      onBlur={(e) => { if (e.target.value !== (l.label || "")) patchMeta(l.id, { label: e.target.value }); }}
                      data-testid={`task-link-label-${l.id}`} />
              <Input defaultValue={l.note || ""} placeholder="Note"
                      className="h-7 text-[11px]"
                      onBlur={(e) => { if (e.target.value !== (l.note || "")) patchMeta(l.id, { note: e.target.value }); }}
                      data-testid={`task-link-note-${l.id}`} />
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-[2fr,1fr,1fr,auto] gap-2 items-center">
        <Input value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…" className="h-8 text-[11px] font-mono"
                data-testid="task-link-url-input" />
        <Input value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (optional)" className="h-8 text-[11px]"
                data-testid="task-link-label-input" />
        <Input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional)" className="h-8 text-[11px]"
                data-testid="task-link-note-input" />
        <Button size="sm" className="h-8 rounded-none" onClick={add} disabled={busy || !url.trim()}
                 data-testid="task-link-add-btn">
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}



// Iter 253z-6 · Clone starter checklist packs into the workspace with one
// click. Ships a small "library" popover next to the "New pack" button.
function ChecklistPackLibraryButton({ wsId, onCloned }) {
  const [open, setOpen] = React.useState(false);
  const [library, setLibrary] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/checklist-pack-library`);
      setLibrary(r.data?.packs || []);
    } catch { setLibrary([]); }
  };
  React.useEffect(() => { if (open) load(); }, [open]);
  const clone = async (name) => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/checklist-packs/library-clone`,
                                     { pack_names: [name] });
      toast.success(`Cloned ${r.data?.cloned || 0} pack`);
      setOpen(false);
      onCloned?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none h-8"
               onClick={() => setOpen(true)}
               data-testid="checklist-pack-library-btn">
        <Copy className="w-3.5 h-3.5 mr-1" /> Library
      </Button>
      {open && (
        <Dialog open onOpenChange={(v) => { if (!v) setOpen(false); }}>
          <DialogContent className="max-w-lg rounded-none max-h-[80vh] overflow-auto"
                            data-testid="checklist-pack-library-dialog">
            <DialogHeader>
              <DialogTitle>Checklist pack library</DialogTitle>
              <DialogDescription>
                Ready-made packs — click Clone to add a copy to your workspace.
                Cloned packs are fully editable afterwards.
              </DialogDescription>
            </DialogHeader>
            <div className="border border-border divide-y divide-border">
              {library.map((p) => (
                <div key={p.name} className="px-3 py-2 flex items-start gap-2 text-sm"
                      data-testid={`checklist-pack-library-row-${p.name.replace(/\s+/g, "-")}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{p.name}</div>
                    {p.description && (
                      <div className="text-[11px] text-muted-foreground">{p.description}</div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {(p.items || []).length} steps · {(p.items || []).filter((i) => i.is_required).length} required
                    </div>
                  </div>
                  <Button size="sm" className="rounded-none h-7 shrink-0"
                           disabled={busy}
                           onClick={() => clone(p.name)}
                           data-testid={`checklist-pack-library-clone-${p.name.replace(/\s+/g, "-")}`}>
                    Clone
                  </Button>
                </div>
              ))}
              {library.length === 0 && (
                <div className="p-4 text-center text-xs text-muted-foreground">Loading…</div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

